import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import { Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "./App";
import { useLanguage } from "./contexts/LanguageContext";
import { useData } from "./contexts/DataContext";
import { isTaskOverdue, getTaskStatusStyle } from "./utils/dateUtils";
import { prepareFilesForUpload, getUploadErrorMessage, formatFileSize, FILE_UPLOAD_CONFIG } from "./utils/fileUploadUtils";
import axios from "axios";

// Import AI Components
import NaturalLanguageTaskInput from './components/ai/NaturalLanguageTaskInput';
import DailyBriefing from './components/ai/DailyBriefing';
import AIHelpAssistant from './components/ai/AIHelpAssistant';
import SimpleVoiceTaskCreator from './components/ai/SimpleVoiceTaskCreator';
import ProBadge from './components/common/ProBadge';
import TrialBanner from './components/common/TrialBanner';
import TrialExpiredModal from './components/common/TrialExpiredModal';
import { hasProAccess } from './utils/trialHelpers';
import useHistoryBack from './hooks/useHistoryBack';
import OfflineIndicator from './components/common/OfflineIndicator';
import ResetAppDataButton from './components/settings/ResetAppDataButton';
import DeleteAccountModal from './components/settings/DeleteAccountModal';
import ConnectedCloudsPage from './components/settings/ConnectedCloudsPage';
import OrganizationWelcomePage from './components/organization/OrganizationWelcomePage';
import OrganizationMembersPage from './components/organization/OrganizationMembersPage';
import OrganizationSettingsPage from './components/organization/OrganizationSettingsPage';
import OrganizationTeamTasksPage from './components/organization/OrganizationTeamTasksPage';
import OrganizationBillingPage from './components/organization/OrganizationBillingPage';
import WorkspaceSwitcher from './components/organization/WorkspaceSwitcher';
import NotificationBell from './components/notifications/NotificationBell';
import TeamActivityCard from './components/organization/TeamActivityCard';
import {
  ListChecks as PhListChecks,
  CalendarBlank as PhCalendar,
  UsersThree as PhUsers,
  Archive as PhArchive,
  Buildings as PhBuildings,
  GearSix as PhGear,
  Plus as PhPlus,
} from '@phosphor-icons/react';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { isLikelyOffline, isNetworkError } from './utils/networkStatus';
import { drainQueue } from './utils/offlineWriteQueue';
import { PullToRefresh } from './components/common/PullToRefresh';
import { toast } from 'sonner';
import { confirmAction } from './utils/confirmDialog';
import { TaskSplitPane } from './components/tasks/TaskSplitPane';
import { TodayGlance } from './components/tasks/TodayGlance';
import { ContactsGlance } from './components/contacts/ContactsGlance';
import { WeekTimeGrid } from './components/calendar/WeekTimeGrid';
import { NotifyContactSection, buildContactMessage } from './components/tasks/NotifyContactSection';
import { SmsConsentCheckbox } from './components/contacts/SmsConsentCheckbox';
import { PickFromPhoneButton } from './components/contacts/PickFromPhoneButton';
import { BulkCopyContactsButton } from './components/contacts/BulkCopyContactsButton';
import DriveAttachGate from './components/files/DriveAttachGate';
import { uploadTaskFilesToDrive } from './utils/driveUpload';

// Simple cache implementation
class SimpleCache {
  constructor() {
    this.cache = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  }

  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  get(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    // Check if cache is expired
    if (Date.now() - cached.timestamp > this.CACHE_DURATION) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  invalidate(key) {
    this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
  }
}

// Global cache instance
const apiCache = new SimpleCache();

// Add Task form draft — survives the modal being re-created by a parent re-render
const ADD_TASK_DRAFT_KEY = 'edotask_add_task_draft';
// A full page load starts fresh; the draft only bridges in-app re-renders
sessionStorage.removeItem(ADD_TASK_DRAFT_KEY);
const readAddTaskDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ADD_TASK_DRAFT_KEY) || 'null');
  } catch {
    return null;
  }
};

// Support Content Context
const SupportContentContext = createContext();

// Helper function to format text content with basic formatting
const formatContent = (text) => {
  if (!text) return '';
  
  // Split by double line breaks for paragraphs
  const paragraphs = text.split('\n\n').filter(p => p.trim());
  
  return paragraphs.map((paragraph, index) => {
    // Handle single line breaks within paragraphs
    const lines = paragraph.split('\n').filter(line => line.trim());
    
    return (
      <p key={index} className="mb-3 last:mb-0">
        {lines.map((line, lineIndex) => (
          <span key={lineIndex}>
            {line}
            {lineIndex < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
  });
};

const SupportContentProvider = ({ children }) => {
  const [supportContent, setSupportContent] = useState({
    supportEmail: 'support@edotask.com',
    feedbackEmail: 'feedback@edotask.com',
    helpCenterContent: `Welcome to Edotask Help Center!

Here you can find answers to frequently asked questions and comprehensive guides to help you make the most of Edotask.

Common Topics:
• How to create and manage recurring tasks
• How to attach files and media to tasks
• How to organize contacts and link them to tasks
• How to upgrade your subscription and manage billing
• How to export your data and manage account settings

Getting Started:
If you're new to Edotask, we recommend starting with creating your first task and exploring the calendar view to see how your tasks are organized.

Need More Help?
For detailed step-by-step tutorials and advanced features, contact our support team who can provide personalized assistance.`,
    privacyPolicyContent: `Edotask Privacy Policy

Data Collection:
We collect only the information necessary to provide you with the best task management experience. This includes your account information, tasks, contacts, and usage data.

Data Usage:
Your information is used solely to provide and improve our services. We never sell your personal data to third parties.

Data Protection:
We implement industry-standard security measures to protect your information, including encryption and secure data storage.

Your Rights:
You have the right to access, modify, or delete your personal data at any time. Contact us for any privacy-related concerns.`,
    termsOfServiceContent: `Edotask Terms of Service

Service Usage:
By using Edotask, you agree to use our service responsibly and in accordance with these terms.

User Responsibilities:
• Maintain the security of your account credentials
• Use the service for legitimate task management purposes
• Respect other users and our support team

Service Availability:
We strive to maintain 99.9% uptime, but cannot guarantee uninterrupted service due to maintenance and unforeseen circumstances.

Account Management:
You may terminate your account at any time. We reserve the right to suspend accounts that violate these terms.`,
    cookiePolicyContent: `Edotask Cookie Policy

Essential Cookies:
We use essential cookies that are necessary for the basic functionality of Edotask, including user authentication and session management.

Performance Cookies:
These cookies help us understand how you interact with Edotask so we can improve the user experience and optimize performance.

Preference Cookies:
We store your settings and preferences (like language selection and notification preferences) to provide a personalized experience.

Managing Cookies:
You can control cookie settings through your browser preferences, though disabling essential cookies may affect site functionality.`,
    appVersion: '2.1.0',
    appStatus: 'All Systems Operational',
    lastUpdated: 'January 2025'
  });

  const updateSupportContent = (field, value) => {
    setSupportContent(prev => ({ ...prev, [field]: value }));
  };

  const value = {
    supportContent,
    updateSupportContent
  };

  return (
    <SupportContentContext.Provider value={value}>
      {children}
    </SupportContentContext.Provider>
  );
};

const useSupportContent = () => {
  const context = useContext(SupportContentContext);
  if (!context) {
    throw new Error('useSupportContent must be used within SupportContentProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple, working components first
const Components = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage(); // Add language hook for translations
  const { user } = useAuth(); // Pro/trial gating for premium features (voice, AI parse)
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreTasks, setHasMoreTasks] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState([]); // For bulk operations
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showVoiceTaskCreator, setShowVoiceTaskCreator] = useState(false); // New AI Voice Task Creator
  const TASKS_PER_PAGE = 20; // Initial load - smaller for better UX
  const [allTasksLoaded, setAllTasksLoaded] = useState(false); // Track if all tasks are loaded
  // Desktop split view: which task/contact shows in the right pane
  const [desktopSelection, setDesktopSelection] = useState(null);
  const [contactSelection, setContactSelection] = useState(null);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  const [calendarCurrentDate, setCalendarCurrentDate] = useState(() => new Date());
  const [calendarViewMode, setCalendarViewMode] = useState('month');
  const [addTaskPrefillDate, setAddTaskPrefillDate] = useState(null);
  const [calendarTaskSelection, setCalendarTaskSelection] = useState(null);
  // Contact highlighted with the arrow keys on desktop (Enter opens it)
  const [keyboardContactId, setKeyboardContactId] = useState(null);
  // Task highlighted with the arrow keys on desktop (Enter opens it)
  const [keyboardTaskId, setKeyboardTaskId] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  
  // Prevent rapid modal state changes
  const handleOpenAddTask = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!showAddTask) {
      setShowAddTask(true);
    }
  }, [showAddTask]);
  
  const handleCloseAddTask = useCallback(() => {
    if (showAddTask) {
      setShowAddTask(false);
    }
    setAddTaskPrefillDate(null);
    sessionStorage.removeItem(ADD_TASK_DRAFT_KEY);
  }, [showAddTask]);

  const openAddTaskForDate = useCallback((date) => {
    setAddTaskPrefillDate(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    setShowAddTask(true);
  }, []);
  
  const handleOpenAddContact = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!showAddContact) {
      setShowAddContact(true);
    }
  }, [showAddContact]);
  
  const handleCloseAddContact = useCallback(() => {
    if (showAddContact) {
      setShowAddContact(false);
    }
  }, [showAddContact]);

  const handleOpenVoiceTaskCreator = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!showVoiceTaskCreator) {
      setShowVoiceTaskCreator(true);
    }
  }, [showVoiceTaskCreator]);
  
  const handleCloseVoiceTaskCreator = useCallback(() => {
    if (showVoiceTaskCreator) {
      setShowVoiceTaskCreator(false);
    }
  }, [showVoiceTaskCreator]);

  // Hardware / OS / browser back integration:
  //   • Android hardware back, iOS edge-swipe and desktop Alt-Left now dismiss
  //     these modals instead of exiting the app.
  //   • Each hook pushes a tagged history entry while its UI is open and pops
  //     it on close to keep the back stack clean.
  useHistoryBack(showAddTask, handleCloseAddTask, 'add-task-modal');
  useHistoryBack(showAddContact, handleCloseAddContact, 'add-contact-modal');
  useHistoryBack(showVoiceTaskCreator, handleCloseVoiceTaskCreator, 'voice-task-modal');
  // Multi-select mode for tasks — back gesture exits selection instead of leaving the page.
  useHistoryBack(
    isSelectionMode,
    useCallback(() => {
      setIsSelectionMode(false);
      setSelectedTasks([]);
    }, []),
    'tasks-select-mode',
  );

  // Bulk selection functions
  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(prev => !prev);
    setSelectedTasks([]); // Clear selection when toggling mode
  }, []);

  const toggleTaskSelection = useCallback((taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  }, []);

  const selectAllTasks = useCallback(() => {
    setSelectedTasks(tasks.map(task => task.id));
  }, [tasks]);

  const clearSelection = useCallback(() => {
    setSelectedTasks([]);
    setIsSelectionMode(false); // Exit selection mode
  }, []);

  // Enter selection mode from long press and select the task
  const enterSelectionMode = useCallback((taskId) => {
    setIsSelectionMode(true);
    setSelectedTasks([taskId]); // Select the long-pressed task
  }, []);

  const bulkDeleteTasks = useCallback(async () => {
    if (selectedTasks.length === 0) return;
    
    const confirmed = await confirmAction({
      title: `Delete ${selectedTasks.length} task${selectedTasks.length > 1 ? 's' : ''}?`,
      message: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!confirmed) return;

    setIsBulkDeleting(true);
    try {
      // Use bulk delete endpoint
      const res = await axios.post(`${API}/tasks/bulk-delete`, {
        task_ids: selectedTasks
      }, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const deletedCount = res.data?.deleted_count ?? selectedTasks.length;
      const skippedCount = res.data?.skipped_count ?? 0;

      // Update local state — refetch is safest since some tasks may have
      // been skipped by the backend (only the creator + owner/admin can
      // delete team tasks).
      setSelectedTasks([]);
      setIsSelectionMode(false);
      invalidateTasksCache();
      fetchTasks(1, false);

      if (skippedCount > 0) {
        toast.warning(
          `Deleted ${deletedCount}. Skipped ${skippedCount} — only the creator or an org Owner/Admin can delete those tasks.`
        );
      } else {
        toast.success(`${deletedCount} task${deletedCount === 1 ? '' : 's'} deleted`);
      }
      console.log(`✅ Successfully deleted ${deletedCount} tasks (skipped ${skippedCount})`);
    } catch (error) {
      console.error('Error deleting tasks:', error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission to delete the selected tasks.");
      } else {
        toast.error('Some tasks could not be deleted. Please try again.');
      }
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedTasks]);

  // Simple data fetching with proper auth headers and pagination
  const fetchTasks = useCallback(async (page = 1, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Check cache first for this specific page
      const tokenId = localStorage.getItem('token')?.slice(0, 10);
      const cacheKey = `tasks_${tokenId}_page_${page}`;
      const cachedData = apiCache.get(cacheKey);
      
      if (cachedData && !append) {
        setTasks(cachedData);
        setHasMoreTasks(cachedData.length === TASKS_PER_PAGE);
        setCurrentPage(page);
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      
      const response = await axios.get(`${API}/tasks`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          page: page,
          limit: TASKS_PER_PAGE
        }
      });
      
      const newTasks = response.data || [];
      
      // Cache the response for this page
      apiCache.set(cacheKey, newTasks);
      
      if (append) {
        setTasks(prevTasks => [...prevTasks, ...newTasks]);
      } else {
        setTasks(newTasks);
      }
      
      // Check if there are more tasks to load
      setHasMoreTasks(newTasks.length === TASKS_PER_PAGE);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // Offline / network-failure path: preserve whatever the UI already shows
      // (including optimistic tasks created while offline). Only clear the
      // list if we are genuinely online and the request failed for a real
      // reason (auth, server error, etc.).
      const offline = isLikelyOffline() || isNetworkError(error);
      if (!append && !offline) {
        setTasks([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [TASKS_PER_PAGE]);

  // Load more tasks function
  const loadMoreTasks = useCallback(() => {
    if (!loadingMore && hasMoreTasks) {
      fetchTasks(currentPage + 1, true);
    }
  }, [fetchTasks, currentPage, loadingMore, hasMoreTasks]);

  // Load ALL tasks function
  const loadAllTasks = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📡 Loading ALL tasks for full view...');
      
      const response = await axios.get(`${API}/tasks`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          all: true // Use the 'all' parameter to get all tasks
        }
      });
      
      const allTasks = response.data || [];
      console.log(`✅ Loaded ${allTasks.length} total tasks`);
      
      setTasks(allTasks);
      setHasMoreTasks(false); // No more tasks to load
      setAllTasksLoaded(true); // Mark that all tasks are now loaded
      
    } catch (error) {
      console.error('Error loading all tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Background task loading for calendar performance
  const loadAllTasksInBackground = useCallback(async () => {
    if (tasks.length >= 100) return; // Already have enough tasks
    
    try {
      console.log('📡 Loading all tasks in background for calendar...');
      const response = await axios.get(`${API}/tasks?page=1&limit=1000`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const allTasks = response.data;
      console.log(`✅ Background loaded ${allTasks.length} total tasks`);
      
      setTasks(allTasks);
      setHasMoreTasks(false); // No more tasks to load
      
    } catch (error) {
      console.error('Error loading all tasks in background:', error);
    }
  }, [tasks.length]);

  const fetchContacts = useCallback(async () => {
    try {
      // Check cache first
      const cacheKey = `contacts_${localStorage.getItem('token')?.slice(0, 10)}_${localStorage.getItem('activeWorkspaceId') || 'personal'}`;
      const cachedData = apiCache.get(cacheKey);
      
      if (cachedData) {
        setContacts(cachedData);
        return;
      }

      const response = await axios.get(`${API}/contacts`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const contactsData = response.data || [];
      setContacts(contactsData);
      
      // Cache the response
      apiCache.set(cacheKey, contactsData);
      
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Preserve current state on offline / network failures so optimistically
      // created contacts aren't wiped from the UI.
      if (!(isLikelyOffline() || isNetworkError(error))) {
        setContacts([]);
      }
    }
  }, []);

  // Pull-to-refresh: sync offline changes, then reload lists without a full-screen spinner
  const refreshAllData = useCallback(async () => {
    if (isLikelyOffline()) return;
    try { await drainQueue(); } catch (e) { console.error('Sync before refresh failed:', e); }
    apiCache.clear();
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const loadAll = allTasksLoaded || tasks.length > TASKS_PER_PAGE;
    try {
      const [tasksRes, contactsRes] = await Promise.all([
        axios.get(`${API}/tasks`, { headers, params: loadAll ? { all: true } : { page: 1, limit: TASKS_PER_PAGE } }),
        axios.get(`${API}/contacts`, { headers }),
      ]);
      const freshTasks = tasksRes.data || [];
      setTasks(freshTasks);
      if (!loadAll) {
        setHasMoreTasks(freshTasks.length === TASKS_PER_PAGE);
        setCurrentPage(1);
      }
      setContacts(contactsRes.data || []);
    } catch (e) {
      console.error('Pull-to-refresh failed:', e);
    }
  }, [allTasksLoaded, tasks.length, TASKS_PER_PAGE]);

  // Refresh on return: reload lists if the app was in the background for 5+ minutes
  const refreshAllDataRef = useRef(refreshAllData);
  refreshAllDataRef.current = refreshAllData;
  useEffect(() => {
    const AWAY_MS = 5 * 60 * 1000;
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt >= AWAY_MS && localStorage.getItem('token')) {
        hiddenAt = null;
        refreshAllDataRef.current();
      } else {
        hiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      // Check cache first
      const cacheKey = `categories_${localStorage.getItem('token')?.slice(0, 10)}`;
      const cachedData = apiCache.get(cacheKey);
      
      if (cachedData) {
        setCategories(cachedData);
        return;
      }

      const response = await axios.get(`${API}/categories`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const categoriesData = response.data || [];
      setCategories(categoriesData);
      
      // Cache the response
      apiCache.set(cacheKey, categoriesData);
      
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  }, []);

  const createCategory = useCallback(async (categoryData) => {
    try {
      console.log('🟢 [Components.js] Creating category:', categoryData);
      const response = await axios.post(`${API}/categories`, categoryData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const newCategory = response.data;
      console.log('🟢 [Components.js] New category received:', newCategory);
      
      // Immediately add the new category to the UI (optimistic update)
      setCategories(prev => {
        console.log('🟢 [Components.js] Previous categories count:', prev.length);
        const updated = [newCategory, ...prev];
        console.log('🟢 [Components.js] Updated categories count:', updated.length);
        return updated;
      });
      
      // Clear cache only, NO immediate refresh
      invalidateCategoriesCache();
      console.log('🟢 [Components.js] Category added to UI successfully');
      
      return newCategory;
    } catch (error) {
      console.error('❌ [Components.js] Error creating category:', error);
      throw error;
    }
  }, []);

  const updateCategory = useCallback(async (categoryId, categoryData) => {
    try {
      const response = await axios.put(`${API}/categories/${categoryId}`, categoryData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      await fetchCategories(); // Refresh categories after update
      invalidateCategoriesCache(); // Clear categories cache
      return response.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }, [fetchCategories]);

  const deleteCategory = useCallback(async (categoryId) => {
    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      await fetchCategories(); // Refresh categories after deletion
      invalidateCategoriesCache(); // Clear categories cache
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }, [fetchCategories]);

  // Cache invalidation helpers
  const invalidateTasksCache = useCallback(() => {
    const tokenId = localStorage.getItem('token')?.slice(0, 10);
    if (tokenId) {
      apiCache.invalidate(`tasks_${tokenId}_page_1`);
      apiCache.invalidate(`tasks_${tokenId}_page_2`);
      apiCache.invalidate(`tasks_${tokenId}_page_3`);
      // Clear more pages if needed
      for (let i = 1; i <= 10; i++) {
        apiCache.invalidate(`tasks_${tokenId}_page_${i}`);
      }
    }
  }, []);

  // Expose cache invalidation to DataContext
  useEffect(() => {
    window.invalidateTasksCache = invalidateTasksCache;
    return () => {
      delete window.invalidateTasksCache;
    };
  }, [invalidateTasksCache]);

  const invalidateContactsCache = useCallback(() => {
    const tokenId = localStorage.getItem('token')?.slice(0, 10);
    if (tokenId) {
      apiCache.invalidate(`contacts_${tokenId}_${localStorage.getItem('activeWorkspaceId') || 'personal'}`);
    }
  }, []);

  // Expose contacts cache invalidation to DataContext
  useEffect(() => {
    window.invalidateContactsCache = invalidateContactsCache;
    return () => {
      delete window.invalidateContactsCache;
    };
  }, [invalidateContactsCache]);

  const invalidateCategoriesCache = useCallback(() => {
    const tokenId = localStorage.getItem('token')?.slice(0, 10);
    if (tokenId) {
      apiCache.invalidate(`categories_${tokenId}`);
    }
  }, []);

  // Handle voice task creation callback (placed after all referenced functions)
  const handleVoiceTaskCreated = useCallback(async (newTask) => {
    // Immediately add the new task to the UI (optimistic update)
    setTasks(prevTasks => [newTask, ...prevTasks]);
    
    // Clear cache and refresh in background
    invalidateTasksCache();
    fetchTasks(1, false).catch(err => console.error('Background refresh failed:', err));
    
    setShowVoiceTaskCreator(false);
  }, [fetchTasks, invalidateTasksCache]);

  // Create contact lookup map for O(1) performance
  const contactLookup = useMemo(() => {
    const lookup = {};
    contacts.forEach(contact => {
      lookup[contact.id] = contact.name;
    });
    return lookup;
  }, [contacts]);

  // Optimized contact name getter
  const getContactName = useCallback((contactId) => {
    if (!contactId) return null;
    return contactLookup[contactId] || `Contact #${contactId}`;
  }, [contactLookup]);

  useEffect(() => {
    // Check if we're on calendar page - if so, load ALL tasks, otherwise preserve current state
    const isCalendarPage = location.pathname === '/calendar';
    
    if (isCalendarPage) {
      // Load ALL tasks for calendar
      const loadAllTasksForCalendar = async () => {
        try {
          setLoading(true);
          console.log('📅 Loading all tasks for calendar page...');
          const response = await axios.get(`${API}/tasks`, {
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            params: {
              all: true // Use the new 'all' parameter to get all tasks
            }
          });
          
          const allTasks = response.data || [];
          console.log(`✅ Loaded ${allTasks.length} tasks for calendar`);
          setTasks(allTasks);
          setHasMoreTasks(false); // No more tasks to load for calendar
          
        } catch (error) {
          console.error('Error loading all tasks for calendar:', error);
          // Fallback to regular fetch
          fetchTasks();
        } finally {
          setLoading(false);
        }
      };
      
      loadAllTasksForCalendar();
    } else {
      // For tasks page - only load initial data if we don't have any tasks yet AND user hasn't loaded all tasks
      if (tasks.length === 0 && !allTasksLoaded) {
        fetchTasks(); // Load first page only if no tasks and not manually loaded all
        fetchContacts();
        fetchCategories();
      }
    }
    
    // Always load contacts and categories if they're empty (but don't reload if manual load happened)
    if (contacts.length === 0 && !allTasksLoaded) {
      fetchContacts();
    }
    if (categories.length === 0 && !allTasksLoaded) {
      fetchCategories();
    }
  }, [user, allTasksLoaded]);

  // Refetch tasks whenever the active workspace changes — the axios
  // interceptor now scopes the query to the new org_id, but the existing
  // useEffect above only fires on first mount. We listen to the custom
  // event WorkspaceContext dispatches so we don't have to plumb the
  // active workspace through this big component manually. We must also
  // invalidate the in-memory cache — its key doesn't include the active
  // workspace so a stale Personal page-1 entry would shadow the org one.
  useEffect(() => {
    const onChange = () => {
      invalidateTasksCache();
      setTasks([]);
      setAllTasksLoaded(false);
      setHasMoreTasks(true);
      fetchTasks();
      setContacts([]);
      fetchContacts();
    };
    window.addEventListener('edotask:workspace-changed', onChange);
    return () => window.removeEventListener('edotask:workspace-changed', onChange);
  }, [fetchTasks, fetchContacts, invalidateTasksCache]);

  // Original effect body continued (no-op placeholder to keep indentation)
  useEffect(() => { /* noop */
  }, [location.pathname, fetchTasks, fetchContacts, fetchCategories, tasks.length, allTasksLoaded, contacts.length, categories.length]); // React to route changes but preserve task state

  // Force refetch when navigating back to tasks list (for completed tasks, etc.)
  useEffect(() => {
    if (location.pathname === '/' && tasks.length > 0) {
      // Check if we need to refetch (e.g., coming back from task details)
      const shouldRefresh = sessionStorage.getItem('needsTaskRefresh');
      if (shouldRefresh === 'true') {
        console.log('🔄 Refreshing tasks list after task update');
        invalidateTasksCache();
        fetchTasks(1, false);
        sessionStorage.removeItem('needsTaskRefresh');
      }
    }
  }, [location.pathname, fetchTasks, invalidateTasksCache, tasks.length]);

  // Mobile Layout Component — phone-first with a "professional" desktop
  // sidebar that lights up automatically when the viewport is ≥ lg
  // (1024 px). Personal and Business workspaces both get a desktop
  // sidebar, but with deliberately different styling so the upgrade
  // path feels meaningful:
  //   • Personal → slim 224 px sidebar, blue brand accent, basic nav,
  //     and a "Pro features" upsell panel for free users (drives
  //     conversion using the extra desktop real estate).
  //   • Business → 256 px sidebar, indigo accent, WorkspaceSwitcher
  //     pinned at the top, plus the existing "Signed in as …" footer.
  // On screens smaller than `lg:` everyone keeps the existing
  // mobile-first chrome (gradient header + bottom nav) untouched.
  const navItems = [
    { to: '/',          Icon: PhListChecks, label: t('tasks'),    match: (p) => p === '/' },
    { to: '/calendar',  Icon: PhCalendar,   label: t('calendar'), match: (p) => p === '/calendar' },
    { to: '/contacts',  Icon: PhUsers,      label: t('contacts'), match: (p) => p === '/contacts' },
    { to: '/organization/members', Icon: PhBuildings, label: t('team') && t('team') !== 'team' ? t('team') : 'Team',     match: (p) => p.startsWith('/organization') },
    { to: '/settings',  Icon: PhGear,       label: t('settings'), match: (p) => p.startsWith('/settings') },
  ];

  const MobileLayout = ({ children, title, subtitle, onRefresh }) => {
    const { activeWorkspace } = useWorkspace(); // Read inside the provider's subtree
    const inOrg = activeWorkspace?.kind === 'org';
    const isPro = user?.subscription_status === 'pro' || user?.subscription_status === 'trial' || user?.subscription_status === 'active';
    const showDesktopSidebar = true; // Personal + Business both get a desktop shell at `lg:`. CSS hides it on small screens.
    const personalNavItems = navItems.filter((item) => item.to !== '/organization/members'); // Hide "Team" in personal mode
    const itemsForSidebar = inOrg ? navItems : personalNavItems;
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className={showDesktopSidebar ? "lg:flex lg:min-h-screen" : ""}>
          {/* ---- Desktop sidebar (lg+ only). Styling differs between
                  Personal (slim, blue accent, Pro upsell) and Business
                  (wider, indigo, WorkspaceSwitcher pinned). ---- */}
          {showDesktopSidebar && (
            <aside
              className={`hidden lg:flex lg:flex-col lg:min-h-screen lg:bg-white lg:border-r lg:border-gray-200 lg:px-4 lg:py-6 ${inOrg ? 'lg:w-64' : 'lg:w-56'}`}
              data-testid={inOrg ? 'business-desktop-sidebar' : 'personal-desktop-sidebar'}
            >
              <div className="flex items-center gap-2 mb-6 px-1">
                <img
                  src="/logos/edotask-wordmark.svg"
                  alt="Edotask"
                  className="h-7"
                  style={inOrg
                    ? { filter: 'brightness(0) saturate(100%) invert(28%) sepia(94%) saturate(2008%) hue-rotate(229deg) brightness(95%) contrast(94%)' }
                    : { filter: 'brightness(0) saturate(100%) invert(28%) sepia(94%) saturate(3151%) hue-rotate(207deg) brightness(95%) contrast(94%)' }
                  }
                />
              </div>

              {/* Workspace switcher lives in the page header (top-right) on desktop. */}

              <nav className="flex flex-col gap-1">
                {itemsForSidebar.map((item) => {
                  const active = item.match(location.pathname);
                  const accentActive = inOrg ? 'bg-indigo-50 text-indigo-700' : 'bg-blue-50 text-blue-700';
                  const Icon = item.Icon;
                  return (
                    <button
                      key={item.to}
                      onClick={() => navigate(item.to)}
                      data-testid={`${inOrg ? 'business' : 'personal'}-nav-${item.to.replace(/\//g, '-')}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                        active ? accentActive : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon size={18} weight={active ? 'fill' : 'regular'} aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Personal-mode Pro upsell — uses the desktop real-estate
                  to surface what Pro unlocks. Hidden if the user is
                  already on Pro/trial, and hidden in Business mode. */}
              {!inOrg && !isPro && (
                <div
                  className="mt-6 rounded-xl p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100"
                  data-testid="personal-sidebar-pro-upsell"
                >
                  <p className="text-xs font-semibold text-blue-900 mb-2">🚀 Unlock Pro</p>
                  <ul className="text-[11px] text-blue-900/80 space-y-1 mb-3 leading-snug">
                    <li>✨ AI Daily Brief</li>
                    <li>🎙️ Voice task entry</li>
                    <li>📎 File attachments</li>
                    <li>♾️ Unlimited tasks</li>
                  </ul>
                  <button
                    onClick={() => navigate('/settings/subscription')}
                    data-testid="personal-sidebar-upgrade-btn"
                    className="w-full text-xs bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium py-1.5 rounded-md hover:from-blue-700 hover:to-indigo-700 transition-all"
                  >
                    Try 14 days free
                  </button>
                </div>
              )}

              <div className="mt-auto pt-6 px-1 space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(t('signOutConfirm'))) {
                      localStorage.removeItem('token');
                      localStorage.removeItem('user');
                      window.location.href = '/login';
                    }
                  }}
                  data-testid="desktop-sidebar-signout"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors border border-red-100"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('signOut')}
                </button>
                <p className="text-xs text-gray-400 truncate">Signed in as {user?.name || user?.email}</p>
              </div>
            </aside>
          )}

          {/* ---- Main column ---- */}
          <div className={showDesktopSidebar ? "flex-1 min-w-0 lg:bg-gray-50" : ""}>
            {/* Header — kept on mobile; hidden on desktop in favour of an
                inline page title (cleaner desktop look). */}
            <div className={`bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 ${showDesktopSidebar ? 'lg:hidden' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center min-w-0">
                  <img
                    src="/logos/edotask-wordmark.svg"
                    alt="Edotask"
                    className="h-8 mr-3"
                    style={{filter: 'brightness(0) invert(1)'}}
                  />
                  <h1 className="text-xl font-bold truncate">{title}</h1>
                </div>
                <WorkspaceSwitcher />
              </div>
              {subtitle && <div className="text-blue-100 opacity-90 text-sm">{subtitle}</div>}
            </div>

            {/* Desktop page header (replaces the gradient header at lg+) */}
            {showDesktopSidebar && (
              <div className="hidden lg:block relative lg:z-[60] px-8 pt-8 pb-4 border-b border-gray-200 bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                    {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
                  </div>
                  {/* Workspace switcher + notification bell pinned top-right */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <NotificationBell />
                    <div data-testid="desktop-header-switcher">
                      <WorkspaceSwitcher />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div
              className={showDesktopSidebar ? "p-4 lg:px-8 lg:py-6 lg:pb-12" : "p-4"}
              style={{ paddingBottom: 'calc(12rem + var(--safe-bottom, 0px))' }}
            >
              {onRefresh ? <PullToRefresh onRefresh={onRefresh}>{children}</PullToRefresh> : children}
            </div>
          </div>
        </div>

        {/* Bottom Navigation — mobile only when the desktop sidebar is showing */}
        <div
          className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 shadow-lg ${showDesktopSidebar ? 'lg:hidden' : ''}`}
        >
          <div className="flex justify-around">
            <button
              onClick={() => navigate('/')}
              className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                location.pathname === '/' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-xl mb-1">📋</span>
              <span className="text-xs font-medium">{t('tasks')}</span>
            </button>
            <button
              onClick={() => navigate('/calendar')}
              className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                location.pathname === '/calendar' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-xl mb-1">📅</span>
              <span className="text-xs font-medium">{t('calendar')}</span>
            </button>
            <button
              onClick={() => navigate('/contacts')}
              className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                location.pathname === '/contacts' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-xl mb-1">👥</span>
              <span className="text-xs font-medium">{t('contacts')}</span>
            </button>
            <button
              onClick={() => navigate('/settings')}
              className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                location.pathname.startsWith('/settings') ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="text-xl mb-1">⚙️</span>
              <span className="text-xs font-medium">{t('settings')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Edit Task Modal (Enhanced AddTaskModal for editing)
  const EditTaskModal = ({ isOpen, onClose, task }) => {
    const { t } = useLanguage();
    const { user } = useAuth();
    // Visibility editing only makes sense for tasks that live inside an
    // organization workspace. Determined from the task itself (not the
    // user's *active* workspace) so users editing an org task while
    // visiting Personal mode still see the selector.
    const isOrgTask = !!task?.org_id;
    
    // Debug: Log user subscription status
    useEffect(() => {
      if (isOpen) {
        console.log('🔍 EditTaskModal - User subscription status:', user?.subscription_status);
        console.log('🔍 EditTaskModal - Should show Pro badge:', user?.subscription_status !== 'pro');
      }
    }, [isOpen, user]);
    
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [existingFiles, setExistingFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [formData, setFormData] = useState({
      title: '',
      due_date: '',
      time: '',
      category: '',
      priority: 'Medium',
      location: '',
      contact_id: '',
      notes: '',
      recurring: false,
      recurrence_pattern: '',
      recurrence_end_date: '',
      reminder_enabled: false,
      visibility: 'team',
    });

    // Initialize form data when task changes
    useEffect(() => {
      if (task && isOpen) {
        setFormData({
          title: task.title || '',
          due_date: task.due_date ? task.due_date.split('T')[0] : '',
          time: task.time || '',
          category: task.category || '',
          priority: task.priority || 'Medium',
          location: task.location || '',
          contact_id: task.contact_id || '',
          notes: task.notes || '',
          recurring: task.recurring || false,
          recurrence_pattern: task.recurrence_pattern || '',
          recurrence_end_date: task.recurrence_end_date ? task.recurrence_end_date.split('T')[0] : '',
          reminder_enabled: task.reminder_enabled || false,
          // Default an org task that pre-dates the visibility field to
          // "team" since that's what GET /api/tasks already implicitly
          // treats it as (see the team-scope $or clause in server.py).
          visibility: task.visibility || (task.org_id ? 'team' : 'team'),
        });
        setExistingFiles(task.attachments || []);
        setAttachments([]);
      }
    }, [task, isOpen]);

    const handleFileChange = useCallback(async (e) => {
      const files = Array.from(e.target.files);
      
      if (files.length === 0) return;
      
      try {
        // Get user plan from auth context or default to 'free'
        const userPlan = user?.subscription_status || 'free';
        const currentFileCount = (existingFiles?.length || 0) + attachments.length;
        
        // Validate and prepare files with compression
        const result = await prepareFilesForUpload(files, currentFileCount, userPlan);
        
        // Show errors if any
        if (result.errors.length > 0) {
          const errorMessages = result.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
          alert(`Some files could not be added:\n${errorMessages}`);
        }
        
        // Add successfully prepared files
        if (result.preparedFiles.length > 0) {
          const processedFiles = result.preparedFiles.map(p => p.file);
          setAttachments(prev => [...prev, ...processedFiles]);
          
          // Show compression summary if any images were compressed
          const compressedCount = result.preparedFiles.filter(p => p.compressed).length;
          if (compressedCount > 0) {
            console.log(`✅ ${compressedCount} image(s) compressed before upload`);
          }
        }
      } catch (error) {
        alert(getUploadErrorMessage(error));
      }
      
      // Clear input
      e.target.value = '';
    }, [attachments, existingFiles, user]);

    const removeAttachment = useCallback((index) => {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    }, []);

    const removeExistingFile = useCallback(async (fileId, index) => {
      if (window.confirm('Are you sure you want to delete this file?')) {
        try {
          await axios.delete(`${API}/tasks/${task.id}/attachments/${fileId}`);
          setExistingFiles(prev => prev.filter((_, i) => i !== index));
        } catch (error) {
          console.error('Error deleting file:', error);
          alert('Failed to delete file');
        }
      }
    }, [task]);

    // Audio recording functions
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
          setAttachments(prev => [...prev, file]);
          stream.getTracks().forEach(track => track.stop());
        };

        setMediaRecorder(recorder);
        recorder.start();
        setIsRecording(true);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        alert("Unable to access microphone. Please check permissions.");
      }
    };

    const stopRecording = () => {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
        setIsRecording(false);
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        // Update the task
        const taskData = {
          ...formData,
          due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
          recurrence_end_date: formData.recurrence_end_date ? new Date(formData.recurrence_end_date).toISOString() : null
        };
        // Personal tasks have no visibility concept — keep the payload clean.
        if (!isOrgTask) {
          delete taskData.visibility;
        }

        await axios.put(`${API}/tasks/${task.id}`, taskData);

        // Handle new file attachments
        const uploadErrors = [];
        if (attachments.length > 0) {
          setUploadProgress({ current: 0, total: attachments.length, filename: '' });

          uploadErrors.push(...await uploadTaskFilesToDrive(task.id, attachments, {
            onFileStart: (i, file) => setUploadProgress({ current: i + 1, total: attachments.length, filename: file.name })
          }));
        }

        if (uploadErrors.length > 0) {
          alert(
            'The task was saved but some attachments failed to upload:\n\n' +
            uploadErrors.map(e => `• ${e.filename}: ${e.message}`).join('\n')
          );
        }

        await fetchTasks(1, false); // Refresh first page after deleting task
        invalidateTasksCache(); // Clear task cache
        onClose();
        // Refresh the current page if we're on task details
        if (window.location.pathname.includes('/task/')) {
          window.location.reload();
        }
      } catch (error) {
        console.error("Error updating task:", error);
        alert("Failed to update task. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const getFileIcon = (file) => {
      if (file.type?.startsWith('image/') || file.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
        return '🖼️';
      } else if (file.type?.startsWith('audio/') || file.filename?.match(/\.(mp3|wav|webm|ogg)$/i)) {
        return '🎵';
      } else if (file.type?.includes('pdf') || file.filename?.includes('.pdf')) {
        return '📄';
      } else if (file.type?.includes('document') || file.type?.includes('word')) {
        return '📝';
      }
      return '📎';
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
        <div className="bg-white rounded-t-3xl w-full max-h-[95vh] overflow-y-auto animate-slide-up">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Edit Task</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('title')} *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  placeholder={t('enterTaskTitle')}
                  enterKeyHint="done"
                  autoCapitalize="sentences"
                />
                {/* Discoverability hint for the phone keyboard's built-in dictation —
                    works on iOS Safari and Android Gboard/SwiftKey at zero AI cost. */}
                <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-14 0m7 7v4m-4 0h8m-9-7a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  {t('micTip')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('dueDate')}</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('time')}</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('category')}</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('selectCategory')}</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('priority')}</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Low">{t('low')}</option>
                    <option value="Medium">{t('medium')}</option>
                    <option value="High">{t('high')}</option>
                  </select>
                </div>
              </div>

              {/* Visibility — Owners/creators can flip a team task to private
                  (or vice versa) post-creation. Only renders when the task
                  belongs to an organization workspace. */}
              {isOrgTask && (
                <div data-testid="edit-task-visibility-row">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    {t('visibility') || 'Visibility'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, visibility: 'team' })}
                      data-testid="edit-visibility-team-button"
                      className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                        formData.visibility === 'team'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span aria-hidden="true">🏢</span>
                        <span>{t('visibilityTeam') || 'Team'}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {t('visibilityTeamHint') || 'Visible to everyone'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, visibility: 'private' })}
                      data-testid="edit-visibility-private-button"
                      className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                        formData.visibility === 'private'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span aria-hidden="true">🔒</span>
                        <span>{t('visibilityPrivate') || 'Private'}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {t('visibilityPrivateHint') || 'Only you'}
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('location')}</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('enterLocation')}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('contact')}</label>
                  <select
                    value={formData.contact_id}
                    onChange={(e) => setFormData({...formData, contact_id: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('selectContact')}</option>
                    {contacts.map(contact => (
                      <option key={contact.id} value={contact.id}>{contact.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder={t('addNotes')}
                />
              </div>

              {/* Reminder Checkbox - PRO ONLY, mobile only */}
              {!isDesktop && (
              <div className="flex items-center space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <input
                  type="checkbox"
                  id="reminder-checkbox-edit"
                  checked={formData.reminder_enabled}
                  onChange={(e) => {
                    if (!hasProAccess(user)) {
                      alert('Task reminders are a Pro feature. Upgrade to Pro to enable reminders.');
                      return;
                    }
                    setFormData({...formData, reminder_enabled: e.target.checked});
                  }}
                  className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  disabled={!hasProAccess(user)}
                />
                <label htmlFor="reminder-checkbox-edit" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">⏰ {t('enableReminder') || 'Enable Reminder'}</span>
                    {!hasProAccess(user) && <ProBadge size="sm" />}
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('reminderDescription') || 'Receive a reminder 1 hour before the due time via your preferred method (Email/SMS)'}
                  </p>
                </label>
              </div>
              )}
            </div>

            {/* Existing Files */}
            {existingFiles.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-4">Current Files ({existingFiles.length})</h3>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {existingFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-blue-50 rounded-lg p-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getFileIcon(file)}</span>
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {file.filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {file.size_mb} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExistingFile(file.id, index)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete file"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* File Attachments & Media */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-900 mb-4">Add New Files & Media</h3>
              
              <DriveAttachGate>
              <div className="grid grid-cols-3 gap-4">
                {/* File Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload-edit"
                    accept="*/*"
                  />
                  <label htmlFor="file-upload-edit" className="cursor-pointer">
                    <div className="text-2xl mb-2">📁</div>
                    <p className="text-xs text-gray-600">{t('uploadFiles')}</p>
                  </label>
                </div>

                {/* Audio Recording */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`text-2xl mb-2 ${isRecording ? 'animate-pulse text-red-500' : 'text-gray-400'}`}
                  >
                    {isRecording ? '⏹️' : '🎤'}
                  </button>
                  <p className="text-xs text-gray-600">
                    {isRecording ? 'Recording...' : t('recordAudio')}
                  </p>
                </div>

                {/* Camera/Photo */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    className="hidden"
                    id="photo-capture-edit"
                  />
                  <label htmlFor="photo-capture-edit" className="cursor-pointer">
                    <div className="text-2xl mb-2">📷</div>
                    <p className="text-xs text-gray-600">{t('takePhoto')}</p>
                  </label>
                </div>
              </div>
              </DriveAttachGate>

              {/* New Files Preview */}
              {attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">New Files ({attachments.length})</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 rounded-lg p-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getFileIcon(file)}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate max-w-40">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploadProgress.total > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span>Uploading: {uploadProgress.filename}</span>
                    <span>{uploadProgress.current}/{uploadProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                disabled={loading}
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Task'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // File Viewer Modal with Navigation (Fixed version)
  const FileViewerModal = ({ isOpen, onClose, files, initialIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
      setCurrentIndex(initialIndex);
    }, [initialIndex, isOpen]);

    if (!isOpen || !files || files.length === 0) return null;

    const currentFile = files[currentIndex];

    const handlePrevious = () => {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : files.length - 1));
    };

    const handleNext = () => {
      setCurrentIndex((prev) => (prev < files.length - 1 ? prev + 1 : 0));
    };

    const handleDownload = async () => {
      try {
        // For mock data or base64 data, create download directly
        if (currentFile.data_url) {
          const link = document.createElement('a');
          link.href = currentFile.data_url;
          link.setAttribute('download', currentFile.filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          // Try API download endpoint
          const response = await axios.get(`${API}/tasks/attachment/${currentFile.id}/download`, {
            responseType: 'blob'
          });
          
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', currentFile.filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Download failed:', error);
        alert('Download feature coming soon!');
      }
    };

    const getFileType = () => {
      const filename = currentFile.filename?.toLowerCase() || '';
      const contentType = currentFile.content_type?.toLowerCase() || '';
      
      if (contentType.startsWith('image/') || filename.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) {
        return 'image';
      } else if (contentType.startsWith('audio/') || filename.match(/\.(mp3|wav|webm|ogg|m4a)$/)) {
        return 'audio';
      } else if (contentType.includes('pdf') || filename.includes('.pdf')) {
        return 'pdf';
      } else if (contentType.startsWith('video/') || filename.match(/\.(mp4|webm|ogg|mov|avi)$/)) {
        return 'video';
      }
      return 'other';
    };

    const getFileUrl = () => {
      // For demo purposes, create a placeholder image URL for images
      const fileType = getFileType();
      
      if (currentFile.data_url) {
        return currentFile.data_url;
      }
      
      // Create mock/demo content for testing
      switch (fileType) {
        case 'image':
          // Return a placeholder image for demo
          return `https://via.placeholder.com/600x400/3B82F6/FFFFFF?text=${encodeURIComponent(currentFile.filename)}`;
        case 'audio':
          // For audio, we'll show controls but no source for now
          return null;
        case 'pdf':
          // For PDF, we'll show an icon and open button
          return null;
        default:
          return null;
      }
    };

    const renderFileContent = () => {
      const fileType = getFileType();
      const fileUrl = getFileUrl();

      switch (fileType) {
        case 'image':
          return (
            <div className="flex items-center justify-center w-full h-full">
              <img 
                src={fileUrl}
                alt={currentFile.filename}
                className="max-w-full max-h-full object-contain rounded-lg"
                style={{ maxHeight: '70vh', maxWidth: '90vw' }}
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  e.target.src = `https://via.placeholder.com/400x300/6B7280/FFFFFF?text=Image+Preview`;
                }}
              />
            </div>
          );
        case 'audio':
          return (
            <div className="flex flex-col items-center space-y-6">
              <div className="text-8xl">🎵</div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">{currentFile.filename}</h3>
                <p className="text-gray-300 mb-4">Audio playback will be available when files are properly uploaded</p>
              </div>
              {fileUrl && (
                <audio controls className="w-full max-w-md">
                  <source src={fileUrl} type={currentFile.content_type} />
                  Your browser does not support the audio element.
                </audio>
              )}
            </div>
          );
        case 'video':
          return (
            <div className="flex items-center justify-center w-full h-full">
              {fileUrl ? (
                <video controls className="max-w-full max-h-full rounded-lg" style={{ maxHeight: '70vh' }}>
                  <source src={fileUrl} type={currentFile.content_type} />
                  Your browser does not support the video element.
                </video>
              ) : (
                <div className="flex flex-col items-center space-y-4">
                  <div className="text-8xl">🎬</div>
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-white mb-2">{currentFile.filename}</h3>
                    <p className="text-gray-300">Video playback will be available when files are properly uploaded</p>
                  </div>
                </div>
              )}
            </div>
          );
        case 'pdf':
          return (
            <div className="flex flex-col items-center space-y-6">
              <div className="text-8xl">📄</div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">{currentFile.filename}</h3>
                <p className="text-gray-300 mb-4">
                  {currentFile.content_type} • {currentFile.size_mb} MB
                </p>
              </div>
              <button
                onClick={() => window.open(fileUrl || '#', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Open PDF
              </button>
            </div>
          );
        default:
          return (
            <div className="flex flex-col items-center space-y-6">
              <div className="text-8xl">📎</div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">{currentFile.filename}</h3>
                <p className="text-gray-300 mb-2">
                  {currentFile.content_type} • {currentFile.size_mb} MB
                </p>
                <p className="text-gray-400 text-sm">
                  File preview not available for this type
                </p>
              </div>
            </div>
          );
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-white z-10">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium truncate max-w-xs">{currentFile.filename}</h3>
            <span className="text-sm opacity-75">
              {currentIndex + 1} of {files.length}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Download file"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Arrows */}
        {files.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors z-10"
              title="Previous file"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors z-10"
              title="Next file"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* File Content */}
        <div className="flex items-center justify-center w-full h-full pt-16" style={{ paddingBottom: 'calc(12rem + var(--safe-bottom, 0px))' }}>
          {renderFileContent()}
        </div>

        {/* File Info Footer */}
        <div className="absolute bottom-4 left-4 right-4 text-center text-white text-sm opacity-75">
          <p>
            {currentFile.content_type} • {currentFile.size_mb} MB
            {currentFile.file_type === 'completion' && ' • Completion File'}
          </p>
        </div>

        {/* Thumbnails Navigation */}
        {files.length > 1 && (
          <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex space-x-2 max-w-xs overflow-x-auto bg-black bg-opacity-50 rounded-lg p-2">
            {files.map((file, index) => {
              const isActive = index === currentIndex;
              const fileType = file.content_type?.startsWith('image/') ? '🖼️' :
                              file.content_type?.startsWith('audio/') ? '🎵' :
                              file.content_type?.includes('pdf') ? '📄' : '📎';
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-12 h-12 rounded border-2 transition-all ${
                    isActive ? 'border-blue-400 scale-110' : 'border-gray-600 hover:border-gray-400'
                  }`}
                  title={file.filename}
                >
                  <div className="w-full h-full bg-gray-700 rounded flex items-center justify-center text-white text-lg">
                    {fileType}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Enhanced Add Task Modal with ALL features
  const AddTaskModal = ({ isOpen, onClose, initialDate }) => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { activeWorkspace } = useWorkspace();
    const inOrgWorkspace = activeWorkspace?.kind === 'org';
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    
    // Conflict detection state
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictData, setConflictData] = useState({ exactMatches: [], conflicts: [] });
    const [pendingTaskData, setPendingTaskData] = useState(null);
    const [notifyContact, setNotifyContact] = useState(() => readAddTaskDraft()?.notifyContact || { email: false, sms: false, message: '', edited: false });
    const canMessageContact = isDesktop && activeWorkspace?.kind === 'org';

    const [formData, setFormData] = useState(() => {
      // Restore what the user typed if a background refresh re-created this modal
      const draft = readAddTaskDraft()?.formData;
      if (draft) return initialDate ? { ...draft, due_date: initialDate } : draft;
      return {
      title: '',
      due_date: initialDate || '',
      time: '',
      category: '',
      priority: 'Medium',
      location: '',
      contact_id: '',
      notes: '',
      recurring: false,
      recurrence_pattern: '',
      recurrence_end_date: '',
      reminder_enabled: false,
      // Business workspaces — defaults to "team" when creating a task in
      // an Organization workspace so team members can see it. Switched
      // by the user via the Visibility selector below. Ignored by the
      // backend (set to null) for personal tasks.
      visibility: 'team',
      };
    });

    // Keep a draft while the form is open (cleared in handleCloseAddTask)
    useEffect(() => {
      if (isOpen) sessionStorage.setItem(ADD_TASK_DRAFT_KEY, JSON.stringify({ formData, notifyContact }));
    }, [isOpen, formData, notifyContact]);

    const handleFileChange = useCallback(async (e) => {
      const files = Array.from(e.target.files);
      
      if (files.length === 0) return;
      
      try {
        // Get user plan from auth context or default to 'free'
        const userPlan = user?.subscription_status || 'free';
        const currentFileCount = attachments.length; // For new task, only count current attachments
        
        // Validate and prepare files with compression
        const result = await prepareFilesForUpload(files, currentFileCount, userPlan);
        
        // Show errors if any
        if (result.errors.length > 0) {
          const errorMessages = result.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
          alert(`Some files could not be added:\n${errorMessages}`);
        }
        
        // Add successfully prepared files
        if (result.preparedFiles.length > 0) {
          const processedFiles = result.preparedFiles.map(p => p.file);
          setAttachments(prev => [...prev, ...processedFiles]);
          
          // Show compression summary if any images were compressed
          const compressedCount = result.preparedFiles.filter(p => p.compressed).length;
          if (compressedCount > 0) {
            console.log(`✅ ${compressedCount} image(s) compressed before upload`);
          }
        }
      } catch (error) {
        alert(getUploadErrorMessage(error));
      }
      
      // Clear input
      e.target.value = '';
    }, [attachments, user]);

    const removeAttachment = useCallback((index) => {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Audio recording functions
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
          setAttachments(prev => [...prev, file]);
          stream.getTracks().forEach(track => track.stop());
        };

        setMediaRecorder(recorder);
        recorder.start();
        setIsRecording(true);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        alert("Unable to access microphone. Please check permissions.");
      }
    };

    const stopRecording = () => {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
        setIsRecording(false);
      }
    };

    const checkConflicts = async (taskData) => {
      // Offline path: skip the network entirely. Conflict-checking is a
      // best-effort feature and we'd rather let the user save offline than
      // block them on a timeout. The user can resolve any real conflict the
      // next time they're online and the queue drains.
      if (isLikelyOffline()) {
        return { has_exact_match: false, has_conflicts: false, exact_match_tasks: [], conflict_tasks: [] };
      }
      try {
        const response = await axios.post(`${API}/tasks/check-conflicts`, {
          due_date: taskData.due_date,
          time: taskData.time
        }, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          timeout: 8000
        });
        return response.data;
      } catch (error) {
        console.error('Error checking conflicts:', error);
        return { has_exact_match: false, has_conflicts: false, exact_match_tasks: [], conflict_tasks: [] };
      }
    };    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        // Prepare task data
        const taskData = {
          ...formData,
          due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
          recurrence_end_date: formData.recurrence_end_date ? new Date(formData.recurrence_end_date).toISOString() : null
        };
        // Strip visibility for personal-workspace creates — the backend
        // ignores it anyway, but keeping the payload clean prevents any
        // future server-side validation from rejecting legacy clients.
        if (!inOrgWorkspace) {
          delete taskData.visibility;
        }

        // Check for conflicts
        const conflictCheck = await checkConflicts(taskData);
        
        if (conflictCheck.has_exact_match || conflictCheck.has_conflicts) {
          // Show conflict modal
          setConflictData({
            exactMatches: conflictCheck.exact_match_tasks || [],
            conflicts: conflictCheck.conflict_tasks || []
          });
          setPendingTaskData(taskData);
          setShowConflictModal(true);
          setLoading(false);
          return; // Stop here, let user decide
        }

        // No conflicts, proceed with creation
        await createTaskDirectly(taskData);
      } catch (error) {
        console.error('Error creating task:', error);
        if (error.response?.status === 403) {
          alert(error.response.data.detail || "Permission denied. Please check your subscription status.");
        } else {
          alert('Failed to create task. Please try again.');
        }
        setLoading(false);
      }
    };

    const createTaskDirectly = async (taskData) => {
      try {
        const response = await axios.post(`${API}/tasks`, taskData, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const taskId = response.data.id;
        const newTask = response.data;

        // Handle file attachments if any
        const uploadErrors = [];
        if (attachments.length > 0) {
          setUploadProgress({ current: 0, total: attachments.length, filename: '' });

          uploadErrors.push(...await uploadTaskFilesToDrive(taskId, attachments, {
            onFileStart: (i, file) => setUploadProgress({ current: i + 1, total: attachments.length, filename: file.name })
          }));
        }

        if (uploadErrors.length > 0) {
          alert(
            'The task was created but some attachments failed to upload:\n\n' +
            uploadErrors.map(e => `• ${e.filename}: ${e.message}`).join('\n')
          );
        }

        // Message the contact (email / SMS) if the user asked for it
        if (canMessageContact && formData.contact_id && (notifyContact.email || notifyContact.sms) && notifyContact.message.trim()) {
          const channels = [notifyContact.email && 'email', notifyContact.sms && 'sms'].filter(Boolean);
          const contactName = contacts.find((c) => c.id === formData.contact_id)?.name || 'the contact';
          axios.post(`${API}/tasks/${taskId}/notify-contact`, { channels, message: notifyContact.message }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }).then(({ data }) => {
            const labels = { email: 'Email', sms: 'SMS' };
            Object.entries(data.results || {}).forEach(([ch, r]) => {
              if (r.ok) toast.success(`${labels[ch]} sent to ${contactName}`);
              else toast.error(`${labels[ch]} not sent: ${r.error}`);
            });
          }).catch((err) => toast.error(err.response?.data?.detail || 'Could not message the contact.'));
        }
        setNotifyContact({ email: false, sms: false, message: '', edited: false });

        // Immediately add the new task to the UI (optimistic update)
        setTasks(prevTasks => [newTask, ...prevTasks]);
        
        // Clear cache to ensure future fetches are fresh
        invalidateTasksCache();
        
        // Refresh tasks in the background to ensure sync — but only when online.
        // Offline, the SW would serve the cached list (without our optimistic
        // task) and clobber the UI state we just set.
        if (!isLikelyOffline()) {
          fetchTasks(1, false).catch(err => console.error('Background refresh failed:', err));
        }
        
        // Reset form and close modal
        setFormData({
          title: '', due_date: '', time: '', category: '', priority: 'Medium',
          location: '', contact_id: '', notes: '', recurring: false,
          recurrence_pattern: '', recurrence_end_date: '', reminder_enabled: false,
          visibility: 'team'
        });
        setAttachments([]);
        setShowConflictModal(false);
        setPendingTaskData(null);
        onClose();
      } catch (error) {
        console.error('Error creating task:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const handleSaveAnyway = async () => {
      if (pendingTaskData) {
        setLoading(true);
        await createTaskDirectly(pendingTaskData);
      }
    };

    const handleEditTime = () => {
      setShowConflictModal(false);
      setPendingTaskData(null);
      // Form stays open for user to edit
    };

    const getFileIcon = (file) => {
      if (file.type.startsWith('image/')) {
        return '🖼️';
      } else if (file.type.startsWith('audio/')) {
        return '🎵';
      } else if (file.type.includes('pdf')) {
        return '📄';
      } else if (file.type.includes('document') || file.type.includes('word')) {
        return '📝';
      }
      return '📎';
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end lg:items-center lg:justify-center lg:p-6" style={{ touchAction: 'none' }} data-testid="add-task-modal">
        <div className="bg-white rounded-t-3xl lg:rounded-2xl w-full lg:max-w-2xl lg:shadow-2xl flex flex-col animate-slide-up" style={{ 
          maxHeight: '85vh',
          touchAction: 'pan-y',
          position: 'relative'
        }}>
          {/* Fixed Header */}
          <div className="flex items-center justify-between px-4 py-6 border-b border-gray-200 flex-shrink-0" style={{ 
            touchAction: 'none'
          }}>
            <h2 className="text-xl font-semibold text-gray-900">{t('addNewTask')}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto" style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
            overscrollBehaviorX: 'none'
          }}>
            <form onSubmit={handleSubmit} className="p-4 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('title')} *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  placeholder={t('enterTaskTitle')}
                  enterKeyHint="done"
                  autoCapitalize="sentences"
                />
                {/* Discoverability hint for the phone keyboard's built-in dictation —
                    works on iOS Safari and Android Gboard/SwiftKey at zero AI cost. */}
                <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-14 0m7 7v4m-4 0h8m-9-7a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  {t('micTip')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('dueDate')} *</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    required
                    data-testid="add-task-due-date"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('time')}</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('category')}</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('selectCategory')}</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('priority')} *</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
              </div>

              {/* Visibility — only shown when creating in a team workspace.
                  Personal tasks have no concept of visibility, so this row
                  is hidden entirely to keep the modal compact. */}
              {inOrgWorkspace && (
                <div data-testid="task-visibility-row">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    {t('visibility') || 'Visibility'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, visibility: 'team' })}
                      data-testid="visibility-team-button"
                      className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                        formData.visibility === 'team'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span aria-hidden="true">🏢</span>
                        <span>{t('visibilityTeam') || 'Team'}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {t('visibilityTeamHint') || 'Visible to everyone'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, visibility: 'private' })}
                      data-testid="visibility-private-button"
                      className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                        formData.visibility === 'private'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span aria-hidden="true">🔒</span>
                        <span>{t('visibilityPrivate') || 'Private'}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {t('visibilityPrivateHint') || 'Only you'}
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('location')}</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('enterLocation')}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('contact')}</label>
                  <select
                    value={formData.contact_id}
                    onChange={(e) => setFormData({...formData, contact_id: e.target.value})}
                    data-testid="add-task-contact-select"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('selectContact')}</option>
                    {contacts.map(contact => (
                      <option key={contact.id} value={contact.id}>{contact.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {canMessageContact && <NotifyContactSection
                contact={contacts.find((c) => c.id === formData.contact_id)}
                isPro={hasProAccess(user)}
                value={notifyContact}
                onChange={setNotifyContact}
                onUpgrade={() => toast.info('Messaging contacts is a Pro feature. Upgrade in Settings > Subscription.')}
                template={buildContactMessage({
                  contactName: contacts.find((c) => c.id === formData.contact_id)?.name,
                  senderName: user?.name,
                  title: formData.title,
                  dueDate: formData.due_date,
                  time: formData.time,
                  location: formData.location,
                })}
              />}


              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder={t('addNotes')}
                />
              </div>

              {/* Reminder Checkbox - PRO ONLY, mobile only */}
              {!isDesktop && (
              <div className="flex items-center space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-lg relative">
                <input
                  type="checkbox"
                  id="reminder-checkbox-add"
                  checked={formData.reminder_enabled}
                  onChange={(e) => {
                    if (!hasProAccess(user)) {
                      alert('Task reminders are a Pro feature. Upgrade to Pro to enable reminders.');
                      return;
                    }
                    setFormData({...formData, reminder_enabled: e.target.checked});
                  }}
                  className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  disabled={!hasProAccess(user)}
                />
                <label htmlFor="reminder-checkbox-add" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">⏰ {t('enableReminder') || 'Enable Reminder'}</span>
                    {!hasProAccess(user) && <ProBadge size="sm" />}
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('reminderDescription') || 'Receive a reminder 1 hour before the due time via your preferred method (Email/SMS)'}
                  </p>
                </label>
              </div>
              )}
            </div>

            {/* Recurring Task Options - PRO ONLY */}
            <div className="border-t pt-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={formData.recurring}
                  onChange={(e) => {
                    if (!hasProAccess(user)) {
                      alert('Recurring tasks are a Pro feature. Upgrade to Pro to create recurring tasks.');
                      return;
                    }
                    setFormData({...formData, recurring: e.target.checked});
                  }}
                  className="mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={!hasProAccess(user)}
                />
                <label className="text-gray-700 font-medium flex items-center gap-2">
                  {t('recurringTask')}
                  {!hasProAccess(user) && <ProBadge size="sm" />}
                </label>
              </div>

              {formData.recurring && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Recurrence Pattern</label>
                    <select
                      value={formData.recurrence_pattern}
                      onChange={(e) => setFormData({...formData, recurrence_pattern: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={formData.recurring}
                    >
                      <option value="">Select Pattern</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">End Date</label>
                    <input
                      type="date"
                      value={formData.recurrence_end_date}
                      onChange={(e) => setFormData({...formData, recurrence_end_date: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={formData.recurring}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* File Attachments & Media */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-900 mb-4">{t('attachmentsMedia')}</h3>
              
              <DriveAttachGate>
              <div className="grid grid-cols-3 gap-4">
                {/* File Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    accept="*/*"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="text-2xl mb-2">📁</div>
                    <p className="text-xs text-gray-600">{t('uploadFiles')}</p>
                  </label>
                </div>

                {/* Audio Recording */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`text-2xl mb-2 ${isRecording ? 'animate-pulse text-red-500' : 'text-gray-400'}`}
                  >
                    {isRecording ? '⏹️' : '🎤'}
                  </button>
                  <p className="text-xs text-gray-600">
                    {isRecording ? 'Recording...' : t('recordAudio')}
                  </p>
                </div>

                {/* Camera/Photo */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    className="hidden"
                    id="photo-capture"
                  />
                  <label htmlFor="photo-capture" className="cursor-pointer">
                    <div className="text-2xl mb-2">📷</div>
                    <p className="text-xs text-gray-600">{t('takePhoto')}</p>
                  </label>
                </div>
              </div>
              </DriveAttachGate>

              {/* Selected Files Preview */}
              {attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files ({attachments.length})</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getFileIcon(file)}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate max-w-40">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploadProgress.total > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span>Uploading: {uploadProgress.filename}</span>
                    <span>{uploadProgress.current}/{uploadProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
                disabled={loading}
              >
                {loading ? t('creating') : t('createTask')}
              </button>
            </div>
          </form>
          </div>
        </div>
        
        {/* Conflict Warning Modal */}
        {showConflictModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {conflictData.exactMatches.length > 0 ? `⚠️ ${t('timeConflictCannotSave')}` : `⚠️ ${t('schedulingConflictWarning')}`}
                </h2>
                <button onClick={handleEditTime} className="text-gray-400 hover:text-gray-600 p-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Exact Match Error */}
                {conflictData.exactMatches.length > 0 && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-red-800 mb-2">{t('exactTimeConflictDetected')}</h3>
                        <p className="text-sm text-red-700 mb-3">
                          {t('exactTimeConflictMessage')}
                        </p>
                        <div className="space-y-2">
                          {conflictData.exactMatches.map((task) => (
                            <div key={task.id} className="bg-white p-3 rounded border border-red-200">
                              <p className="font-medium text-gray-900">{task.title}</p>
                              <div className="flex items-center mt-1 text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{new Date(task.due_date).toLocaleDateString()} {t('at')} {task.time}</span>
                                <span className="ml-3 px-2 py-0.5 bg-gray-100 rounded text-xs">{task.priority}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 2-Hour Window Warning */}
                {conflictData.exactMatches.length === 0 && conflictData.conflicts.length > 0 && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-yellow-800 mb-2">{t('nearbyTasksDetected')}</h3>
                        <p className="text-sm text-yellow-700 mb-3">
                          {t('nearbyTasksMessage').replace('{count}', conflictData.conflicts.length).replace('{plural}', conflictData.conflicts.length > 1 ? 's' : '')}
                        </p>
                        <div className="space-y-2">
                          {conflictData.conflicts.map((task) => (
                            <div key={task.id} className="bg-white p-3 rounded border border-yellow-200">
                              <p className="font-medium text-gray-900">{task.title}</p>
                              <div className="flex items-center mt-1 text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{new Date(task.due_date).toLocaleDateString()} {t('at')} {task.time}</span>
                                <span className="ml-3 px-2 py-0.5 bg-gray-100 rounded text-xs">{task.priority}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4 border-t">
                  {conflictData.exactMatches.length > 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={handleEditTime}
                        className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
                      >
                        {t('cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={handleEditTime}
                        className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700"
                      >
                        {t('editTime')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleEditTime}
                        className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
                      >
                        {t('editTime')}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveAnyway}
                        className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700"
                      >
                        {t('saveAnyway')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Add Contact Modal  
  const AddContactModal = ({ isOpen, onClose }) => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      phone: '',
      company: '',
      address: ''
    });

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        console.log('📝 [Components.js AddContactModal] Creating contact:', formData);
        
        // Make API call
        const response = await axios.post(`${API}/contacts`, formData, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const newContact = response.data;
        console.log('✅ [Components.js AddContactModal] Contact created successfully:', newContact);
        
        // Optimistic update - immediately add to contacts list
        setContacts(prev => [newContact, ...prev]);
        console.log('✅ [Components.js AddContactModal] Contact added to UI');
        
        setFormData({ name: '', email: '', phone: '', company: '', address: '', sms_consent: false });
        invalidateContactsCache(); // Clear contacts cache in Components.js
        
        // Wait a bit for state to propagate
        await new Promise(resolve => setTimeout(resolve, 100));
        
        onClose();
      } catch (error) {
        console.error('❌ [Components.js AddContactModal] Error creating contact:', error);
        if (error.response?.status === 403) {
          alert(error.response.data.detail || "Permission denied. Please check your subscription status.");
        } else {
          alert('Failed to create contact');
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
        <div className="bg-white rounded-t-3xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{t('addNewContact')}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">{t('name')} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                placeholder={t('enterContactName')}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">{t('email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('contactEmailPlaceholder')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('phone')}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('phonePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('place')}</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeName')}
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">{t('address')}</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                rows="3"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder={t('enterFullAddress')}
              />

            <div className="mt-3">
              <SmsConsentCheckbox
                checked={formData.sms_consent}
                onChange={(v) => setFormData({ ...formData, sms_consent: v })}
              />
            </div>

            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
              >
                {t('createContact')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Tasks Page
  // Task Card Component
  const TaskCard = React.memo(({ 
    task, 
    onClick, 
    getContactName,
    contacts,
    tasks,
    completed = false,
    // Bulk selection props
    isSelectionMode,
    isSelected,
    onToggleSelection,
    onEnterSelectionMode
  }) => {
    const navigate = useNavigate();
    const [longPressTimer, setLongPressTimer] = useState(null);

    const handleTaskClick = (e) => {
      e.preventDefault();
      
      // If in selection mode, toggle selection instead of navigating
      if (isSelectionMode) {
        onToggleSelection(task.id);
      } else if (onClick) {
        onClick(task);
      } else {
        navigate(`/tasks/${task.id}`);
      }
    };

    const handleCheckboxClick = (e) => {
      e.stopPropagation(); // Prevent task click
      onToggleSelection(task.id);
    };

    // Long press handlers for entering selection mode
    const handleLongPressStart = (e) => {
      if (isSelectionMode) return; // Already in selection mode
      
      const timer = setTimeout(() => {
        // Trigger selection mode and select this task
        if (onEnterSelectionMode) {
          onEnterSelectionMode(task.id);
        }
      }, 500); // 500ms for long press
      
      setLongPressTimer(timer);
    };

    const handleLongPressEnd = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    };

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
        }
      };
    }, [longPressTimer]);

    // Count follow-up tasks
    const followUpCount = tasks.filter(t => t.parent_task_id === task.id).length;

    // Determine task status and background color
    const getTaskStatusStyleLocal = () => {
      return getTaskStatusStyle(task);
    };

    return (
      <div
        onClick={handleTaskClick}
        onMouseDown={handleLongPressStart}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
        onTouchStart={handleLongPressStart}
        onTouchEnd={handleLongPressEnd}
        onTouchCancel={handleLongPressEnd}
        className={`${getTaskStatusStyleLocal()} rounded-2xl p-4 md:px-5 md:py-4 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer hover:border-blue-300 ${
          isSelectionMode && isSelected ? 'ring-2 ring-blue-500 border-blue-500' : ''
        }`}
      >
        {/* Desktop (md+): two-line layout — title on top line (long titles get
            full width), metadata + priority spread across the second line so
            the card uses the available width instead of stacking on the left. */}
        <div className="hidden md:block">
          {/* Line 1: title (left) + priority pill (right) */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {isSelectionMode && (
                <div
                  onClick={handleCheckboxClick}
                  className="mt-1 flex-shrink-0"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-gray-300 hover:border-blue-400'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
              )}
              <h3 className={`font-semibold text-lg leading-snug break-words ${completed ? 'text-gray-700' : 'text-gray-900'}`}>
                {task.title}
                <OfflineIndicator entity={task} className="ml-2 align-middle relative" />
              </h3>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                task.priority === 'High' ? 'bg-red-100 text-red-800' :
                task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {t((task.priority || 'medium').toLowerCase())}
              </span>
              {completed && <span className="text-green-600 text-xl">✓</span>}
            </div>
          </div>

          {/* Line 2: full-width metadata row */}
          <div className={`mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm ${completed ? 'text-gray-500' : 'text-gray-600'}`}>
            {task.due_date && (
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span aria-hidden>📅</span>
                <span>{new Date(task.due_date).toLocaleDateString()}</span>
                {task.time && <span className="text-gray-500">· {task.time}</span>}
              </span>
            )}
            {task.category && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                completed ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-800'
              }`}>
                📂 {task.category}
              </span>
            )}
            {task.location && (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <span aria-hidden>📍</span>
                <span className="truncate max-w-[12rem]">{task.location}</span>
              </span>
            )}
            {task.contact_id && (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <span aria-hidden>👤</span>
                <span className="truncate max-w-[12rem]">{getContactName(task.contact_id)}</span>
              </span>
            )}
            {(task.recurring || task.parent_recurring_task_id) && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                completed ? 'bg-purple-200 text-purple-600' : 'bg-purple-100 text-purple-800'
              }`}>
                🔄 {t('recurring') || 'Recurring'}
              </span>
            )}
            {followUpCount > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                completed ? 'bg-green-200 text-green-600' : 'bg-green-100 text-green-800'
              }`}>
                📋 {followUpCount}
              </span>
            )}
          </div>
        </div>

        {/* Mobile (default) — original stacked layout */}
        <div className="md:hidden">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start space-x-3 flex-1">
            {/* Checkbox for selection mode */}
            {isSelectionMode && (
              <div 
                onClick={handleCheckboxClick}
                className="mt-1 flex-shrink-0"
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                  isSelected 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'border-gray-300 hover:border-blue-400'
                }`}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
            )}
            
            <h3 className={`font-semibold text-lg flex-1 cursor-pointer hover:text-blue-600 transition-colors ${completed ? 'text-gray-700' : 'text-gray-900'}`}>
              {task.title}
              {/* Subtle amber dot for tasks created/edited offline that have
                  not synced yet. Disappears automatically on first fresh fetch. */}
              <OfflineIndicator entity={task} className="ml-2 align-middle relative" />
            </h3>
          </div>
          
          <div className="flex items-center space-x-2 ml-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              task.priority === 'High' ? 'bg-red-100 text-red-800' :
              task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              {t((task.priority || 'medium').toLowerCase())}
            </span>
            {completed && <span className="text-green-600 text-xl">✓</span>}
          </div>
        </div>
        
        {/* Task Metadata */}
        <div className="space-y-2">
          {task.due_date && (
            <div className={`flex items-center text-sm ${completed ? 'text-gray-500' : 'text-gray-600'}`}>
              <span className="mr-2">📅</span>
              <span>{new Date(task.due_date).toLocaleDateString()}</span>
              {task.time && <span className="ml-2">🕒 {task.time}</span>}
            </div>
          )}
          
          <div className="flex flex-wrap items-center gap-2">
            {task.category && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                completed 
                  ? 'bg-gray-200 text-gray-600' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                📂 {task.category}
              </span>
            )}
            
            {task.location && (
              <span className={`text-sm ${completed ? 'text-gray-500' : 'text-gray-600'}`}>
                📍 {task.location}
              </span>
            )}
            
            {task.contact_id && (
              <span className={`text-sm ${completed ? 'text-gray-500' : 'text-gray-600'}`}>
                👤 {getContactName(task.contact_id)}
              </span>
            )}
            
            {(task.recurring || task.parent_recurring_task_id) && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                completed 
                  ? 'bg-purple-200 text-purple-600' 
                  : 'bg-purple-100 text-purple-800'
              }`}>
                🔄 Recurring
              </span>
            )}

            {followUpCount > 0 && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                completed 
                  ? 'bg-green-200 text-green-600' 
                  : 'bg-green-100 text-green-800'
              }`}>
                📋 {followUpCount} follow-up{followUpCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        
        {completed && task.completed_at && (
          <div className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-100">
            Completed on {new Date(task.completed_at).toLocaleDateString()}
          </div>
        )}
        </div>
      </div>
    );
  });

  // Task Details Page Component
  const TaskDetailsPage = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { activeWorkspace } = useWorkspace();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showFileViewer, setShowFileViewer] = useState(false);
    const [fileViewerIndex, setFileViewerIndex] = useState(0);
    const [recurringTasks, setRecurringTasks] = useState([]);

    // Trello-like delete permission. The backend enforces this too; the
    // UI just hides the button to avoid 403 surprises.
    const currentUserId = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}').id || null; }
      catch { return null; }
    })();
    const canDeleteTask = (() => {
      if (!task) return false;
      if (task.user_id === currentUserId) return true;        // creator
      if (!task.org_id) return false;                          // someone else's personal task
      const role = activeWorkspace?.kind === 'org' && activeWorkspace?.id === task.org_id
        ? activeWorkspace.role
        : null;
      return role === 'owner' || role === 'admin';
    })();

    useEffect(() => {
      fetchTask();
    }, [taskId]);

    const fetchTask = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/tasks/${taskId}`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setTask(response.data);
        setError(null);
        
        // Automatically fetch recurring tasks if this is part of a recurring group
        if (response.data.recurring || response.data.parent_recurring_task_id) {
          const recurringGroupId = response.data.parent_recurring_task_id || response.data.id;
          console.log('🔄 Task is part of recurring group:', {
            taskId: response.data.id,
            taskTitle: response.data.title,
            isRecurring: response.data.recurring,
            parentRecurringTaskId: response.data.parent_recurring_task_id,
            recurringGroupId
          });
          fetchRecurringTasks(recurringGroupId);
        } else {
          console.log('❌ Task is not part of a recurring group:', {
            taskId: response.data.id,
            taskTitle: response.data.title,
            isRecurring: response.data.recurring,
            parentRecurringTaskId: response.data.parent_recurring_task_id
          });
        }
        
      } catch (error) {
        console.error('Error fetching task:', error);
        const offline = isLikelyOffline() || isNetworkError(error);
        if (offline) {
          // Offline fallback: try to locate the task in the cached tasks list
          // (the SW would have cached /api/tasks on the last online visit).
          // If we find it, render from there so the user sees a useful page
          // instead of an error.
          try {
            const cached = await axios.get(`${API}/tasks`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            });
            const list = Array.isArray(cached.data) ? cached.data : (cached.data?.tasks || []);
            const match = list.find((x) => x.id === taskId);
            if (match) {
              setTask(match);
              setError(null);
              return;
            }
          } catch { /* fall through to error state */ }
          setError("You're offline and this task isn't in your local cache. Please reconnect to view it.");
        } else {
          setError('Failed to load task details');
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchRecurringTasks = async (recurringGroupId) => {
      try {
        console.log('🔄 Fetching recurring tasks for group ID:', recurringGroupId);
        
        const response = await axios.get(`${API}/tasks`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          params: {
            all: true // Get all tasks to find the recurring group
          }
        });
        
        console.log('📋 All tasks received:', response.data.length);
        console.log('🔍 Looking for tasks with parent_recurring_task_id or id matching:', recurringGroupId);
        
        // Filter tasks that are part of the same recurring group
        const recurringGroupTasks = response.data.filter(task => {
          const isInGroup = task.parent_recurring_task_id === recurringGroupId || 
                           (task.id === recurringGroupId && task.recurring);
          
          if (isInGroup) {
            console.log('✅ Found recurring task:', {
              id: task.id,
              title: task.title,
              recurring: task.recurring,
              parent_recurring_task_id: task.parent_recurring_task_id
            });
          }
          
          return isInGroup;
        });
        
        console.log(`🔄 Found ${recurringGroupTasks.length} tasks in recurring group`);
        
        // Sort with the original recurring task first
        recurringGroupTasks.sort((a, b) => {
          if (a.recurring && !b.recurring) return -1;
          if (!a.recurring && b.recurring) return 1;
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        });
        
        console.log('🎯 Final recurring tasks list:', recurringGroupTasks.map(t => ({
          id: t.id,
          title: t.title,
          recurring: t.recurring
        })));
        
        setRecurringTasks(recurringGroupTasks);
      } catch (error) {
        console.error('❌ Error fetching recurring tasks:', error);
      }
    };

    const handleBack = () => {
      navigate(-1);
    };

    const handleEdit = () => {
      setShowEditModal(true);
    };

    const handleDelete = async () => {
      if (!window.confirm('Are you sure you want to delete this task?')) {
        return;
      }

      try {
        await axios.delete(`${API}/tasks/${taskId}`);
        navigate('/');
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('Failed to delete task');
      }
    };

    const handleFileClick = (fileIndex) => {
      setFileViewerIndex(fileIndex);
      setShowFileViewer(true);
    };

    const getContactName = (contactId) => {
      if (!contactId) return null;
      const contact = contacts.find(c => c.id === contactId);
      return contact ? contact.name : `Contact #${contactId}`;
    };

    // Separate regular files from completion files
    const regularFiles = (task?.attachments || []).filter(file => file.file_type !== 'completion');
    const completionFiles = (task?.attachments || []).filter(file => file.file_type === 'completion');
    const allFiles = [...regularFiles, ...completionFiles];

    if (loading) {
      return (
        <MobileLayout title="Task Details">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </MobileLayout>
      );
    }

    if (error || !task) {
      return (
        <MobileLayout title="Task Details">
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="text-6xl mb-4">😞</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Task Not Found</h3>
            <p className="text-gray-600 mb-4">{error || 'This task does not exist or has been deleted.'}</p>
            <button
              onClick={handleBack}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
            >
              Go Back
            </button>
          </div>
        </MobileLayout>
      );
    }

    const priorityColors = {
      High: 'bg-red-100 text-red-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      Low: 'bg-green-100 text-green-800'
    };

    return (
      <MobileLayout title="Task Details">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Tasks
          </button>
        </div>

        {/* Task Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <div className="flex items-start justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 flex-1">{task.title}</h1>
            {task.completed && (
              <div className="ml-3 flex-shrink-0">
                <span className="text-green-600 text-3xl">✓</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${priorityColors[task.priority] || 'bg-gray-100 text-gray-800'}`}>
              {t((task.priority || 'medium').toLowerCase())} {t('priority')}
            </span>
            
            {task.category && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                📂 {task.category}
              </span>
            )}
            
            {task.completed && (
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Completed
              </span>
            )}

            {(task.recurring || task.parent_recurring_task_id) && (
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                🔄 Recurring
              </span>
            )}
          </div>

          {/* Task Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleEdit}
              className="px-4 py-3 bg-blue-100 text-blue-800 rounded-lg font-medium hover:bg-blue-200 transition-colors"
            >
              Edit
            </button>
            {canDeleteTask && (
              <button
                onClick={handleDelete}
                data-testid="task-detail-delete-btn"
                className="px-4 py-3 bg-red-100 text-red-800 rounded-lg font-medium hover:bg-red-200 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Task Details */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
          
          <div className="space-y-4">
            {task.due_date && (
              <div className="flex items-center">
                <span className="text-gray-600 w-24 flex-shrink-0">Due Date:</span>
                <span className="font-medium">
                  📅 {new Date(task.due_date).toLocaleDateString()}
                  {task.time && ` at 🕒 ${task.time}`}
                </span>
              </div>
            )}
            
            {task.location && (
              <div className="flex items-center">
                <span className="text-gray-600 w-24 flex-shrink-0">Location:</span>
                <span className="font-medium">📍 {task.location}</span>
              </div>
            )}
            
            {task.contact_id && (
              <div className="flex items-center">
                <span className="text-gray-600 w-24 flex-shrink-0">Contact:</span>
                <button
                  onClick={() => navigate(`/contacts/${task.contact_id}`)}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  👤 {getContactName(task.contact_id)}
                </button>
              </div>
            )}
            
            <div className="flex items-start">
              <span className="text-gray-600 w-24 flex-shrink-0">Created:</span>
              <span className="font-medium">
                {new Date(task.created_at || Date.now()).toLocaleDateString()}
              </span>
            </div>
            
            {task.completed_at && (
              <div className="flex items-start">
                <span className="text-gray-600 w-24 flex-shrink-0">Completed:</span>
                <span className="font-medium text-green-600">
                  {new Date(task.completed_at).toLocaleDateString()}
                </span>
              </div>
            )}

            {task.recurring && (
              <div className="flex items-start">
                <span className="text-gray-600 w-24 flex-shrink-0">Pattern:</span>
                <span className="font-medium">
                  🔄 {task.recurrence_pattern} 
                  {task.recurrence_end_date && ` until ${new Date(task.recurrence_end_date).toLocaleDateString()}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {task.notes && (
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('notes')}</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{task.notes}</p>
          </div>
        )}

        {/* Regular Attachments */}
        {regularFiles.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Attachments ({regularFiles.length})
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {regularFiles.map((attachment, index) => (
                <div key={index} 
                     className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                     onClick={() => handleFileClick(index)}>
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">
                      {attachment.content_type?.startsWith('image/') ? '🖼️' :
                       attachment.content_type?.startsWith('audio/') ? '🎵' :
                       attachment.content_type?.includes('pdf') ? '📄' : '📎'}
                    </span>
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm truncate">{attachment.filename}</h3>
                      <p className="text-xs text-gray-500">
                        {attachment.size_mb} MB
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completion Details Section */}
        {task.completed && (task.completion_notes || completionFiles.length > 0) && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 shadow-sm mb-4">
            <h2 className="text-lg font-semibold text-green-900 mb-4">✅ Completion Details</h2>
            
            {task.completion_notes && (
              <div className="mb-4">
                <h3 className="font-medium text-green-800 mb-2">{t('completionNotes')}</h3>
                <p className="text-green-700 leading-relaxed whitespace-pre-wrap">{task.completion_notes}</p>
              </div>
            )}

            {completionFiles.length > 0 && (
              <div>
                <h3 className="font-medium text-green-800 mb-2">Completion Files ({completionFiles.length})</h3>
                <div className="grid grid-cols-2 gap-3">
                  {completionFiles.map((file, index) => (
                    <div key={index} 
                         className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer hover:bg-green-100 transition-colors border border-green-200"
                         onClick={() => handleFileClick(regularFiles.length + index)}>
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">
                          {file.content_type?.startsWith('image/') ? '🖼️' :
                           file.content_type?.startsWith('audio/') ? '🎵' :
                           file.content_type?.includes('pdf') ? '📄' : '📎'}
                        </span>
                        <div>
                          <h3 className="font-medium text-green-900 text-sm truncate">{file.filename}</h3>
                          <p className="text-xs text-green-600">
                            {file.size_mb} MB
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Related Tasks */}
        {tasks.filter(t => t.parent_task_id === task.id).length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Follow-up Tasks ({tasks.filter(t => t.parent_task_id === task.id).length})
            </h2>
            <div className="space-y-3">
              {tasks.filter(t => t.parent_task_id === task.id).map((followUpTask) => (
                <div key={followUpTask.id} 
                     className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                     onClick={() => navigate(`/tasks/${followUpTask.id}`)}>
                  <div className="flex-1">
                    <h3 className={`font-medium ${followUpTask.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {followUpTask.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {followUpTask.priority} Priority
                      {followUpTask.due_date && ` • Due: ${new Date(followUpTask.due_date).toLocaleDateString()}`}
                    </p>
                  </div>
                  {followUpTask.completed && <span className="text-green-600 text-xl">✓</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recurring Tasks */}
        {recurringTasks.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              🔄 Recurring Tasks ({recurringTasks.length})
            </h2>
            <div className="space-y-3">
              {recurringTasks.map((recurringTask) => (
                <div key={recurringTask.id} className="p-3 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer"
                     onClick={() => navigate(`/task/${recurringTask.id}`)}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">
                          {recurringTask.title}
                        </h3>
                        {recurringTask.recurring && (
                          <span className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs font-medium">
                            Original
                          </span>
                        )}
                        {recurringTask.id === task.id && (
                          <span className="px-2 py-0.5 bg-blue-200 text-blue-800 rounded text-xs font-medium">
                            Current
                          </span>
                        )}
                      </div>
                      {recurringTask.due_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          Due: {new Date(recurringTask.due_date).toLocaleDateString()}
                        </p>
                      )}
                      {recurringTask.recurring_frequency && (
                        <p className="text-xs text-purple-600 mt-1">
                          Repeats: {recurringTask.recurring_frequency}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        recurringTask.completed 
                          ? 'bg-green-100 text-green-800' 
                          : new Date(recurringTask.due_date) < new Date()
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {recurringTask.completed ? 'Completed' : 
                         new Date(recurringTask.due_date) < new Date() ? 'Overdue' : 'Pending'}
                      </span>
                      {recurringTask.completed && <span className="text-green-600 text-xl">✓</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modals */}
        <EditTaskModal 
          isOpen={showEditModal} 
          onClose={() => setShowEditModal(false)} 
          task={task}
        />

        <FileViewerModal
          isOpen={showFileViewer}
          onClose={() => setShowFileViewer(false)}
          files={allFiles}
          initialIndex={fileViewerIndex}
        />
      </MobileLayout>
    );
  };

  // Contact Details Page Component  
  const ContactDetailsPage = () => {
    const { contactId } = useParams();
    const navigate = useNavigate();
    const [contact, setContact] = useState(null);
    const [contactTasks, setContactTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      fetchContact();
      fetchContactTasks();
    }, [contactId]);

    const fetchContact = async () => {
      try {
        const response = await axios.get(`${API}/contacts/${contactId}`);
        setContact(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching contact:', error);
        const offline = isLikelyOffline() || isNetworkError(error);
        if (offline) {
          // Offline fallback: pull the contact from the cached list.
          try {
            const cached = await axios.get(`${API}/contacts`);
            const list = Array.isArray(cached.data) ? cached.data : (cached.data?.contacts || []);
            const match = list.find((x) => x.id === contactId);
            if (match) {
              setContact(match);
              setError(null);
              return;
            }
          } catch { /* fall through */ }
          setError("You're offline and this contact isn't in your local cache. Please reconnect to view it.");
        } else {
          setError('Failed to load contact details');
        }
      }
    };

    const fetchContactTasks = async () => {
      try {
        const response = await axios.get(`${API}/tasks?contact_id=${contactId}`);
        setContactTasks(response.data || []);
      } catch (error) {
        console.error('Error fetching contact tasks:', error);
        setContactTasks([]);
      } finally {
        setLoading(false);
      }
    };

    const handleBack = () => {
      navigate(-1);
    };

    const handleEdit = () => {
      alert('Edit contact functionality coming soon!');
    };

    const handleDelete = async () => {
      if (!window.confirm('Are you sure you want to delete this contact?')) {
        return;
      }

      try {
        await axios.delete(`${API}/contacts/${contactId}`);
        navigate('/contacts');
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert('Failed to delete contact');
      }
    };

    if (loading) {
      return (
        <MobileLayout title="Contact Details">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </MobileLayout>
      );
    }

    if (error || !contact) {
      return (
        <MobileLayout title="Contact Details">
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="text-6xl mb-4">😞</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Not Found</h3>
            <p className="text-gray-600 mb-4">{error || 'This contact does not exist or has been deleted.'}</p>
            <button
              onClick={handleBack}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
            >
              Go Back
            </button>
          </div>
        </MobileLayout>
      );
    }

    return (
      <MobileLayout title="Contact Details">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Contacts
          </button>
        </div>

        {/* Contact Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <div className="flex items-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-4">
              {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{contact.name}</h1>
              {contact.company && (
                <p className="text-blue-600 font-medium">{contact.company}</p>
              )}
            </div>
          </div>

          {/* Contact Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleEdit}
              className="flex-1 bg-blue-100 text-blue-800 py-3 rounded-lg font-medium hover:bg-blue-200 transition-colors"
            >
              Edit Contact
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-3 bg-red-100 text-red-800 rounded-lg font-medium hover:bg-red-200 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
          
          <div className="space-y-4">
            {contact.email && (
              <div className="flex items-center">
                <span className="text-gray-600 w-20 flex-shrink-0">Email:</span>
                <a href={`mailto:${contact.email}`} className="font-medium text-blue-600 hover:text-blue-800">
                  📧 {contact.email}
                </a>
              </div>
            )}
            
            {contact.phone && (
              <div className="flex items-center">
                <span className="text-gray-600 w-20 flex-shrink-0">Phone:</span>
                <a href={`tel:${contact.phone}`} className="font-medium text-blue-600 hover:text-blue-800">
                  📞 {contact.phone}
                </a>
              </div>
            )}
            
            {contact.company && (
              <div className="flex items-center">
                <span className="text-gray-600 w-20 flex-shrink-0">Company:</span>
                <span className="font-medium">🏢 {contact.company}</span>
              </div>
            )}

            <div className="flex items-start">
              <span className="text-gray-600 w-20 flex-shrink-0">Added:</span>
              <span className="font-medium">
                {new Date(contact.created_at || Date.now()).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Related Tasks */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Related Tasks ({contactTasks.length})
          </h2>
          
          {contactTasks.length > 0 ? (
            <div className="space-y-3">
              {contactTasks.map((task) => (
                <div key={task.id} 
                     className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                     onClick={() => navigate(`/tasks/${task.id}`)}>
                  <div className="flex-1">
                    <h3 className={`font-medium ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                      {task.title}
                      <OfflineIndicator entity={task} className="ml-2 align-middle relative" />
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        task.priority === 'High' ? 'bg-red-100 text-red-800' :
                        task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {t((task.priority || 'medium').toLowerCase())}
                      </span>
                      {task.due_date && (
                        <span className="text-xs text-gray-500">
                          📅 Due: {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  {task.completed && <span className="text-green-600 text-xl">✓</span>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">📋</div>
              <p className="text-gray-500">{t('noTasksAssigned')}</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('quickActions')}</h2>
          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={() => navigate('/tasks/new', { state: { contactId: contact.id, contactName: contact.name } })}
              className="flex items-center justify-center p-3 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <span className="mr-2">📋</span>
              {t('createTask')}
            </button>
            {contact.email && (
              <a href={`mailto:${contact.email}`} 
                 className="flex items-center justify-center p-3 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors">
                <span className="mr-2">📧</span>
                {t('sendEmail')}
              </a>
            )}
            {contact.phone && (
              <a href={`tel:${contact.phone}`} 
                 className="flex items-center justify-center p-3 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 transition-colors">
                <span className="mr-2">📞</span>
                {t('call')}
              </a>
            )}
            <button 
              onClick={() => navigate('/contacts')}
              className="flex items-center justify-center p-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <span className="mr-2">👥</span>
              {t('allContacts')}
            </button>
          </div>
        </div>
      </MobileLayout>
    );
  };

  const TasksPage = ({ 
    tasks, 
    contacts, 
    categories, 
    loading, 
    loadingMore, 
    hasMoreTasks, 
    loadMoreTasks, 
    getContactName, 
    handleOpenAddTask, 
    showAddTask, 
    handleCloseAddTask,
    TASKS_PER_PAGE,
    // Bulk selection props
    selectedTasks,
    isSelectionMode,
    isBulkDeleting,
    toggleSelectionMode,
    toggleTaskSelection,
    selectAllTasks,
    clearSelection,
    bulkDeleteTasks,
    enterSelectionMode,
    // New props for load all tasks functionality
    allTasksLoaded,
    loadAllTasks
  }) => {
    const { t } = useLanguage();
    const location = useLocation();
    const { activeWorkspace } = useWorkspace();
    const [showFilters, setShowFilters] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // Long-press shortcut on the primary "+" FAB. Short-press immediately
    // opens the manual Add Task form — that's the action 95 % of users want.
    // Long-press (≥450 ms) on Pro/trial accounts opens the voice modal
    // directly, matching the iOS Notes "tap-and-hold to dictate" pattern.
    // Free users only have manual create, so the long-press is a no-op for
    // them and short-press just behaves like a normal FAB.
    const longPressTimerRef = useRef(null);
    const longPressFiredRef = useRef(false);
    const handleFabPointerDown = () => {
      if (!hasProAccess(user)) return; // free users: short-press only
      longPressFiredRef.current = false;
      longPressTimerRef.current = setTimeout(() => {
        longPressFiredRef.current = true;
        // Haptic nudge on devices that support it — tiny pulse confirms the
        // shortcut fired without needing visual feedback.
        try { navigator.vibrate && navigator.vibrate(20); } catch { /* noop */ }
        handleOpenVoiceTaskCreator();
      }, 450);
    };
    const cancelLongPress = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
    const handleFabClick = () => {
      // If a long-press already triggered voice, suppress the click so we
      // don't also open the manual modal on top.
      if (longPressFiredRef.current) {
        longPressFiredRef.current = false;
        return;
      }
      handleOpenAddTask();
    };
    const [filters, setFilters] = useState({
      category: '',
      priority: '',
      completed: '',
      parent_task_id: '',
      recurring_group: '',
      dateFrom: '',
      dateTo: ''
    });

    // Handle URL parameters
    useEffect(() => {
      const urlParams = new URLSearchParams(location.search);
      const parentTaskId = urlParams.get('parent_task_id');
      const recurringId = urlParams.get('recurring');
      
      if (parentTaskId) {
        setFilters(prev => ({ ...prev, parent_task_id: parentTaskId }));
        setShowFilters(true); // Show filters when filtering by parent task
      }
      
      if (recurringId) {
        setFilters(prev => ({ ...prev, recurring_group: recurringId }));
        setShowFilters(true); // Show filters when filtering by recurring group
      }
    }, [location.search]);

    // Filter tasks based on current filters and search query
    const filteredTasks = useMemo(() => {
      let filtered = [...(tasks || [])]; // Add safety check
      
      // Search by title
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        filtered = filtered.filter(task => 
          task.title?.toLowerCase().includes(searchLower)
        );
      }
      
      if (filters.category) {
        filtered = filtered.filter(task => task.category === filters.category);
      }
      if (filters.priority) {
        filtered = filtered.filter(task => task.priority === filters.priority);
      }
      if (filters.completed !== '') {
        const isCompleted = filters.completed === 'true';
        filtered = filtered.filter(task => !!task.completed === isCompleted);
      }
      if (filters.parent_task_id) {
        filtered = filtered.filter(task => task.parent_task_id === filters.parent_task_id);
      }
      if (filters.recurring_group) {
        // Filter tasks that are part of the same recurring group
        // This includes the original recurring task and all its instances
        filtered = filtered.filter(task => {
          // Match the original recurring task
          if (task.id === filters.recurring_group) return true;
          // Match tasks that belong to this recurring series
          if (task.parent_recurring_task_id === filters.recurring_group) return true;
          // Handle case where we're filtering by a recurring instance
          if (task.parent_recurring_task_id && task.parent_recurring_task_id === task.id) return true;
          return false;
        });
      }
      if (filters.dateFrom) {
        filtered = filtered.filter(task => 
          task.due_date && new Date(task.due_date) >= new Date(filters.dateFrom)
        );
      }
      if (filters.dateTo) {
        filtered = filtered.filter(task => 
          task.due_date && new Date(task.due_date) <= new Date(filters.dateTo)
        );
      }
      
      return filtered;
    }, [tasks, filters, searchQuery]);

    // Split tasks into pending and completed
    const pendingTasks = useMemo(() => {
      return filteredTasks
        .filter(task => !task.completed)
        .sort((a, b) => {
          // Simple sorting: newest created first
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
    }, [filteredTasks]);

    const completedTasks = useMemo(() => {
      return filteredTasks
        .filter(task => task.completed)
        .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
        // Removed .slice(0, 10) limit to show ALL completed tasks like calendar does
    }, [filteredTasks]);

    // Desktop keyboard: ↑/↓ highlight a task, Enter opens it on the right, Esc closes the pane
    useEffect(() => {
      if (!isDesktop) return undefined;
      const onKeyDown = (e) => {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        const el = e.target;
        if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
        if (document.querySelector('.fixed.inset-0')) return;
        const ids = Array.from(document.querySelectorAll('[data-testid^="task-row-"]')).map((n) => n.dataset.testid.slice(9));
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (!ids.length) return;
          e.preventDefault();
          const current = keyboardTaskId ?? (desktopSelection?.type === 'task' ? desktopSelection.id : null);
          const idx = ids.indexOf(current);
          const next = idx === -1 ? 0 : Math.min(Math.max(idx + (e.key === 'ArrowDown' ? 1 : -1), 0), ids.length - 1);
          setKeyboardTaskId(ids[next]);
          document.querySelector(`[data-testid="task-row-${ids[next]}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && keyboardTaskId && ids.includes(keyboardTaskId)) {
          e.preventDefault();
          setDesktopSelection({ type: 'task', id: keyboardTaskId });
        } else if (e.key === 'Escape' && desktopSelection) {
          setDesktopSelection(null);
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [isDesktop, keyboardTaskId, desktopSelection]);

    // Handle task click
    const handleTaskClick = useCallback((task) => {
      if (isDesktop) {
        setDesktopSelection({ type: 'task', id: task.id });
        setKeyboardTaskId(task.id);
        return;
      }
      navigate(`/tasks/${task.id}`);
    }, [navigate, isDesktop]);

    const rowRing = (id) => {
      if (!isDesktop) return '';
      if (desktopSelection?.type === 'task' && desktopSelection.id === id) return 'rounded-2xl ring-2 ring-indigo-500 ring-offset-2';
      if (keyboardTaskId === id) return 'rounded-2xl ring-2 ring-indigo-300 ring-offset-2';
      return '';
    };

    // Use dynamic categories from data context
    // Categories are already available from the parent Components context

    return (
      <MobileLayout 
        title={t('tasks')} 
        onRefresh={refreshAllData}
        subtitle={
          <div className="flex items-center space-x-3">
            <span>{`${filteredTasks.length} ${t('tasks').toLowerCase()} ${searchQuery ? '(' + t('filtered') + ')' : ''}`}</span>
            {loading && (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs text-blue-600 font-medium">{t('loadingTasks')}</span>
              </div>
            )}
          </div>
        }
      >
        <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        <div className="lg:col-span-5" data-testid="tasks-list-column">
        {isDesktop && (
          <button
            onClick={handleOpenAddTask}
            data-testid="desktop-add-task-btn"
            className="w-full mb-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-3 rounded-xl font-medium shadow-sm hover:shadow-md hover:from-blue-600 hover:to-indigo-700 transition-colors"
          >
            <PhPlus size={18} weight="bold" />
            {t('addTask')}
          </button>
        )}
        {/* Team activity widget — business-mode only, admins/owners only.
            The card hides itself when the API returns 403 (regular members). */}
        {activeWorkspace?.kind === 'org' && (activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin') && (
          <TeamActivityCard org={activeWorkspace.raw || { id: activeWorkspace.id, name: activeWorkspace.name }} />
        )}

        {/* Daily Briefing - AI-powered task summary (moves to the right pane on desktop) */}
        {!isDesktop && <DailyBriefing tasks={tasks} />}

        {/* Load All Tasks Button - Show when task count is 20 */}
        {tasks.length <= 20 && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-800 font-medium">{t('loadAllTasksHint')}</p>
              </div>
              <button
                onClick={loadAllTasks}
                disabled={loading}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                  loading 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading && (
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{loading ? `${t('loading')}...` : t('loadAllTasks')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Search Bar - Only show when more than 20 tasks are loaded */}
        {tasks.length > 20 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bulk Selection Controls - Show when in selection mode */}
        {isSelectionMode && (
          <div className="mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-blue-800 font-medium">
                    {selectedTasks.length} task(s) selected
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={clearSelection}
                    className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                  >
                    Clear Selection
                  </button>
                  {selectedTasks.length > 0 && (
                    <button
                      onClick={bulkDeleteTasks}
                      disabled={isBulkDeleting}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1 ${
                        isBulkDeleting
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {isBulkDeleting && (
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                      )}
                      <span>{isBulkDeleting ? 'Deleting...' : `Delete ${selectedTasks.length}`}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Active Filter Indicator for Parent Task */}
        {filters.parent_task_id && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-blue-900">Viewing Follow-up Tasks</h3>
                <p className="text-sm text-blue-700">
                  Showing tasks that follow up on: {tasks.find(t => t.id === filters.parent_task_id)?.title || 'Parent Task'}
                </p>
              </div>
              <button
                onClick={() => setFilters({...filters, parent_task_id: ''})}
                className="text-blue-600 hover:text-blue-800 font-medium text-sm px-3 py-1 rounded-lg bg-white hover:bg-blue-50 transition-colors"
              >
                {t('cancel')} {t('filter')}
              </button>
            </div>
          </div>
        )}

        {/* Filter Controls - Only show when more than 20 tasks are loaded */}
        {tasks.length > 20 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{t('filter')}</h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
              >
                {showFilters ? t('hide') : t('show')} {t('filters')}
                <svg 
                  className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

          {showFilters && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">{t('allCategories')}</option>
                  {categories.map(category => (
                    <option key={category.id || category.name} value={category.name}>{category.name}</option>
                  ))}
                </select>
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters({...filters, priority: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">All Priorities</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              
              <select
                value={filters.completed}
                onChange={(e) => setFilters({...filters, completed: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Tasks</option>
                <option value="false">Active Only</option>
                <option value="true">Completed Only</option>
              </select>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">From Date</label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">To Date</label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <button
                onClick={() => setFilters({ category: '', priority: '', completed: '', parent_task_id: '', recurring_group: '', dateFrom: '', dateTo: '' })}
                className="w-full py-2 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-300 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          )}
          </div>
        )}

        {/* Select Tasks Button - Show when there are any tasks */}
        {(pendingTasks.length > 0 || completedTasks.length > 0) && !isSelectionMode && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={toggleSelectionMode}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center space-x-1 px-4 py-2 bg-white rounded-lg shadow-sm border border-blue-200 hover:bg-blue-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{t('selectTasks')}</span>
            </button>
          </div>
        )}

        {/* Active Tasks */}
        {pendingTasks.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('activeTasks')}</h2>
            </div>
            <div className="space-y-3">
              {pendingTasks.map(task => (
                <div key={task.id} className={rowRing(task.id)} data-testid={`task-row-${task.id}`}>
                <TaskCard
                  key={task.id}
                  task={task}
                  onClick={handleTaskClick}
                  getContactName={getContactName}
                  contacts={contacts}
                  tasks={tasks}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedTasks.includes(task.id)}
                  onToggleSelection={toggleTaskSelection}
                  onEnterSelectionMode={enterSelectionMode}
                />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('completedTasks')}</h2>
            <div className="space-y-3">
              {completedTasks.map(task => (
                <div key={task.id} className={rowRing(task.id)} data-testid={`task-row-${task.id}`}>
                <TaskCard
                  key={task.id}
                  task={task}
                  onClick={handleTaskClick}
                  getContactName={getContactName}
                  contacts={contacts}
                  tasks={tasks}
                  completed
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedTasks.includes(task.id)}
                  onToggleSelection={toggleTaskSelection}
                  onEnterSelectionMode={enterSelectionMode}
                />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Load More Button */}
        {hasMoreTasks && (pendingTasks.length > 0 || completedTasks.length > 0) && (
          <div className="text-center mb-6">
            <button
              onClick={loadMoreTasks}
              disabled={loadingMore}
              className="bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loadingMore ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span>Loading more tasks...</span>
                </div>
              ) : (
                `Load More Tasks (${TASKS_PER_PAGE} more)`
              )}
            </button>
          </div>
        )}

        {/* Empty State */}
        {pendingTasks.length === 0 && completedTasks.length === 0 && (
          <div className="bg-white rounded-2xl p-10 border border-gray-200 text-center" data-testid="tasks-empty-state">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 mx-auto mb-4 flex items-center justify-center">
              <PhListChecks size={32} weight="duotone" className="text-blue-600" />
            </div>
            <h3 className="font-display text-xl font-bold text-gray-900 mb-1 tracking-tight">
              {t('noTasksFound')}
            </h3>
            <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">
              {Object.values(filters).some(f => f !== '')
                ? 'Try adjusting your filters — or clear them to see everything.'
                : "A blank canvas. What's on your mind today?"}
            </p>
            <button
              onClick={handleOpenAddTask}
              data-testid="tasks-empty-add-cta"
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2.5 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all"
            >
              <PhPlus size={16} weight="bold" />
              {t('addTask')}
            </button>
          </div>
        )}
        </div>

        {isDesktop && (
          <aside
            className="lg:col-span-7 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:z-50 lg:px-1 rounded-2xl"
            data-testid="tasks-detail-pane"
          >
            <TaskSplitPane
              selection={desktopSelection}
              setSelection={setDesktopSelection}
              onChanged={refreshAllData}
              glance={
                <TodayGlance
                  tasks={tasks}
                  onSelectTask={(id) => setDesktopSelection({ type: 'task', id })}
                  briefing={<DailyBriefing tasks={tasks} collapsible />}
                />
              }
            />
          </aside>
        )}
        </div>

        {/* Single + FAB. Short-press = open the manual Add Task modal (the
            action 95 % of users want). Pro/trial users can also long-press
            (≥450 ms) to dictate a voice task instead — same iOS-Notes
            tap-and-hold pattern, no menu in the way. Free users only see the
            short-press behavior. */}
        <button
          onClick={handleFabClick}
          onPointerDown={handleFabPointerDown}
          onPointerUp={cancelLongPress}
          onPointerLeave={cancelLongPress}
          onPointerCancel={cancelLongPress}
          onContextMenu={(e) => e.preventDefault()}
          data-testid="add-task-fab"
          aria-label={hasProAccess(user) ? 'Add task — tap to type, long-press to dictate' : 'Add task'}
          className="lg:hidden fixed bottom-20 right-4 w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 z-40 flex items-center justify-center select-none"
          title={hasProAccess(user) ? 'Tap to add, hold to dictate' : 'Add new task'}
          style={{ touchAction: 'manipulation', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {/* Voice Task Creator Modal */}
        {showVoiceTaskCreator && (
          <SimpleVoiceTaskCreator
            onTaskCreated={handleVoiceTaskCreated}
            onClose={handleCloseVoiceTaskCreator}
          />
        )}

        {/* Add Task Modal */}
        <AddTaskModal 
          isOpen={showAddTask} 
          onClose={handleCloseAddTask} 
          initialDate={addTaskPrefillDate}
        />
      </MobileLayout>
    );
  };

  // Simple Calendar Page
  const CalendarPage = () => {
    // Month/week state lives in Components so it survives re-renders
    const currentDate = calendarCurrentDate;
    const setCurrentDate = setCalendarCurrentDate;
    const viewMode = calendarViewMode;
    const setViewMode = setCalendarViewMode;
    // Selected day lives in Components so it survives re-renders; desktop defaults to today
    const selectedDate = calendarSelectedDate || (isDesktop ? new Date() : null);
    const setSelectedDate = setCalendarSelectedDate;
    const navigate = useNavigate();

    // Memoized calendar calculations
    const calendarData = useMemo(() => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      // Get first day of month and last day
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDate = new Date(firstDay);
      const endDate = new Date(lastDay);
      
      // Adjust to show full weeks
      startDate.setDate(startDate.getDate() - startDate.getDay());
      endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
      
      const days = [];
      const current = new Date(startDate);
      
      while (current <= endDate) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      return { days, firstDay, lastDay };
    }, [currentDate]);

    // Memoized tasks by date mapping using global tasks
    const tasksByDate = useMemo(() => {
      const tasksMap = {};
      
      tasks.forEach(task => {
        if (task.due_date) {
          const dateKey = new Date(task.due_date).toDateString();
          if (!tasksMap[dateKey]) {
            tasksMap[dateKey] = [];
          }
          tasksMap[dateKey].push(task);
        }
      });
      
      return tasksMap;
    }, [tasks]);

    // Get tasks for a specific date
    const getTasksForDate = useCallback((date) => {
      const dateKey = date.toDateString();
      return tasksByDate[dateKey] || [];
    }, [tasksByDate]);

    const dayTasks = selectedDate ? [...getTasksForDate(selectedDate)].sort((a, b) => (a.time || '99').localeCompare(b.time || '99')) : [];

    // Desktop drag-to-reschedule: drag from the day list, drop on a day (asks first)
    const [dragOverKey, setDragOverKey] = useState(null);
    const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const handleDropOnDate = async (date, taskId, newTime) => {
      const task = tasks.find((x) => x.id === taskId);
      if (!task) return;
      const ymd = toYMD(date);
      const sameDay = String(task.due_date || '').slice(0, 10) === ymd;
      const timeChanges = Boolean(newTime) && newTime !== task.time;
      if (sameDay && !timeChanges) return;
      const dayLabel = date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      const label = timeChanges ? `${dayLabel} at ${newTime}` : dayLabel;
      const ok = await confirmAction({
        title: `Move "${task.title}" to ${label}?`,
        message: !timeChanges && task.time ? `It keeps its time (${task.time}).` : undefined,
        confirmLabel: 'Move',
      });
      if (!ok) return;
      try {
        await axios.put(`${API}/tasks/${task.id}`, {
          due_date: `${ymd}T00:00:00.000Z`,
          ...(timeChanges ? { time: newTime } : {}),
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        toast.success(`Moved to ${label}`);
        setSelectedDate(date);
        await refreshAllData();
      } catch (error) {
        console.error('Reschedule failed:', error);
        toast.error(isNetworkError(error) ? "You're offline — try again when connected." : 'Could not move the task. Please try again.');
      }
    };
    const dropHandlers = (date) => (isDesktop ? {
      onDragOver: (e) => {
        if (!e.dataTransfer.types.includes('text/task-id')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverKey !== date.toDateString()) setDragOverKey(date.toDateString());
      },
      onDragLeave: () => setDragOverKey((k) => (k === date.toDateString() ? null : k)),
      onDrop: (e) => {
        e.preventDefault();
        setDragOverKey(null);
        const id = e.dataTransfer.getData('text/task-id');
        if (id) handleDropOnDate(date, id);
      },
    } : {});
    const weekStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - currentDate.getDay());

    // Navigation functions
    const navigateMonth = useCallback((direction) => {
      // Anchor to day 1 so e.g. Oct 31 -> "Sep 31" doesn't overflow back into October
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + direction, 1));
    }, []);

    const navigateWeek = useCallback((direction) => {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + (direction * 7));
        return newDate;
      });
    }, []);

    const handleDateClick = useCallback((date) => {
      setSelectedDate(date);
      setCalendarTaskSelection(null);
    }, []);

    const handleTaskClick = useCallback((task) => {
      if (isDesktop) {
        setCalendarTaskSelection({ type: 'task', id: task.id, fromRoot: true });
        return;
      }
      navigate(`/tasks/${task.id}`);
    }, [navigate]);

    const goToToday = useCallback(() => {
      setCurrentDate(new Date());
      setSelectedDate(null);
      setCalendarTaskSelection(null);
    }, []);

    // Task status indicator component
    const TaskIndicator = ({ task }) => {
      const isOverdue = isTaskOverdue(task);
      const isCompleted = task.completed;
      
      let colorClass = '';
      if (isCompleted) {
        colorClass = 'bg-green-500';
      } else if (isOverdue) {
        colorClass = 'bg-red-500';
      } else {
        colorClass = 'bg-blue-500';
      }
      
      return (
        <div
          className={`w-2 h-2 rounded-full ${colorClass} mr-1 mb-1`}
          title={`${task.title} - ${task.priority} priority`}
        />
      );
    };

    const getContactName = (contactId) => {
      if (!contactId) return null;
      const contact = contacts.find(c => c.id === contactId);
      return contact ? contact.name : `Contact #${contactId}`;
    };

    const monthNames = [
      t('january'), t('february'), t('march'),
      t('april'), t('may'), t('june'),
      t('july'), t('august'), t('september'),
      t('october'), t('november'), t('december')
    ];

    const dayPanel = selectedDate && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4" data-testid="calendar-day-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">
                📅 {selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                {dayTasks.length} {dayTasks.length === 1 ? 'task' : 'tasks'}
              </span>
            </div>
            
            {isDesktop && dayTasks.length > 0 && (
              <p className="-mt-2 mb-3 text-xs text-gray-400" data-testid="calendar-drag-hint">Tip: drag a task onto another day to reschedule it — in Week view, drop it on an hour to change its time too.</p>
            )}
            {dayTasks.length > 0 ? (
              <div className="space-y-3">
                {dayTasks.map(task => (
                  <div
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    draggable={isDesktop}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/task-id', task.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    data-testid={`calendar-day-task-${task.id}`}
                    className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200 ${isDesktop ? 'active:cursor-grabbing' : ''}`}
                  >
                    <div className="flex-1">
                      <h4 className={`font-medium ${task.completed ? 'text-gray-700' : 'text-gray-900'}`}>
                        {task.title}
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          task.priority === 'High' ? 'bg-red-100 text-red-800' :
                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {t((task.priority || 'medium').toLowerCase())}
                        </span>
                        {task.time && (
                          <span className="text-xs text-gray-500">
                            🕒 {task.time}
                          </span>
                        )}
                        {task.contact_id && (
                          <span className="text-xs text-blue-600">
                            👤 {getContactName(task.contact_id)}
                          </span>
                        )}
                        {task.category && (
                          <span className="text-xs text-gray-500">
                            📂 {task.category}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-3 flex items-center space-x-2">
                      {/* Task status circle indicators */}
                      {task.completed ? (
                        <div className="w-6 h-6 rounded-full bg-green-500" title="Completed"></div>
                      ) : isTaskOverdue(task) ? (
                        <div className="w-6 h-6 rounded-full bg-red-500" title="Overdue"></div>
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-blue-500" title="Pending"></div>
                      )}
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">📋</div>
                <p className="text-gray-500">No tasks scheduled for this date</p>
                <button
                  onClick={() => openAddTaskForDate(selectedDate)}
                  data-testid="calendar-add-task-for-date-btn"
                  className="mt-3 text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  + Add a task for this date
                </button>
              </div>
            )}
          </div>
    );

    const weekDays = [t('weekDaySun'), t('weekDayMon'), t('weekDayTue'), t('weekDayWed'), t('weekDayThu'), t('weekDayFri'), t('weekDaySat')];

    return (
      <MobileLayout onRefresh={refreshAllData} title={t('calendar')} subtitle={`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}>
        <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        <div className="lg:col-span-7" data-testid="calendar-main-column">
        {/* Calendar Header */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          {/* Navigation Controls */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
              data-testid="calendar-prev-btn"
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-semibold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              {loading && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs text-blue-600 font-medium">Loading all tasks...</span>
                </div>
              )}
            </div>
            
            <button
              onClick={() => viewMode === 'month' ? navigateMonth(1) : navigateWeek(1)}
              data-testid="calendar-next-btn"
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* View Mode Toggle and Today Button */}
          <div className="flex items-center justify-between">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'month' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('month')}
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'week' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('week')}
              </button>
            </div>
            
            <button
              onClick={goToToday}
              data-testid="calendar-today-btn"
              className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium hover:bg-blue-200 transition-colors text-sm"
            >
              {t('today')}
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        {viewMode === 'week' ? (
          <WeekTimeGrid
            weekStart={weekStart}
            weekDays={weekDays}
            getTasksForDate={getTasksForDate}
            onTaskClick={handleTaskClick}
            onDayClick={handleDateClick}
            selectedDate={selectedDate}
            onDropTask={isDesktop ? handleDropOnDate : undefined}
            allDayLabel={t('allDay') === 'allDay' ? 'All day' : t('allDay')}
          />
        ) : (
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          {/* Week Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarData.days.map(date => {
              const dayTasksList = getTasksForDate(date);
              const isCurrentMonth = date.getMonth() === currentDate.getMonth();
              const isToday = date.toDateString() === new Date().toDateString();
              const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
              
              return (
                <div
                  key={date.toISOString()}
                  onClick={() => handleDateClick(date)}
                  {...dropHandlers(date)}
                  data-testid={`calendar-day-${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`}
                  className={`
                    relative p-2 min-h-14 border border-gray-100 rounded-lg cursor-pointer transition-all
                    ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    ${isToday ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200' : 'hover:bg-gray-50'}
                    ${isSelected ? 'bg-blue-100 border-blue-300 ring-1 ring-blue-300' : ''}
                    ${dragOverKey === date.toDateString() ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''}
                  `}
                >
                  <div className="text-sm font-medium mb-1">
                    {date.getDate()}
                  </div>
                  
                  {/* Task Status Indicators */}
                  {dayTasksList.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {/* Show one circle per status type */}
                      {(() => {
                        const hasOverdue = dayTasksList.some(task => isTaskOverdue(task));
                        const hasPending = dayTasksList.some(task => !task.completed && !isTaskOverdue(task));
                        const hasCompleted = dayTasksList.some(task => task.completed);
                        
                        return (
                          <>
                            {hasOverdue && (
                              <div
                                className="w-3 h-3 rounded-full bg-red-500"
                                title="Has overdue tasks"
                              />
                            )}
                            {hasPending && (
                              <div
                                className="w-3 h-3 rounded-full bg-blue-500"
                                title="Has pending tasks"
                              />
                            )}
                            {hasCompleted && (
                              <div
                                className="w-3 h-3 rounded-full bg-green-500"
                                title="Has completed tasks"
                              />
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Legend */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <h3 className="font-medium text-gray-900 mb-3">{t('taskStatusLegend')}</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
              <span className="text-gray-600">{t('hasPendingTasks')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
              <span className="text-gray-600">{t('hasOverdueTasks')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span className="text-gray-600">{t('hasCompletedTasks')}</span>
            </div>
          </div>
        </div>

        {/* Selected Date Tasks (moves to the right pane on desktop) */}
        {!isDesktop && dayPanel}

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-blue-600">{tasks.filter(t => !t.completed).length}</div>
            <div className="text-sm text-gray-600">{t('pending')}</div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-red-600">
              {tasks.filter(t => isTaskOverdue(t)).length}
            </div>
            <div className="text-sm text-gray-600">{t('overdue')}</div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-green-600">{tasks.filter(t => t.completed).length}</div>
            <div className="text-sm text-gray-600">{t('done')}</div>
          </div>
        </div>
        </div>

        {isDesktop && (
          <aside
            className="lg:col-span-5 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:z-50 lg:px-1 rounded-2xl"
            data-testid="calendar-detail-pane"
          >
            <TaskSplitPane
              selection={calendarTaskSelection}
              setSelection={setCalendarTaskSelection}
              onChanged={refreshAllData}
              rootBackLabel={t('backToDay') === 'backToDay' ? 'Back to day' : t('backToDay')}
              glance={dayPanel}
            />
          </aside>
        )}
        </div>

        <AddTaskModal
          isOpen={showAddTask}
          onClose={handleCloseAddTask}
          initialDate={addTaskPrefillDate}
        />
      </MobileLayout>
    );
  };

  // Contacts Page  
  const ContactsPage = () => {
    const { t } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const importInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [deletingSelection, setDeletingSelection] = useState(false);

    const exitSelectMode = () => {
      setSelectMode(false);
      setSelectedIds([]);
    };

    // Hardware/OS back integration: pressing back in contact select mode exits
    // selection instead of leaving the page.
    useHistoryBack(selectMode, exitSelectMode, 'contacts-select-mode');

    const handleContactClick = (contactId) => {
      if (selectMode) {
        setSelectedIds((prev) =>
          prev.includes(contactId)
            ? prev.filter((id) => id !== contactId)
            : [...prev, contactId]
        );
        return;
      }
      if (isDesktop) {
        setContactSelection({ type: 'contact', id: contactId });
        setKeyboardContactId(contactId);
        return;
      }
      navigate(`/contacts/${contactId}`);
    };

    const handleImportClick = () => {
      if (importInputRef.current) importInputRef.current.click();
    };

    const handleImportFile = async (e) => {
      const file = e.target.files?.[0];
      // Reset input so re-selecting the same file works
      if (importInputRef.current) importInputRef.current.value = '';
      if (!file) return;

      setImporting(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/contacts/import`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const { imported_count = 0, skipped_duplicates = 0 } = response.data || {};
        invalidateContactsCache();
        await fetchContacts();
        alert(
          `Imported ${imported_count} contact(s)` +
          (skipped_duplicates ? ` (${skipped_duplicates} duplicate(s) skipped)` : '')
        );
      } catch (error) {
        console.error('Contact import failed:', error);
        const detail = error.response?.data?.detail;
        if (error.response?.status === 403) {
          alert(detail || 'Free plan limit reached. Upgrade to Pro to import more contacts.');
        } else if (error.response?.status === 400) {
          alert(detail || 'No valid contacts found in this file.');
        } else {
          alert('Failed to import contacts. Please make sure the file is a valid .vcf or .csv export.');
        }
      } finally {
        setImporting(false);
      }
    };

    // Filter contacts based on search query
    const filteredContacts = contacts.filter(contact => {
      const searchLower = searchQuery.toLowerCase();
      return (
        contact.name?.toLowerCase().includes(searchLower) ||
        contact.company?.toLowerCase().includes(searchLower) ||
        contact.address?.toLowerCase().includes(searchLower)
      );
    });

    const allFilteredSelected =
      filteredContacts.length > 0 &&
      filteredContacts.every((c) => selectedIds.includes(c.id));

    const toggleSelectAllFiltered = () => {
      if (allFilteredSelected) {
        const visibleIds = new Set(filteredContacts.map((c) => c.id));
        setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      } else {
        const merged = new Set([...selectedIds, ...filteredContacts.map((c) => c.id)]);
        setSelectedIds(Array.from(merged));
      }
    };

    const handleBulkDelete = async () => {
      if (selectedIds.length === 0) return;
      const ok = await confirmAction({
        title: `Delete ${selectedIds.length} contact${selectedIds.length === 1 ? '' : 's'}?`,
        message: 'This cannot be undone.',
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!ok) return;

      setDeletingSelection(true);
      try {
        const response = await axios.post(
          `${API}/contacts/bulk-delete`,
          { contact_ids: selectedIds },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const deleted = response.data?.deleted_count ?? selectedIds.length;
        // Optimistic UI update
        const toDelete = new Set(selectedIds);
        setContacts((prev) => prev.filter((c) => !toDelete.has(c.id)));
        invalidateContactsCache();
        exitSelectMode();
        toast.success(`${deleted} contact${deleted === 1 ? '' : 's'} deleted`);
      } catch (error) {
        console.error('Bulk delete failed:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete selected contacts.');
      } finally {
        setDeletingSelection(false);
      }
    };

    // Desktop keyboard: ↑/↓ highlight a contact, Enter opens it on the right, Esc closes the pane
    useEffect(() => {
      if (!isDesktop) return undefined;
      const onKeyDown = (e) => {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        const el = e.target;
        if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
        if (document.querySelector('.fixed.inset-0')) return;
        if (e.key === 'Escape') {
          if (contactSelection) setContactSelection(null);
          return;
        }
        if (selectMode) return;
        const ids = Array.from(document.querySelectorAll('[data-testid^="contact-row-"]')).map((n) => n.dataset.testid.slice(12));
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (!ids.length) return;
          e.preventDefault();
          const current = keyboardContactId ?? (contactSelection?.type === 'contact' ? contactSelection.id : null);
          const idx = ids.indexOf(current);
          const next = idx === -1 ? 0 : Math.min(Math.max(idx + (e.key === 'ArrowDown' ? 1 : -1), 0), ids.length - 1);
          setKeyboardContactId(ids[next]);
          document.querySelector(`[data-testid="contact-row-${ids[next]}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && keyboardContactId && ids.includes(keyboardContactId)) {
          e.preventDefault();
          setContactSelection({ type: 'contact', id: keyboardContactId });
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [isDesktop, contactSelection, keyboardContactId, selectMode]);

    return (
      <MobileLayout onRefresh={refreshAllData} title={t('contacts')} subtitle={`${filteredContacts.length} ${filteredContacts.length === 1 ? (t('contact') === 'contact' ? 'contact' : t('contact').toLowerCase()) : t('contacts').toLowerCase()}`}>
        {/* Hidden file picker for contact import */}
        <input
          ref={importInputRef}
          type="file"
          accept=".vcf,.csv,text/vcard,text/x-vcard,text/csv"
          onChange={handleImportFile}
          className="hidden"
          data-testid="contact-import-file-input"
        />

        <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        <div className="lg:col-span-5" data-testid="contacts-list-column">
        {/* Search Bar */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder={t('searchByNameOrPlace')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Contacts Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 shadow-sm mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-sm text-blue-800 leading-relaxed">
              {t('contactsExplanation')}
            </p>
          </div>
        </div>

        {/* Import / Add actions */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={handleImportClick}
            disabled={importing}
            data-testid="contact-import-button"
            className="flex items-center justify-center gap-2 bg-white border border-blue-200 text-blue-700 rounded-xl py-3 font-medium hover:bg-blue-50 transition-colors disabled:opacity-60"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
            </svg>
            {importing ? t('importingProgress') : t('importFromPhone')}
          </button>
          <button
            onClick={handleOpenAddContact}
            data-testid="contact-add-button"
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl py-3 font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            {t('addContactBtn')}
          </button>
          <PickFromPhoneButton
            className="col-span-2"
            onDone={async () => { invalidateContactsCache(); await fetchContacts(); }}
          />
        </div>
        <p className="text-xs text-gray-500 mb-4 px-1">
          {t('importTipText')}
        </p>

        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('contacts')}</h2>
            {filteredContacts.length > 0 && (
              <div className="flex items-center gap-2">
                {!selectMode ? (
                  <button
                    onClick={() => setSelectMode(true)}
                    data-testid="contacts-select-mode-button"
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50"
                  >
                    {t('selectBtn')}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={toggleSelectAllFiltered}
                      data-testid="contacts-select-all-button"
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50"
                    >
                      {allFilteredSelected ? 'Clear' : 'Select all'}
                    </button>
                    <button
                      onClick={exitSelectMode}
                      data-testid="contacts-cancel-select-button"
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100"
                    >
                      {t('cancel')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {selectMode && (
            <div className="flex items-center justify-between mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <span className="text-sm text-blue-900" data-testid="contacts-selected-count">
                {selectedIds.length} {t('selectedCountSuffix')}
              </span>
              <div className="flex items-center gap-2">
              <BulkCopyContactsButton selectedIds={selectedIds} onDone={exitSelectMode} />
              <button
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0 || deletingSelection}
                data-testid="contacts-bulk-delete-button"
                className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                </svg>
                {deletingSelection ? t('deletingProgress') : t('delete')}
              </button>
              </div>
            </div>
          )}

          {filteredContacts.length > 0 ? (
            <div className="space-y-3">
              {filteredContacts.map((contact) => {
                const isSelected = selectedIds.includes(contact.id);
                return (
                  <div
                    key={contact.id}
                    data-testid={`contact-row-${contact.id}`}
                    className={`flex items-center p-3 border rounded-lg transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-400'
                        : isDesktop && contactSelection?.type === 'contact' && contactSelection.id === contact.id
                          ? 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500'
                          : isDesktop && keyboardContactId === contact.id
                            ? 'border-indigo-300 ring-2 ring-indigo-300'
                            : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => handleContactClick(contact.id)}
                  >
                    {selectMode && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleContactClick(contact.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`contact-checkbox-${contact.id}`}
                        className="mr-3 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    )}
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                      {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate flex items-center">
                        <span className="truncate">{contact.name}</span>
                        <OfflineIndicator entity={contact} className="ml-2 flex-shrink-0 relative" />
                      </h3>
                      {contact.email && (
                        <p className="text-sm text-gray-600 truncate">{contact.email}</p>
                      )}
                      {contact.company && (
                        <p className="text-sm text-blue-600 truncate">{contact.company}</p>
                      )}
                    </div>
                    {!selectMode && (
                      <div className="ml-3 flex-shrink-0">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            {searchQuery ? (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('noContactsFound')}</h3>
                <p className="text-gray-600 mb-4">{t('noContactsFoundDesc')}</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {t('clearSearch')}
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('noContactsYet')}</h3>
                <p className="text-gray-600 mb-4">{t('addFirstContact')}</p>
              </>
            )}
            <button
              onClick={handleOpenAddContact}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
            >
              Add Contact
            </button>
          </div>
        )}
      </div>
      </div>

      {isDesktop && (
        <aside
          className="lg:col-span-7 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:z-50 lg:px-1 rounded-2xl"
          data-testid="contacts-detail-pane"
        >
          <TaskSplitPane
            selection={contactSelection}
            setSelection={setContactSelection}
            onChanged={refreshAllData}
            glance={
              <ContactsGlance
                contacts={contacts}
                onSelectContact={(id) => setContactSelection({ type: 'contact', id })}
              />
            }
          />
        </aside>
      )}
      </div>

      {/* Floating Action Button */}
      {!selectMode && (
        <button
          onClick={handleOpenAddContact}
          data-testid="contacts-fab"
          className="lg:hidden fixed bottom-20 right-4 w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 z-40 flex items-center justify-center"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}

      {/* Add Contact Modal */}
      <AddContactModal 
        isOpen={showAddContact} 
        onClose={handleCloseAddContact} 
      />
    </MobileLayout>
  );
  };

  // Complete Administration Page with Admin Panel and Content Management
  const AdministrationPage = () => {
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();
    
    // Redirect non-admin/manager users
    if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager')) {
      return (
        <MobileLayout title="Access Denied" subtitle="You don't have permission to access this page">
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🚫</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600 mb-4">You don't have permission to access the administration panel.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Go Back to Home
            </button>
          </div>
        </MobileLayout>
      );
    }

    const [activeSection, setActiveSection] = useState('admin');
    const [activeAdminTab, setActiveAdminTab] = useState('users');
    const [activeContentTab, setActiveContentTab] = useState('support');
    const [users, setUsers] = useState([]);
    const [adminLoading, setAdminLoading] = useState(false);
    const [adminStats, setAdminStats] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [userFilters, setUserFilters] = useState({
      status: 'all',
      subscription: 'all',
      admin: 'all'
    });
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [openUserMenuId, setOpenUserMenuId] = useState(null); // Track which user menu is open
    const [selectedUserForView, setSelectedUserForView] = useState(null); // Selected user for viewing data
    // Feature flags shown in the Features tab. Each entry is a full flag
    // document from /api/admin/feature-flags. Loaded lazily on tab open.
    const [featureFlags, setFeatureFlags] = useState([]);
    const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);
    const [featureFlagSaving, setFeatureFlagSaving] = useState(null); // flag key being saved

    const sections = [
      { id: 'admin', label: 'Admin Panel', icon: '🛡️' },
      { id: 'content', label: 'Content Management', icon: '📊' }
    ];

    const adminTabs = [
      { id: 'users', label: 'User Management', icon: '👥' },
      { id: 'user-tasks', label: 'User Tasks', icon: '📋' },
      { id: 'user-contacts', label: 'User Contacts', icon: '👥' },
      { id: 'user-payments', label: 'User Payments', icon: '💳' },
      { id: 'analytics', label: 'Analytics', icon: '📊' },
      { id: 'features', label: 'Features', icon: '🚩' },
      { id: 'system', label: 'System', icon: '🔧' }
    ];

    const contentTabs = [
      { id: 'support', label: 'Support Content', icon: '📝' },
      { id: 'knowledge', label: 'AI Knowledge Base', icon: '🧠' }
    ];

    const fetchUsers = useCallback(async () => {
      try {
        setAdminLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data || []);
      } catch (error) {
        console.error('Error fetching users:', error);
        if (error.response?.status === 403) {
          alert('Admin access required to view users');
        }
      } finally {
        setAdminLoading(false);
      }
    }, []);

    const fetchAdminStats = useCallback(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdminStats(response.data);
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      }
    }, []);

    useEffect(() => {
      if (activeSection === 'admin' && activeAdminTab === 'users') {
        fetchUsers();
        fetchAdminStats();
      }
    }, [activeSection, activeAdminTab, fetchUsers, fetchAdminStats]);

    // Load feature flags lazily when the Features tab is opened. Cached in
    // state for the lifetime of the page so toggling stays snappy.
    const fetchFeatureFlags = useCallback(async () => {
      setFeatureFlagsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/feature-flags`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFeatureFlags(res.data || []);
      } catch (err) {
        console.error('Error fetching feature flags:', err);
      } finally {
        setFeatureFlagsLoading(false);
      }
    }, []);

    useEffect(() => {
      if (activeSection === 'admin' && activeAdminTab === 'features') {
        fetchFeatureFlags();
      }
    }, [activeSection, activeAdminTab, fetchFeatureFlags]);

    const toggleFeatureFlag = useCallback(async (flagKey, nextEnabled) => {
      setFeatureFlagSaving(flagKey);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.put(
          `${API}/admin/feature-flags/${flagKey}`,
          { enabled: nextEnabled },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFeatureFlags((prev) => prev.map((f) => (f.key === flagKey ? res.data : f)));
      } catch (err) {
        console.error('Error toggling feature flag:', err);
        alert('Could not save the change. Please try again.');
      } finally {
        setFeatureFlagSaving(null);
      }
    }, []);

    // Filter users based on search and filters
    const filteredUsers = useMemo(() => {
      let filtered = users;

      // Apply search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(user =>
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
        );
      }

      // Apply filters
      if (userFilters.status !== 'all') {
        // Backend users have subscription_status, map status filter appropriately
        if (userFilters.status === 'active') {
          filtered = filtered.filter(user => user.subscription_status === 'active');
        } else if (userFilters.status === 'inactive') {
          filtered = filtered.filter(user => user.subscription_status === 'inactive' || user.subscription_status === 'free');
        } else if (userFilters.status === 'blocked') {
          filtered = filtered.filter(user => user.subscription_status === 'blocked');
        }
      }
      if (userFilters.subscription !== 'all') {
        filtered = filtered.filter(user => user.subscription_status === userFilters.subscription);
      }
      if (userFilters.admin !== 'all') {
        filtered = filtered.filter(user => user.role === userFilters.admin);
      }

      return filtered;
    }, [users, searchQuery, userFilters]);

    // User Actions
    const handleEditUser = (user) => {
      setEditingUser(user);
      setShowUserModal(true);
    };

    const handleDeleteUser = async (userId) => {
      if (window.confirm('Are you sure you want to delete this user? This will permanently delete the user and all their data.')) {
        try {
          const token = localStorage.getItem('token');
          console.log('🗑️ Attempting to delete user:', userId);
          console.log('🗑️ API endpoint:', `${API}/admin/users/${userId}`);
          
          const response = await axios.delete(`${API}/admin/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('🗑️ Delete response:', response);
          
          // Update UI after successful deletion
          setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
          
          // Refresh user list to ensure sync
          await fetchUsers();
          
          alert('User deleted successfully');
        } catch (error) {
          console.error('❌ Error deleting user:', error);
          console.error('❌ Error response:', error.response);
          alert(error.response?.data?.detail || 'Failed to delete user');
        }
      }
    };

    const handleToggleUserStatus = async (userId) => {
      try {
        const user = users.find(u => u.id === userId);
        const newStatus = user.subscription_status === 'active' ? 'inactive' : 'active';
        const token = localStorage.getItem('token');
        
        await axios.put(`${API}/admin/users/${userId}`, 
          { subscription_status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setUsers(users.map(u => u.id === userId ? {...u, subscription_status: newStatus} : u));
        alert(`User status updated to ${newStatus}`);
      } catch (error) {
        console.error('Error toggling user status:', error);
        alert(error.response?.data?.detail || 'Failed to update user status');
      }
    };

    const handleChangeRole = async (userId, newRole) => {
      try {
        const user = users.find(u => u.id === userId);
        const token = localStorage.getItem('token');
        
        const response = await axios.put(`${API}/admin/users/${userId}/role`, 
          { role: newRole },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update user with new role and subscription tier
        // Admin and Manager users automatically become Pro
        const newSubscriptionStatus = ['admin', 'manager'].includes(newRole) ? 'pro' : user.subscription_status;
        const newSubscriptionPlan = ['admin', 'manager'].includes(newRole) ? 'pro' : user.subscription_plan;
        
        setUsers(users.map(u => u.id === userId ? {
          ...u, 
          role: newRole,
          subscription_status: newSubscriptionStatus,
          subscription_plan: newSubscriptionPlan,
          subscription_expires: ['admin', 'manager'].includes(newRole) ? null : u.subscription_expires
        } : u));
        
        await fetchUsers(); // Refresh to ensure sync
        alert(`User role updated to ${newRole}. ${response.data.subscription_update || ''}`);
      } catch (error) {
        console.error('Error changing user role:', error);
        alert(error.response?.data?.detail || 'Failed to update user role');
      }
    };

    const handleGrantPro = async (userId) => {
      if (window.confirm('Grant Pro plan to this user? This will give them unlimited access.')) {
        try {
          const token = localStorage.getItem('token');
          
          await axios.post(`${API}/admin/users/${userId}/grant-pro`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update local state
          setUsers(users.map(u => u.id === userId ? {
            ...u, 
            subscription_status: 'pro',
            subscription_plan: 'pro',
            subscription_expires: null
          } : u));
          
          await fetchUsers(); // Refresh to ensure sync
          alert('Pro plan granted successfully!');
        } catch (error) {
          console.error('Error granting Pro plan:', error);
          alert(error.response?.data?.detail || 'Failed to grant Pro plan. You need Manager privileges.');
        }
      }
    };

    const handleRevokePro = async (userId) => {
      if (window.confirm('Revoke Pro plan from this user? They will be downgraded to Free plan.')) {
        try {
          const token = localStorage.getItem('token');
          
          await axios.post(`${API}/admin/users/${userId}/revoke-pro`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update local state
          setUsers(users.map(u => u.id === userId ? {
            ...u, 
            subscription_status: 'free',
            subscription_plan: 'free',
            subscription_expires: null
          } : u));
          
          await fetchUsers(); // Refresh to ensure sync
          alert('Pro plan revoked successfully!');
        } catch (error) {
          console.error('Error revoking Pro plan:', error);
          alert(error.response?.data?.detail || 'Failed to revoke Pro plan. You need Manager privileges.');
        }
      }
    };

    // Check if user can receive trial (ONE-TIME ONLY - only if never used)
    const canReceiveTrial = (user) => {
      // Pro users cannot receive trial
      if (user.subscription_status === 'pro') return false;
      
      // Check if user already used their trial (ONE-TIME ONLY)
      if (user.trial_used === true) return false;
      
      // If trial_used is false or not set, user can receive trial
      return true;
    };

    const handleGrantTrial = async (userId) => {
      if (window.confirm('Grant 3-day trial to this user? This will give them full Pro access for 3 days.')) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/admin/grant-trial`, 
            { user_id: userId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          await fetchUsers(); // Refresh to get updated trial dates
          alert(response.data.message || '3-day trial granted successfully!');
        } catch (error) {
          console.error('Error granting trial:', error);
          alert(error.response?.data?.detail || 'Failed to grant trial. You need Admin or Manager privileges.');
        }
      }
    };

    const handleResetPassword = async (userId) => {
      if (window.confirm('Send password reset email to this user?')) {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          alert('Password reset email sent!');
        } catch (error) {
          console.error('Error resetting password:', error);
          alert(error.response?.data?.detail || 'Failed to send password reset email');
        }
      }
    };

    const handleBulkAction = async (action) => {
      if (selectedUsers.length === 0) {
        alert('Please select users first');
        return;
      }

      const actionText = action === 'delete' ? 
        'permanently delete' : 
        action === 'block' ? 'block' : 'activate';

      if (window.confirm(`Are you sure you want to ${actionText} ${selectedUsers.length} selected users?`)) {
        try {
          const token = localStorage.getItem('token');
          
          switch (action) {
            case 'delete':
              console.log('🗑️ Bulk deleting users:', selectedUsers);
              // Delete each user via API
              const deletePromises = selectedUsers.map(userId => 
                axios.delete(`${API}/admin/users/${userId}`, {
                  headers: { Authorization: `Bearer ${token}` }
                })
              );
              await Promise.all(deletePromises);
              
              // Update UI after all deletions
              setUsers(prevUsers => prevUsers.filter(u => !selectedUsers.includes(u.id)));
              
              // Refresh user list to ensure sync
              await fetchUsers();
              alert(`${selectedUsers.length} users deleted successfully`);
              break;
              
            case 'block':
              console.log('🚫 Bulk blocking users:', selectedUsers);
              const blockPromises = selectedUsers.map(userId =>
                axios.put(`${API}/admin/users/${userId}`, 
                  { subscription_status: 'blocked' },
                  { headers: { Authorization: `Bearer ${token}` } }
                )
              );
              await Promise.all(blockPromises);
              
              setUsers(prevUsers => prevUsers.map(u => 
                selectedUsers.includes(u.id) ? {...u, subscription_status: 'blocked'} : u
              ));
              await fetchUsers();
              alert(`${selectedUsers.length} users blocked successfully`);
              break;
              
            case 'activate':
              console.log('✅ Bulk activating users:', selectedUsers);
              const activatePromises = selectedUsers.map(userId =>
                axios.put(`${API}/admin/users/${userId}`, 
                  { subscription_status: 'active' },
                  { headers: { Authorization: `Bearer ${token}` } }
                )
              );
              await Promise.all(activatePromises);
              
              setUsers(prevUsers => prevUsers.map(u => 
                selectedUsers.includes(u.id) ? {...u, subscription_status: 'active'} : u
              ));
              await fetchUsers();
              alert(`${selectedUsers.length} users activated successfully`);
              break;
              
            default:
              break;
          }
          
          setSelectedUsers([]);
        } catch (error) {
          console.error('❌ Error with bulk action:', error);
          alert(error.response?.data?.detail || `Failed to ${action} users`);
        }
      }
    };

    // Handlers for viewing user-specific data
    const handleViewUserTasks = (user) => {
      setSelectedUserForView(user);
      setActiveAdminTab('user-tasks');
      setOpenUserMenuId(null);
    };

    const handleViewUserContacts = (user) => {
      setSelectedUserForView(user);
      setActiveAdminTab('user-contacts');
      setOpenUserMenuId(null);
    };

    const handleViewUserPayments = (user) => {
      setSelectedUserForView(user);
      setActiveAdminTab('user-payments');
      setOpenUserMenuId(null);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = () => {
        if (openUserMenuId) {
          setOpenUserMenuId(null);
        }
      };
      
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, [openUserMenuId]);

    return (
      <MobileLayout title="Administration" subtitle="System management dashboard">
        {/* Section Navigation */}
        <div className="bg-white rounded-2xl p-1 mb-4 shadow-sm">
          <div className="grid grid-cols-2 gap-1">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center justify-center p-3 rounded-xl transition-colors ${
                  activeSection === section.id
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg mr-2">{section.icon}</span>
                <span className="text-sm font-medium">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ADMIN PANEL SECTION */}
        {activeSection === 'admin' && (
          <>
            {/* Admin Tab Navigation */}
            <div className="bg-white rounded-2xl p-1 mb-4 shadow-sm">
              <div className="grid grid-cols-3 gap-1">
                {adminTabs.slice(0, 6).map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveAdminTab(tab.id)}
                    className={`flex flex-col items-center p-2 rounded-xl transition-colors text-xs ${
                      activeAdminTab === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-sm mb-1">{tab.icon}</span>
                    <span className="text-xs font-medium">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Users Management Tab */}
            {activeAdminTab === 'users' && (
              <div className="space-y-4">
                {/* Search and Filters */}
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <select
                      value={userFilters.status}
                      onChange={(e) => setUserFilters({...userFilters, status: e.target.value})}
                      className="p-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="blocked">Blocked</option>
                      <option value="inactive">Inactive</option>
                    </select>

                    <select
                      value={userFilters.subscription}
                      onChange={(e) => setUserFilters({...userFilters, subscription: e.target.value})}
                      className="p-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="all">All Plans</option>
                      <option value="free">Free</option>
                      <option value="active">Pro</option>
                    </select>

                    <select
                      value={userFilters.admin}
                      onChange={(e) => setUserFilters({...userFilters, admin: e.target.value})}
                      className="p-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="all">All Roles</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>
                  </div>
                </div>

                {/* Bulk Actions */}
                {selectedUsers.length > 0 && (
                  <div className="bg-blue-50 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-800 font-medium">
                        {selectedUsers.length} users selected
                      </span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleBulkAction('activate')}
                          className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                        >
                          Activate
                        </button>
                        <button
                          onClick={() => handleBulkAction('block')}
                          className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600"
                        >
                          Block
                        </button>
                        <button
                          onClick={() => handleBulkAction('delete')}
                          className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Users List */}
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {filteredUsers.length} Users
                    </span>
                  </div>

                  {adminLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Loading users...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredUsers.map((user) => (
                        <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers([...selectedUsers, user.id]);
                                  } else {
                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                  }
                                }}
                                className="mr-3 mt-1"
                              />
                              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                                {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h4 className="font-medium text-gray-900">{user.name}</h4>
                                  {user.role === 'manager' && (
                                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium">
                                      👑 Manager
                                    </span>
                                  )}
                                  {user.role === 'admin' && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                      🛡️ Admin
                                    </span>
                                  )}
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    user.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                                    user.subscription_status === 'blocked' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {user.subscription_status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">{user.email}</p>
                                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                  <span>Joined: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</span>
                                  <span>Last login: {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</span>
                                </div>
                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                  <span>{user.tasks_count || 0} tasks</span>
                                  <span>{user.contacts_count || 0} contacts</span>
                                  <span className={
                                    user.subscription_status === 'active' 
                                      ? 'text-green-600 font-medium' 
                                      : 'text-gray-500'
                                  }>
                                    {user.subscription_status === 'active' ? 'Pro Plan' : 'Free Plan'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Action Dropdown */}
                            <div className="relative">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenUserMenuId(openUserMenuId === user.id ? null : user.id);
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                </svg>
                              </button>

                              {/* Dropdown Menu */}
                              {openUserMenuId === user.id && (
                                <div 
                                  className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* View Data Section */}
                                  <div className="py-1 border-b border-gray-200">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                                      View Data
                                    </div>
                                    <button
                                      onClick={() => handleViewUserTasks(user)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center"
                                    >
                                      <span className="mr-2">📋</span>
                                      <span>Tasks ({user.tasks_count || 0})</span>
                                    </button>
                                    <button
                                      onClick={() => handleViewUserContacts(user)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center"
                                    >
                                      <span className="mr-2">👥</span>
                                      <span>Contacts ({user.contacts_count || 0})</span>
                                    </button>
                                    <button
                                      onClick={() => handleViewUserPayments(user)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center"
                                    >
                                      <span className="mr-2">💳</span>
                                      <span>Payments</span>
                                    </button>
                                  </div>

                                  {/* Actions Section */}
                                  <div className="py-1 border-b border-gray-200">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                                      Actions
                                    </div>
                                    <button
                                      onClick={() => {
                                        handleEditUser(user);
                                        setOpenUserMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center"
                                    >
                                      <span className="mr-2">✏️</span>
                                      <span>Edit User</span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleToggleUserStatus(user.id);
                                        setOpenUserMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-600 flex items-center"
                                    >
                                      <span className="mr-2">{user.subscription_status === 'active' ? '🚫' : '✅'}</span>
                                      <span>{user.subscription_status === 'active' ? 'Deactivate' : 'Activate'}</span>
                                    </button>
                                    {/* Role Management - Only for Managers */}
                                    {currentUser.role === 'manager' && (
                                      <div className="border-t border-gray-100 pt-1">
                                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                                          Change Role
                                        </div>
                                        {user.role !== 'manager' && (
                                          <button
                                            onClick={() => {
                                              handleChangeRole(user.id, 'manager');
                                              setOpenUserMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 flex items-center"
                                          >
                                            <span className="mr-2">👑</span>
                                            <span>Make Manager</span>
                                          </button>
                                        )}
                                        {user.role !== 'admin' && (
                                          <button
                                            onClick={() => {
                                              handleChangeRole(user.id, 'admin');
                                              setOpenUserMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center"
                                          >
                                            <span className="mr-2">🛡️</span>
                                            <span>Make Admin</span>
                                          </button>
                                        )}
                                        {user.role !== 'user' && (
                                          <button
                                            onClick={() => {
                                              handleChangeRole(user.id, 'user');
                                              setOpenUserMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-700 flex items-center"
                                          >
                                            <span className="mr-2">👤</span>
                                            <span>Make Regular User</span>
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Pro Plan Management - Only for Managers */}
                                    {currentUser.role === 'manager' && user.role === 'user' && (
                                      <>
                                        {user.subscription_status !== 'pro' ? (
                                          <button
                                            onClick={() => {
                                              handleGrantPro(user.id);
                                              setOpenUserMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 flex items-center"
                                          >
                                            <span className="mr-2">⭐</span>
                                            <span>Grant Pro Plan</span>
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => {
                                              handleRevokePro(user.id);
                                              setOpenUserMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center"
                                          >
                                            <span className="mr-2">🚫</span>
                                            <span>Revoke Pro Plan</span>
                                          </button>
                                        )}
                                      </>
                                    )}
                                    
                                    {/* Grant Trial - Only for free users with expired/no trial */}
                                    {(currentUser.role === 'admin' || currentUser.role === 'manager') && canReceiveTrial(user) && (
                                      <button
                                        onClick={() => {
                                          handleGrantTrial(user.id);
                                          setOpenUserMenuId(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center"
                                      >
                                        <span className="mr-2">✨</span>
                                        <span>Grant 3-Day Trial</span>
                                      </button>
                                    )}
                                    
                                    <button
                                      onClick={() => {
                                        handleResetPassword(user.id);
                                        setOpenUserMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 flex items-center"
                                    >
                                      <span className="mr-2">🔑</span>
                                      <span>Reset Password</span>
                                    </button>
                                  </div>

                                  {/* Danger Zone */}
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handleDeleteUser(user.id);
                                        setOpenUserMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 flex items-center"
                                    >
                                      <span className="mr-2">🗑️</span>
                                      <span>Delete User</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* User Statistics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <div className="text-2xl font-bold text-blue-600">
                      {adminStats ? adminStats.active_users : users.filter(u => u.subscription_status === 'active' || u.subscription_status === 'pro').length}
                    </div>
                    <div className="text-sm text-gray-600">Pro Subscribers</div>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">
                      {users.filter(u => u.role === 'manager').length}
                    </div>
                    <div className="text-sm text-gray-600">Managers</div>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <div className="text-2xl font-bold text-green-600">
                      {users.filter(u => u.role === 'admin').length}
                    </div>
                    <div className="text-sm text-gray-600">Admins</div>
                  </div>
                </div>
              </div>
            )}

            {/* User Tasks Management Tab */}
            {activeAdminTab === 'user-tasks' && (
              <UserTasksManager 
                users={users} 
                selectedUser={selectedUserForView}
                onBack={() => {
                  setActiveAdminTab('users');
                  setSelectedUserForView(null);
                }}
              />
            )}

            {/* User Contacts Management Tab */}
            {activeAdminTab === 'user-contacts' && (
              <UserContactsManager 
                users={users} 
                selectedUser={selectedUserForView}
                onBack={() => {
                  setActiveAdminTab('users');
                  setSelectedUserForView(null);
                }}
              />
            )}

            {/* User Payments Management Tab */}
            {activeAdminTab === 'user-payments' && (
              <UserPaymentsManager 
                users={users} 
                selectedUser={selectedUserForView}
                onBack={() => {
                  setActiveAdminTab('users');
                  setSelectedUserForView(null);
                }}
              />
            )}

            {/* Analytics Tab */}
            {activeAdminTab === 'analytics' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">System Analytics</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{contacts.length}</div>
                      <div className="text-sm text-gray-600">Total Contacts</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">95%</div>
                      <div className="text-sm text-gray-600">System Uptime</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Database Status</span>
                      <span className="text-green-600 font-medium">✅ Healthy</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">API Response Time</span>
                      <span className="text-blue-600 font-medium">&lt; 200ms</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Active Sessions</span>
                      <span className="text-purple-600 font-medium">{users.filter(u => u.subscription_status === 'active').length} users</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* System Settings Tab */}
            {activeAdminTab === 'features' && (
              <div className="space-y-4" data-testid="admin-features-section">
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Feature Flags</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Toggle product surfaces on or off without a redeploy. Changes
                    take effect within ~30 seconds for all users.
                  </p>

                  {featureFlagsLoading && featureFlags.length === 0 && (
                    <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
                  )}

                  <div className="divide-y divide-gray-100">
                    {featureFlags.map((flag) => (
                      <div
                        key={flag.key}
                        className="flex items-start justify-between gap-4 py-4"
                        data-testid={`feature-flag-row-${flag.key}`}
                      >
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {flag.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">{flag.description}</p>
                          {flag.updated_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              Last changed by {flag.updated_by || 'system'} on{' '}
                              {new Date(flag.updated_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                          <input
                            type="checkbox"
                            checked={!!flag.enabled}
                            disabled={featureFlagSaving === flag.key}
                            onChange={(e) => toggleFeatureFlag(flag.key, e.target.checked)}
                            className="sr-only peer"
                            data-testid={`feature-flag-toggle-${flag.key}`}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-60"></div>
                        </label>
                      </div>
                    ))}
                  </div>

                  {!featureFlagsLoading && featureFlags.length === 0 && (
                    <p className="text-sm text-gray-500 py-6 text-center">
                      No feature flags configured.
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeAdminTab === 'system' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">System Settings</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-200">
                      <div>
                        <h4 className="font-medium text-gray-700">Maintenance Mode</h4>
                        <p className="text-sm text-gray-500">Temporarily disable user access</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-200">
                      <div>
                        <h4 className="font-medium text-gray-700">Email Notifications</h4>
                        <p className="text-sm text-gray-500">System-wide email alerts</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3">
                      <div>
                        <h4 className="font-medium text-gray-700">Auto Backups</h4>
                        <p className="text-sm text-gray-500">Daily database backups</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Server Version</span>
                      <span className="font-medium">v2.1.0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Database Size</span>
                      <span className="font-medium">15.2 MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Backup</span>
                      <span className="font-medium">Today, 3:00 AM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Server Uptime</span>
                      <span className="font-medium">7 days, 12 hours</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* CONTENT MANAGEMENT SECTION */}
        {activeSection === 'content' && (
          <>
            {/* Content Tab Navigation */}
            <div className="bg-white rounded-2xl p-1 mb-4 shadow-sm">
              <div className="grid grid-cols-1 gap-1">
                {contentTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveContentTab(tab.id)}
                    className={`flex items-center justify-center p-3 rounded-xl transition-colors ${
                      activeContentTab === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-lg mr-2">{tab.icon}</span>
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Support Content Management */}
            {activeContentTab === 'support' && (
              <SupportContentManager />
            )}

            {/* AI Knowledge Base Management */}
            {activeContentTab === 'knowledge' && (
              <AIKnowledgeBaseManager />
            )}
          </>
        )}

        {/* Back to Main App */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mt-6">
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-gray-500 to-gray-600 text-white py-3 rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-all"
          >
            ← Back to Main App
          </button>
        </div>
      </MobileLayout>
    );
  };

  // Settings Page
  const SettingsPage = () => {
    const { user } = useAuth();
    const { currentLanguage, changeLanguage, t, getAvailableLanguages } = useLanguage();
    const { supportContent } = useSupportContent();
    const navigate = useNavigate();
    const { section } = useParams();

    // The hub-and-detail Settings page derives its active section from the URL:
    //   /settings           → hub view (vertical list of sections)
    //   /settings/profile   → renders the Profile section + a back link
    //   /settings/preferences, /categories, /subscription, /support → same idea
    // We accept legacy `sessionStorage.settingsActiveTab` once on mount so a
    // user who had a tab open before this refactor lands on the right section.
    const VALID_SECTIONS = ['profile', 'preferences', 'categories', 'cloud', 'subscription', 'support'];
    const activeTab = section && VALID_SECTIONS.includes(section) ? section : null;
    const setActiveTab = (id) => {
      navigate(id ? `/settings/${id}` : '/settings');
    };
    useEffect(() => {
      // One-shot migration: if we landed on /settings (hub) but the user had a
      // saved tab from the old design, jump them straight there. Cleared after
      // use so the hub becomes the default landing on subsequent visits.
      if (!section) {
        const legacy = sessionStorage.getItem('settingsActiveTab');
        if (legacy && VALID_SECTIONS.includes(legacy)) {
          sessionStorage.removeItem('settingsActiveTab');
          navigate(`/settings/${legacy}`, { replace: true });
        }
      }
    }, []);

    // Debug: Track activeTab changes
    useEffect(() => {
      console.log('🔧 [Components.js SettingsPage] Active section:', activeTab || 'hub');
    }, [activeTab]);
    
    const [showAIHelp, setShowAIHelp] = useState(false); // AI Help Assistant modal state
    const [profileData, setProfileData] = useState({
      name: user?.name || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    const [preferences, setPreferences] = useState({
      notifications: false, // Task reminders (legacy, kept for compatibility)
      weeklyDigest: false,
      weeklyDigestDay: 'monday',
      weeklyDigestTime: '08:00',
      dailyDigest: false,
      dailyDigestTime: '20:00',  // Default to 8 PM
      taskReminderMethod: 'email', // Task reminder method: 'email' or 'sms'
      taskReminderPhone: '', // Phone number for SMS reminders
      darkMode: false,
      language: currentLanguage // Sync with language context
    });
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [showDeleteAccount, setShowDeleteAccount] = useState(false);
    
    // Billing history state
    const [subscriptionSummary, setSubscriptionSummary] = useState(null);
    const [billingHistory, setBillingHistory] = useState([]);
    const [billingLoading, setBillingLoading] = useState(false);
    // Feature flags + organizations the user belongs to. These power the
    // Business plan card on the subscription page — both are hidden until
    // an admin has toggled the `business_workspaces` flag on for this user.
    const [featureFlags, setFeatureFlags] = useState({});
    const [myOrgs, setMyOrgs] = useState([]);
    const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
    const [newOrgName, setNewOrgName] = useState('');
    const [creatingOrg, setCreatingOrg] = useState(false);
    
    // Initialize dark mode from localStorage
    useEffect(() => {
      const savedTheme = localStorage.getItem('theme');
      const savedPreferences = localStorage.getItem('edotask_preferences');
      
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
        setPreferences(prev => ({ ...prev, darkMode: true }));
      }
      
      if (savedPreferences) {
        try {
          const parsed = JSON.parse(savedPreferences);
          setPreferences(prev => ({ ...prev, ...parsed }));
          
          // Apply dark mode if it was saved
          if (parsed.darkMode) {
            document.documentElement.classList.add('dark');
          }
        } catch (e) {
          console.error('Failed to parse preferences:', e);
        }
      }
    }, []);
    
    // Support section state
    const [supportSections, setSupportSections] = useState({
      helpCenter: false,
      sendFeedback: false,
      privacyPolicy: false,
      termsOfService: false,
      cookiePolicy: false
    });

    const toggleSupportSection = (section) => {
      setSupportSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    // Update profile data when user changes
    useEffect(() => {
      if (user) {
        setProfileData(prev => ({
          ...prev,
          name: user.name || '',
          email: user.email || ''
        }));
      }
    }, [user]);

    // Load feature flags + org memberships once the user reaches the
    // subscription tab. Both are cheap and gating future UI on them lets us
    // ship to production with the feature dark until you flip the flag.
    useEffect(() => {
      if (activeTab !== 'subscription') return;
      const load = async () => {
        try {
          const token = localStorage.getItem('token');
          const flagsRes = await axios.get(`${API}/feature-flags`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setFeatureFlags(flagsRes.data || {});
          if (flagsRes.data?.business_workspaces) {
            const orgsRes = await axios.get(`${API}/organizations/mine`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setMyOrgs(orgsRes.data || []);
          }
        } catch (err) {
          // Silent — Business is an optional surface; we just hide its card
          // if the API isn't reachable for any reason.
          console.warn('Feature flags / org membership fetch failed:', err?.response?.status);
        }
      };
      load();
    }, [activeTab]);

    // Create a new org via two API calls: POST /organizations (foundation)
    // then POST /organizations/{id}/checkout (Stripe). On success we
    // redirect to Stripe's hosted checkout; the webhook + the
    // /organization/welcome route handle the rest.
    const handleCreateOrganization = useCallback(async () => {
      const name = (newOrgName || '').trim();
      if (name.length < 2) return;
      setCreatingOrg(true);
      try {
        const token = localStorage.getItem('token');
        const orgRes = await axios.post(
          `${API}/organizations`,
          { name },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const orgId = orgRes.data.id;
        try {
          const checkoutRes = await axios.post(
            `${API}/organizations/${orgId}/checkout`,
            { origin_url: window.location.origin },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          window.location.href = checkoutRes.data.checkout_url;
        } catch (checkoutErr) {
          // Billing isn't wired (no STRIPE_PRICE_ID_BUSINESS_SEAT) — the org
          // exists already, so land them on the welcome page so they can
          // explore the workspace surface while we finish setup.
          console.warn('Org checkout unavailable:', checkoutErr?.response?.data?.detail);
          window.location.href = `/organization/welcome?org_id=${orgId}&trial=true`;
        }
      } catch (err) {
        alert(err?.response?.data?.detail || 'Could not create the organization. Please try again.');
        setCreatingOrg(false);
      }
    }, [newOrgName]);

    const handleManageOrgBilling = useCallback(async (orgId) => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/organizations/${orgId}/billing-portal`,
          { origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = res.data.portal_url;
      } catch (err) {
        alert(err?.response?.data?.detail || 'Could not open billing portal.');
      }
    }, []);

    // Fetch billing history when subscription tab is active
    useEffect(() => {
      const fetchBillingData = async () => {
        if (activeTab !== 'subscription') return;
        
        console.log('🔍 Subscription tab active, checking user data...');
        console.log('📊 User stripe_subscription_id:', user?.stripe_subscription_id);
        console.log('📊 User stripe_customer_id:', user?.stripe_customer_id);
        
        // Always fetch, even for free users (will get empty response)
        try {
          console.log('🔍 Fetching subscription data...');
          setBillingLoading(true);
          const token = localStorage.getItem('token');
          
          // Fetch subscription summary
          const summaryRes = await axios.get(`${API}/payments/subscription-summary`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('📊 Summary response status:', summaryRes.status);
          console.log('📊 Summary data:', summaryRes.data);
          setSubscriptionSummary(summaryRes.data);
          
          // Fetch billing history
          const invoicesRes = await axios.get(`${API}/payments/invoices`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('📧 Invoices response status:', invoicesRes.status);
          console.log('📧 Invoices data:', invoicesRes.data);
          setBillingHistory(invoicesRes.data);
        } catch (error) {
          console.error('❌ Error fetching billing data:', error);
          console.error('❌ Error details:', error.response?.data);
        } finally {
          setBillingLoading(false);
        }
      };

      fetchBillingData();
    }, [activeTab, user]);


    // Translation helper that falls back to a sensible English default when
    // the key hasn't been added to LanguageContext yet (instead of rendering
    // the raw key like `profileHubSubtitle` on screen).
    const tWithFallback = (key, fallback) => {
      const translated = t(key);
      return translated && translated !== key ? translated : fallback;
    };

    const tabs = [
      { id: 'profile', label: t('profile'), icon: '👤', subtitle: tWithFallback('profileHubSubtitle', 'Name, email, and password') },
      { id: 'preferences', label: t('preferences'), icon: '⚙️', subtitle: tWithFallback('preferencesHubSubtitle', 'Language, theme, reminders & digests') },
      { id: 'categories', label: t('categories'), icon: '🏷️', subtitle: tWithFallback('categoriesHubSubtitle', 'Customize your task categories') },
      { id: 'cloud', label: tWithFallback('connectedClouds', 'Connected Clouds'), icon: '☁️', subtitle: tWithFallback('connectedCloudsHubSubtitle', 'Store task files in your own Google Drive') },
      { id: 'subscription', label: t('subscription'), icon: '💳', subtitle: tWithFallback('subscriptionHubSubtitle', 'Plan, billing, and invoices') },
      { id: 'support', label: t('support'), icon: '💬', subtitle: tWithFallback('supportHubSubtitle', 'Help center, feedback, legal') },
      // Archives lives at its own /archives route — clicking this row
      // navigates away from /settings rather than activating a tab.
      { id: 'archives', label: tWithFallback('archives', 'Archives'), icon: '📦', subtitle: tWithFallback('archivedTasks', 'Archived tasks'), route: '/archives' }
    ];

    const handleProfileUpdate = async (e) => {
      e.preventDefault();
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        alert('Profile updated successfully!');
      } catch (error) {
        alert('Failed to update profile');
      }
    };

    const handlePasswordChange = async (e) => {
      e.preventDefault();
      if (profileData.newPassword !== profileData.confirmPassword) {
        alert('New passwords do not match');
        return;
      }
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setShowPasswordChange(false);
        setProfileData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
        alert('Password changed successfully!');
      } catch (error) {
        alert('Failed to change password');
      }
    };

    // Removed old push notification service code
    // New reminder system handles reminders via backend (email/SMS)

    // Load preferences from backend and localStorage on component mount
    useEffect(() => {
      // Fetch user preferences from backend
      const fetchUserPreferences = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          const userData = response.data;
          
          // Update preferences with backend data
          setPreferences(prev => ({
            ...prev,
            taskReminderMethod: userData.task_reminder_method || 'email',
            taskReminderPhone: userData.task_reminder_phone || '',
            weeklyDigest: userData.weekly_digest_enabled || false,
            weeklyDigestDay: userData.weekly_digest_day || 'monday',
            weeklyDigestTime: userData.weekly_digest_time || '08:00',
            dailyDigest: userData.daily_digest_enabled || false,
            dailyDigestTime: userData.daily_digest_time || '20:00'
          }));
          
          console.log('✅ User preferences loaded from backend:', {
            phone: userData.task_reminder_phone,
            method: userData.task_reminder_method
          });
          
        } catch (error) {
          console.error('Error fetching user preferences:', error);
          
          // Fallback to localStorage if backend fails
          const savedPreferences = localStorage.getItem('edotask_preferences');
          if (savedPreferences) {
            try {
              const parsed = JSON.parse(savedPreferences);
              setPreferences(prev => ({ ...prev, ...parsed }));
            } catch (error) {
              console.error('Error loading saved preferences:', error);
            }
          }
        }
      };
      
      fetchUserPreferences();
    }, []);

    const handlePreferenceChange = async (key, value) => {
      if (key === 'taskReminderMethod') {
        // Task Reminder Method (Email or SMS)
        try {
          await axios.put(`${BACKEND_URL}/api/auth/preferences`, {
            task_reminder_method: value
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          setPreferences(prev => ({ ...prev, [key]: value }));
          localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
          alert(`Task reminder method updated to ${value.toUpperCase()}. Reminders will be sent 1 hour before tasks with reminder enabled.`);
        } catch (error) {
          console.error('Failed to update task reminder method:', error);
          alert('Failed to update reminder method. Please try again.');
          return;
        }
      } else if (key === 'taskReminderPhone') {
        // Task Reminder Phone Number
        try {
          await axios.put(`${BACKEND_URL}/api/auth/preferences`, {
            task_reminder_phone: value
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          setPreferences(prev => ({ ...prev, [key]: value }));
          localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
          alert('Phone number updated for SMS reminders!');
        } catch (error) {
          console.error('Failed to update phone number:', error);
          alert(error.response?.data?.detail || 'Failed to update phone number. Please use US format: +1XXXXXXXXXX or XXXXXXXXXX');
          return;
        }
      } else if (key === 'weeklyDigest') {
        try {
          // Get user's timezone offset in minutes
          const timezoneOffset = -new Date().getTimezoneOffset();
          
          await axios.put(`${BACKEND_URL}/api/auth/preferences`, {
            weekly_digest_enabled: value,
            timezone_offset: timezoneOffset
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          setPreferences(prev => ({ ...prev, [key]: value }));
          localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
          
          if (value) {
            const day = preferences.weeklyDigestDay || 'Monday';
            const time = preferences.weeklyDigestTime || '08:00';
            alert(`Weekly digest enabled! You will receive a summary every ${day.charAt(0).toUpperCase() + day.slice(1)} at ${time} (your local time).`);
          } else {
            alert('Weekly digest disabled.');
          }
        } catch (error) {
          console.error('Failed to update weekly digest:', error);
          alert('Failed to update weekly digest. Please try again.');
          return;
        }
      } else if (key === 'weeklyDigestDay' || key === 'weeklyDigestTime') {
        try {
          // Get user's timezone offset in minutes
          const timezoneOffset = -new Date().getTimezoneOffset();
          
          const updateData = {
            timezone_offset: timezoneOffset
          };
          
          if (key === 'weeklyDigestDay') {
            updateData.weekly_digest_day = value;
          } else {
            updateData.weekly_digest_time = value;
          }
          
          await axios.put(`${BACKEND_URL}/api/auth/preferences`, updateData, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          setPreferences(prev => ({ ...prev, [key]: value }));
          localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
          
          const day = key === 'weeklyDigestDay' ? value : (preferences.weeklyDigestDay || 'monday');
          const time = key === 'weeklyDigestTime' ? value : (preferences.weeklyDigestTime || '08:00');
          alert(`Weekly digest schedule updated! You will receive emails every ${day.charAt(0).toUpperCase() + day.slice(1)} at ${time} (your local time).`);
        } catch (error) {
          console.error('Failed to update weekly digest schedule:', error);
          alert('Failed to update schedule. Please try again.');
          return;
        }
      } else if (key === 'dailyDigest') {
        try {
          console.log('🔧 DEBUG: Attempting to update daily digest...');
          console.log('🔧 Backend URL:', BACKEND_URL);
          console.log('🔧 Token exists:', !!localStorage.getItem('token'));
          
          // Get user's timezone offset in minutes
          const timezoneOffset = -new Date().getTimezoneOffset(); // Negative because getTimezoneOffset returns opposite
          console.log('🔧 User timezone offset:', timezoneOffset, 'minutes from UTC');
          
          const payload = {
            daily_digest_enabled: value,
            timezone_offset: timezoneOffset
          };
          console.log('🔧 Payload:', payload);
          
          const response = await axios.put(`${BACKEND_URL}/api/auth/preferences`, payload, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          console.log('✅ Daily digest update response:', response.data);
          
          setPreferences(prev => ({ ...prev, [key]: value }));
          localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
          
          if (value) {
            const time = preferences.dailyDigestTime || '08:00';
            alert(`Daily digest enabled! You will receive a summary every day at ${time} (your local time).`);
          } else {
            alert('Daily digest disabled.');
          }
        } catch (error) {
          console.error('❌ Failed to update daily digest:', error);
          console.error('❌ Error response:', error.response?.data);
          console.error('❌ Error status:', error.response?.status);
          alert(`Failed to update daily digest: ${error.response?.data?.detail || error.message}. Please try again.`);
          return;
        }
      } else if (key === 'dailyDigestTime') {
        try {
          console.log('🔧 DEBUG: Attempting to update daily digest time...');
          
          // Get user's timezone offset in minutes
          const timezoneOffset = -new Date().getTimezoneOffset();
          
          const payload = {
            daily_digest_time: value,
            timezone_offset: timezoneOffset
          };
          console.log('🔧 Payload:', payload);
          
          const response = await axios.put(`${BACKEND_URL}/api/auth/preferences`, payload, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          console.log('✅ Daily digest time update response:', response.data);
          
          setPreferences(prev => ({ ...prev, [key]: value }));
          localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
          
          alert(`Daily digest time updated! You will receive emails daily at ${value} (your local time).`);
        } catch (error) {
          console.error('❌ Failed to update daily digest time:', error);
          console.error('❌ Error response:', error.response?.data);
          alert(`Failed to update time: ${error.response?.data?.detail || error.message}. Please try again.`);
          return;
        }
      } else if (key === 'darkMode') {
        // Toggle dark mode
        if (value) {
          document.documentElement.classList.add('dark');
          localStorage.setItem('theme', 'dark');
        } else {
          document.documentElement.classList.remove('dark');
          localStorage.setItem('theme', 'light');
        }
        setPreferences(prev => ({ ...prev, [key]: value }));
        localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
      } else if (key === 'language') {
        // Handle language change through language context
        changeLanguage(value);
        setPreferences(prev => ({ ...prev, [key]: value }));
        // Save other preferences to localStorage
        localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
      } else {
        setPreferences(prev => ({ ...prev, [key]: value }));
        // Save other preferences to localStorage
        localStorage.setItem('edotask_preferences', JSON.stringify({ ...preferences, [key]: value }));
      }
    };

    // Title shown in the mobile layout header — section name when inside a
    // section, the generic "Settings" when on the hub.
    const activeTabMeta = tabs.find((tt) => tt.id === activeTab);
    const headerTitle = activeTabMeta ? activeTabMeta.label : tWithFallback('settingsTitle', 'Settings');
    const headerSubtitle = activeTabMeta
      ? activeTabMeta.subtitle
      : tWithFallback('settingsHubSubtitle', 'Manage your account');

    return (
      <MobileLayout title={headerTitle} subtitle={headerSubtitle}>
        {/* Desktop (lg+): permanent 2-column with hub list on the left + the
            active section on the right. Mobile keeps the existing
            "hub-OR-section" flow so a tap drills into the section. */}
        <div className="lg:grid lg:grid-cols-[260px,1fr] lg:gap-6 lg:items-start">

          {/* Hub list — always visible on desktop, only on `/settings` (no
              section) on mobile. */}
          <aside className={`${activeTab ? 'hidden lg:block' : 'block'} lg:sticky lg:top-6`}>
            <div
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden divide-y divide-gray-100"
              data-testid="settings-hub-list"
            >
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (tab.route) {
                        navigate(tab.route);
                      } else {
                        setActiveTab(tab.id);
                      }
                    }}
                    data-testid={`settings-hub-row-${tab.id}`}
                    className={`w-full flex items-center gap-4 px-4 py-4 text-left transition-colors ${
                      active
                        ? 'lg:bg-blue-50 lg:text-blue-700 hover:bg-gray-50'
                        : 'hover:bg-gray-50 active:bg-gray-100'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex-shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center text-xl ${
                        active
                          ? 'lg:bg-blue-100 lg:text-blue-700 bg-gradient-to-br from-blue-50 to-indigo-100'
                          : 'bg-gradient-to-br from-blue-50 to-indigo-100'
                      }`}
                    >
                      {tab.icon}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-base font-semibold text-gray-900 truncate font-display">
                        {tab.label}
                      </span>
                      <span className="block text-sm text-gray-500 truncate">{tab.subtitle}</span>
                    </span>
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0 lg:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}

              {/* Sign Out as the final row — mobile-only. On desktop (lg+),
                  the persistent left sidebar already exposes "Sign out" at its
                  bottom, so we hide this duplicate to avoid two red buttons. */}
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('signOutConfirm'))) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                  }
                }}
                data-testid="settings-hub-row-signout"
                className="lg:hidden w-full flex items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-red-50 active:bg-red-100 text-red-600 border-t border-gray-100 mt-2"
              >
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center text-xl bg-red-50"
                >
                  🚪
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-base font-semibold truncate font-display">
                    {t('signOut')}
                  </span>
                  <span className="block text-sm text-red-400 truncate">
                    {tWithFallback('signOutSubtitle', 'End your session on this device')}
                  </span>
                </span>
              </button>
            </div>
          </aside>

          {/* Section content */}
          <div className={`min-w-0 ${activeTab ? 'block' : 'hidden lg:block'}`}>

            {/* Mobile back-to-hub link — only shown on mobile when in a section. */}
            {activeTab && (
              <button
                onClick={() => setActiveTab(null)}
                data-testid="settings-back-to-hub"
                className="lg:hidden mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                {t('settings') || 'Settings'}
              </button>
            )}

            {/* Desktop empty-section placeholder shown when no tab is selected. */}
            {!activeTab && (
              <div
                className="hidden lg:flex items-center justify-center bg-white rounded-2xl border border-gray-200 px-8 py-16 text-center"
                data-testid="settings-desktop-placeholder"
              >
                <div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center mx-auto mb-3 text-2xl">
                    ⚙️
                  </div>
                  <h3 className="font-display text-lg font-semibold text-gray-900 mb-1">
                    {tWithFallback('pickASection', 'Pick a section')}
                  </h3>
                  <p className="text-sm text-gray-500 max-w-xs">
                    {tWithFallback('chooseACategoryHelp', 'Choose a category on the left to manage your account.')}
                  </p>
                </div>
              </div>
            )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('basicInformation')}</h3>
              
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('name')}</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">{t('email')}</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
                >
                  {t('updateProfile')}
                </button>
              </form>
            </div>

            {/* Password Change */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('password')}</h3>
                <button
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  {showPasswordChange ? t('cancel') : t('change')}
                </button>
              </div>

              {showPasswordChange && (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Current Password</label>
                    <input
                      type="password"
                      value={profileData.currentPassword}
                      onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">New Password</label>
                    <input
                      type="password"
                      value={profileData.newPassword}
                      onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      minLength="6"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      value={profileData.confirmPassword}
                      onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      minLength="6"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
                  >
                    Change Password
                  </button>
                </form>
              )}
            </div>

            {/* Danger Zone — self-service account deletion. Rendered ONLY on
                the Profile tab, at the bottom, with red framing to visually
                separate irreversible actions from everyday settings. */}
            <div className="bg-white rounded-2xl border-2 border-red-200 p-4 shadow-sm" data-testid="settings-danger-zone">
              <h3 className="text-lg font-semibold text-red-700 mb-1">
                {tWithFallback('dangerZone', 'Danger Zone')}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {tWithFallback('dangerZoneSubtitle', 'Irreversible actions for this account.')}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {tWithFallback('deleteAccount', 'Delete account')}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                    {tWithFallback('deleteAccountHint', 'Permanently remove your account, tasks, contacts and settings.')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteAccount(true)}
                  data-testid="open-delete-account-modal"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100 font-medium transition-colors whitespace-nowrap"
                >
                  {tWithFallback('deleteAccount', 'Delete account')}
                </button>
              </div>
            </div>

            <DeleteAccountModal
              isOpen={showDeleteAccount}
              onClose={() => setShowDeleteAccount(false)}
            />
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('preferences')}</h3>
            
            <div className="space-y-4">
              {/* Task Reminders (Email or SMS) — mobile only */}
              {!isDesktop && (
              <div className="py-3 border-b border-gray-200" data-testid="settings-task-reminders">
                <div className="mb-3">
                  <h4 className="font-medium text-gray-700">{t('taskReminders') || 'Task Reminders'}</h4>
                  <p className="text-sm text-gray-500">{t('taskRemindersDesc') || 'Receive reminders 1 hour before tasks with reminder enabled'}</p>
                </div>
                
                <div className="space-y-3">
                  {/* Reminder Method Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reminderMethod') || 'Reminder Method'}
                    </label>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="reminderMethod"
                          value="email"
                          checked={preferences.taskReminderMethod === 'email'}
                          onChange={(e) => handlePreferenceChange('taskReminderMethod', e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">📧 {t('email') || 'Email'}</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="reminderMethod"
                          value="notification"
                          checked={preferences.taskReminderMethod === 'notification'}
                          onChange={(e) => handlePreferenceChange('taskReminderMethod', e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">🔔 {t('pushNotification')}</span>
                      </label>
                      {/* SMS option — delivered via Textbelt (reuses the Message-contact SMS integration) */}
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="reminderMethod"
                          value="sms"
                          checked={preferences.taskReminderMethod === 'sms'}
                          onChange={(e) => handlePreferenceChange('taskReminderMethod', e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">📱 {t('sms') || 'SMS'}</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Browser Notification Permission (only show if notification selected) */}
                  {preferences.taskReminderMethod === 'notification' && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-sm text-gray-700 mb-2">
                        🔔 {t('browserNotificationPermission')}
                      </p>
                      <button
                        onClick={async () => {
                          try {
                            if (!("Notification" in window) || typeof Notification === 'undefined') {
                              alert("❌ This browser doesn't support notifications. Please use Chrome, Firefox, Safari, or Edge.");
                              return;
                            }
                            
                            const currentPermission = Notification.permission;
                            
                            if (currentPermission === "granted") {
                              alert("✅ Notifications are already enabled!");
                              try {
                                // Test notification
                                new Notification("Test Notification", {
                                  body: "Task reminders will appear like this!",
                                  icon: "/logo192.png"
                                });
                              } catch (e) {
                                console.error("Error showing test notification:", e);
                                alert("⚠️ Notifications are enabled but couldn't show test. This may work in production.");
                              }
                            } else if (currentPermission !== "denied") {
                              try {
                                const permission = await Notification.requestPermission();
                                if (permission === "granted") {
                                  alert("✅ Notifications enabled successfully!");
                                  try {
                                    // Test notification
                                    new Notification("Test Notification", {
                                      body: "Task reminders will appear like this!",
                                      icon: "/logo192.png"
                                    });
                                  } catch (e) {
                                    console.error("Error showing test notification:", e);
                                  }
                                } else {
                                  alert("❌ Notification permission denied. Please enable it in your browser settings.");
                                }
                              } catch (e) {
                                console.error("Error requesting permission:", e);
                                alert("⚠️ Could not request notification permission. This feature may not work in preview mode but will work in production.");
                              }
                            } else {
                              alert("❌ Notifications are blocked. Please enable them in your browser settings.");
                            }
                          } catch (error) {
                            console.error("Notification error:", error);
                            alert("⚠️ Notification feature may not be available in preview mode. It will work in production (HTTPS).");
                          }
                        }}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm"
                      >
                        {typeof Notification !== 'undefined' && Notification?.permission === "granted" ? t('enabledSendTest') : t('enableNotifications')}
                      </button>
                      <p className="text-xs text-gray-600 mt-2">
                        {t('statusLabel')} <span className="font-medium">
                          {typeof Notification === 'undefined' || !("Notification" in window) ? t('statusNotAvailablePreview') :
                           Notification.permission === "granted" ? t('statusEnabled') :
                           Notification.permission === "denied" ? t('statusBlocked') : t('statusNotEnabled')}
                        </span>
                      </p>
                      {(typeof Notification === 'undefined' || !("Notification" in window)) && (
                        <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded">
                          ℹ️ Notifications may not work in preview mode but will work in production on HTTPS.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Phone Number Input (only show if SMS selected) */}
                  {preferences.taskReminderMethod === 'sms' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('phoneNumber') || 'Phone Number'}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={preferences.taskReminderPhone || ''}
                          onChange={(e) => setPreferences(prev => ({ ...prev, taskReminderPhone: e.target.value }))}
                          placeholder="+1 555 123 4567"
                          className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <button
                          onClick={() => {
                            const phoneValue = preferences.taskReminderPhone?.trim() || '';
                            console.log('💾 Save button clicked with value:', phoneValue);
                            handlePreferenceChange('taskReminderPhone', phoneValue);
                          }}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                        >
                          {t('save') || 'Save'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{t('intlPhoneFormatHint') || 'Include country code (e.g. +1 for US, +33 for France). Bare 10-digit numbers are treated as US.'}</p>
                    </div>
                  )}
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <p className="text-xs text-blue-800">
                      ℹ️ {t('reminderInfo') || 'To receive reminders, check the "Reminder" box when creating or editing a task. Reminders are sent 1 hour before the due time.'}
                    </p>
                  </div>
                </div>
              </div>
              )}

              {/* Weekly Email Digest - PRO ONLY */}
              <div className="py-3 border-b border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-700">{t('weeklyDigest') || 'Weekly Email Digest'}</h4>
                      {!hasProAccess(user) && <ProBadge size="sm" />}
                    </div>
                    <p className="text-sm text-gray-500">{t('weeklyDigestDesc') || 'Overdue tasks from past week + upcoming tasks'}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.weeklyDigest}
                      onChange={(e) => {
                        if (!hasProAccess(user)) {
                          alert('Weekly email digest is a Pro feature. Upgrade to Pro to enable this feature.');
                          return;
                        }
                        handlePreferenceChange('weeklyDigest', e.target.checked);
                      }}
                      disabled={!hasProAccess(user)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>
                
                {preferences.weeklyDigest && (
                  <div className="mt-3 pl-4 space-y-3 bg-blue-50 p-3 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('digestDay') || 'Day'}
                      </label>
                      <select
                        value={preferences.weeklyDigestDay || 'monday'}
                        onChange={(e) => handlePreferenceChange('weeklyDigestDay', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="monday">{t('monday') || 'Monday'}</option>
                        <option value="tuesday">{t('tuesday') || 'Tuesday'}</option>
                        <option value="wednesday">{t('wednesday') || 'Wednesday'}</option>
                        <option value="thursday">{t('thursday') || 'Thursday'}</option>
                        <option value="friday">{t('friday') || 'Friday'}</option>
                        <option value="saturday">{t('saturday') || 'Saturday'}</option>
                        <option value="sunday">{t('sunday') || 'Sunday'}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('digestTime') || 'Time'}
                      </label>
                      <select
                        value={preferences.weeklyDigestTime || '08:00'}
                        onChange={(e) => handlePreferenceChange('weeklyDigestTime', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="00:00">12:00 AM</option>
                        <option value="01:00">1:00 AM</option>
                        <option value="02:00">2:00 AM</option>
                        <option value="03:00">3:00 AM</option>
                        <option value="04:00">4:00 AM</option>
                        <option value="05:00">5:00 AM</option>
                        <option value="06:00">6:00 AM</option>
                        <option value="07:00">7:00 AM</option>
                        <option value="08:00">8:00 AM</option>
                        <option value="09:00">9:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="13:00">1:00 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="16:00">4:00 PM</option>
                        <option value="17:00">5:00 PM</option>
                        <option value="18:00">6:00 PM</option>
                        <option value="19:00">7:00 PM</option>
                        <option value="20:00">8:00 PM</option>
                        <option value="21:00">9:00 PM</option>
                        <option value="22:00">10:00 PM</option>
                        <option value="23:00">11:00 PM</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Sent every week on selected day at this time (your local time)</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Daily Email Digest - PRO ONLY */}
              <div className="py-3 border-b border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-700">{t('dailyDigest') || 'Daily Email Digest'}</h4>
                      {!hasProAccess(user) && <ProBadge size="sm" />}
                    </div>
                    <p className="text-sm text-gray-500">{t('dailyDigestDesc') || 'Overdue today + all tasks for tomorrow'}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.dailyDigest}
                      onChange={(e) => {
                        if (!hasProAccess(user)) {
                          alert('Daily email digest is a Pro feature. Upgrade to Pro to enable this feature.');
                          return;
                        }
                        handlePreferenceChange('dailyDigest', e.target.checked);
                      }}
                      disabled={!hasProAccess(user)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>
                
                {preferences.dailyDigest && (
                  <div className="mt-3 pl-4 space-y-3 bg-green-50 p-3 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('digestTime') || 'Time (5 PM - 11 PM)'}
                      </label>
                      <select
                        value={preferences.dailyDigestTime || '20:00'}
                        onChange={(e) => handlePreferenceChange('dailyDigestTime', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="17:00">5:00 PM</option>
                        <option value="18:00">6:00 PM</option>
                        <option value="19:00">7:00 PM</option>
                        <option value="20:00">8:00 PM</option>
                        <option value="21:00">9:00 PM</option>
                        <option value="22:00">10:00 PM</option>
                        <option value="23:00">11:00 PM</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">{t('dailyDigestHint') || 'Sent every day at this time (your local time)'}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Dark Mode */}
              <div className="flex items-center justify-between py-3 border-b border-gray-200">
                <div>
                  <h4 className="font-medium text-gray-700">{t('darkMode')}</h4>
                  <p className="text-sm text-gray-500">{t('darkModeDesc')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.darkMode}
                    onChange={(e) => handlePreferenceChange('darkMode', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Language */}
              <div className="py-3">
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('language')}</label>
                <select
                  value={currentLanguage}
                  onChange={(e) => handlePreferenceChange('language', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {getAvailableLanguages().map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Privacy & Data — local-only reset for cached app data */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('privacyDataTitle')}</h3>
              <p className="text-sm text-gray-500 mb-3">
                {t('privacyDataDesc')}
              </p>
              <ResetAppDataButton />
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <CategoriesTabContent 
            categories={categories}
            createCategory={createCategory}
            updateCategory={updateCategory}
            deleteCategory={deleteCategory}
          />
        )}

        {/* Connected Clouds Tab */}
        {activeTab === 'cloud' && (
          <ConnectedCloudsPage />
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-4">
            {/* Current Plan */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('currentPlan')}</h3>
              
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-gray-700">
                    {user?.subscription_status === 'pro' ? t('proPlan') || 'Pro Plan' : t('freePlan')}
                  </h4>
                  <p className="text-sm text-green-600">{t('statusActive')}</p>
                </div>
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {t('active')}
                </div>
              </div>

              {/* Show upgrade button only for free users */}
              {user?.subscription_status !== 'pro' && (
                <button
                  onClick={() => navigate('/payment')}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
                >
                  {t('upgradeToPro')}
                </button>
              )}

              {/* Show Pro badge for Pro users */}
              {user?.subscription_status === 'pro' && (
                <div className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-medium text-center">
                  <span className="text-lg">👑</span> {t('youAreProUser')}
                </div>
              )}
            </div>

            {/* Business plan card — only shown when the `business_workspaces`
                feature flag is enabled for this user. Two states:
                  • No org yet — primary CTA opens the create-org modal which
                    creates the workspace and redirects to Stripe Checkout.
                  • At least one org — show the workspace(s) with quick links
                    to the team area and (Owner-only) the billing portal. */}
            {featureFlags.business_workspaces && (
              <div
                className="bg-white rounded-2xl p-4 shadow-sm border-2 border-indigo-100"
                data-testid="business-plan-card"
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="flex-shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-xl">
                    🏢
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">Business</h3>
                    <p className="text-sm text-gray-600">
                      Share tasks, contacts, and categories with your team.
                      $10/month includes you + 1 teammate · $5/month per extra teammate · 14-day free trial · cancel anytime.
                    </p>
                  </div>
                </div>

                {myOrgs.length === 0 ? (
                  <button
                    onClick={() => { setNewOrgName(''); setShowCreateOrgModal(true); }}
                    data-testid="create-business-cta"
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-indigo-600 hover:to-purple-700 transition-all"
                  >
                    Create a Business workspace
                  </button>
                ) : (
                  <>
                    <div className="space-y-2">
                      {myOrgs.map((org) => (
                        <div
                          key={org.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          data-testid={`org-row-${org.id}`}
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{org.name}</p>
                            <p className="text-xs text-gray-500 capitalize">{org.subscription_status} · {org.seat_count} seat{org.seat_count === 1 ? '' : 's'} · ~${Math.max(2, org.seat_count || 1) * 5}/mo</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => navigate(`/organization/members?org_id=${org.id}`)}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Open
                            </button>
                            {org.owner_user_id === user?.id && org.stripe_customer_id && (
                              <button
                                onClick={() => handleManageOrgBilling(org.id)}
                                className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                              >
                                Billing
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => { setNewOrgName(''); setShowCreateOrgModal(true); }}
                      data-testid="add-another-business-cta"
                      className="mt-3 w-full border border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50 py-2.5 rounded-lg font-medium transition-colors"
                    >
                      + Add another Business workspace
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Each workspace has its own seats, members, and Stripe subscription.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Subscription Management for Pro Users */}
            {user?.subscription_status === 'pro' && user?.stripe_subscription_id && (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionManagement') || 'Subscription Management'}</h3>
                
                <div className="space-y-3">
                  {/* Billing Info */}
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t('billingCycle') || 'Billing Cycle'}</span>
                    <span className="font-medium text-gray-900">{t('monthly') || 'Monthly'}</span>
                  </div>
                  
                  {subscriptionSummary?.next_renewal_date ? (
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">{t('nextBillingDate') || 'Next Billing Date'}</span>
                      <span className="font-medium text-gray-900">
                        {subscriptionSummary.next_renewal_date}
                      </span>
                    </div>
                  ) : user?.subscription_current_period_end && (
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">{t('nextBillingDate') || 'Next Billing Date'}</span>
                      <span className="font-medium text-gray-900">
                        {new Date(user.subscription_current_period_end).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {/* Cancellation Status */}
                  {user?.subscription_cancel_at_period_end ? (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center mb-2">
                        <span className="text-yellow-600 mr-2">⚠️</span>
                        <span className="font-medium text-yellow-900">
                          {t('subscriptionCancelScheduled') || 'Subscription Cancellation Scheduled'}
                        </span>
                      </div>
                      <p className="text-sm text-yellow-700 mb-3">
                        {t('accessUntil') || 'You will have access until'} {new Date(user.subscription_current_period_end).toLocaleDateString()}
                      </p>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${API}/subscription/reactivate`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            alert(t('subscriptionReactivated') || 'Subscription reactivated successfully!');
                            window.location.reload();
                          } catch (error) {
                            console.error('Reactivation error:', error);
                            alert(error.response?.data?.detail || 'Failed to reactivate subscription');
                          }
                        }}
                        className="w-full bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                      >
                        {t('reactivateSubscription') || 'Reactivate Subscription'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        if (window.confirm(t('confirmCancelSubscription') || 'Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
                          try {
                            const token = localStorage.getItem('token');
                            const response = await axios.post(`${API}/subscription/cancel`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            alert(response.data.message);
                            window.location.reload();
                          } catch (error) {
                            console.error('Cancellation error:', error);
                            alert(error.response?.data?.detail || 'Failed to cancel subscription');
                          }
                        }
                      }}
                      className="w-full bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
                    >
                      {t('cancelSubscription') || 'Cancel Subscription'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Usage Stats */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('usageStatistics')}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('tasksCreated')}</span>
                  <span className="font-medium">
                    {user?.usage_stats?.active_tasks || 0} / {user?.subscription_status === 'pro' ? '∞' : '20'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('contacts')}</span>
                  <span className="font-medium">
                    {user?.usage_stats?.total_contacts || 0} / {user?.subscription_status === 'pro' ? '∞' : '3'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('storageUsed')}</span>
                  <span className="font-medium">
                    {user?.usage_stats?.storage_used_mb || 0} MB / {user?.subscription_status === 'pro' ? '2048 MB' : '500 MB'}
                  </span>
                </div>
              </div>
            </div>

            {/* Billing History */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('billingHistory') || 'Billing History'}</h3>
              
              {billingLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : billingHistory.length > 0 ? (
                <div className="space-y-2">
                  {billingHistory.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          ${invoice.amount.toFixed(2)} {invoice.currency}
                        </div>
                        <div className="text-sm text-gray-500">{invoice.date}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'Paid' 
                            ? 'bg-green-100 text-green-800'
                            : invoice.status === 'Failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status}
                        </span>
                        {invoice.invoice_pdf && (
                          <a
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {t('download') || 'Download'}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📄</div>
                  <p className="text-gray-500">{t('noBillingHistory') || 'No billing history available'}</p>
                  {user?.subscription_status !== 'pro' && (
                    <p className="text-sm text-gray-400">{t('upgradeToSeeBilling') || 'Upgrade to Pro to see your billing history'}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <div className="space-y-4">
            {/* Help & Support */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('helpSupport')}</h3>
              
              {/* AI Help Assistant */}
              <div className="mb-4">
                <button 
                  onClick={() => setShowAIHelp(true)}
                  className="w-full text-left p-3 rounded-lg border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-xl mr-3">🤖</span>
                      <div>
                        <h4 className="font-medium text-gray-900">{t('aiHelpAssistant')}</h4>
                        <p className="text-sm text-gray-600">{t('askMeAnything')}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">{t('voiceEnabled')}</span>
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <button 
                    onClick={() => toggleSupportSection('helpCenter')}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-xl mr-3">📖</span>
                        <div>
                          <h4 className="font-medium text-gray-900">{t('helpCenter')}</h4>
                          <p className="text-sm text-gray-500">{t('browseFrequentlyAsked')}</p>
                        </div>
                      </div>
                      <svg className={`w-5 h-5 text-gray-400 transition-transform ${supportSections.helpCenter ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                  {supportSections.helpCenter && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                      <div className="text-sm text-gray-700">
                        {formatContent(supportContent.helpCenterContent)}
                      </div>
                      <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-200">
                        For additional support, contact us at {supportContent.supportEmail}
                      </p>
                    </div>
                  )}
                </div>

                <a 
                  href={`mailto:${supportContent.supportEmail}`}
                  className="block w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-xl mr-3">📧</span>
                      <div>
                        <h4 className="font-medium text-gray-900">{t('contactSupport')}</h4>
                        <p className="text-sm text-gray-500">{supportContent.supportEmail}</p>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                </a>

                <div>
                  <button 
                    onClick={() => toggleSupportSection('sendFeedback')}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-xl mr-3">💬</span>
                        <div>
                          <h4 className="font-medium text-gray-900">{t('sendFeedback')}</h4>
                          <p className="text-sm text-gray-500">{t('helpUsImprove')}</p>
                        </div>
                      </div>
                      <svg className={`w-5 h-5 text-gray-400 transition-transform ${supportSections.sendFeedback ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                  {supportSections.sendFeedback && (
                    <div className="mt-2 p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>We'd love to hear from you!</strong>
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        Your feedback helps us make Edotask better. Please share:
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Feature requests or suggestions</li>
                        <li>• Bug reports or issues</li>
                        <li>• General feedback about your experience</li>
                        <li>• Ideas for improvement</li>
                      </ul>
                      <p className="text-xs text-gray-500 mt-2">
                        Send your feedback to: <a href={`mailto:${supportContent.feedbackEmail}`} className="text-blue-600 hover:underline">{supportContent.feedbackEmail}</a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* App Information */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('appInformation')}</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('version')}</span>
                  <span className="font-medium">{supportContent.appVersion}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('lastUpdated')}</span>
                  <span className="font-medium">{t('appLastUpdated')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('status')}</span>
                  <span className="text-green-600 font-medium">{t('allSystemsOperational')}</span>
                </div>
              </div>
            </div>

            {/* Privacy & Terms */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('legal')}</h3>
              
              <div className="space-y-2">
                <div>
                  <button 
                    onClick={() => toggleSupportSection('privacyPolicy')}
                    className="w-full text-left text-blue-600 hover:text-blue-800 font-medium py-1"
                  >
                    {t('privacyPolicy')}
                  </button>
                  {supportSections.privacyPolicy && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <div className="text-sm text-gray-700">
                        {formatContent(supportContent.privacyPolicyContent)}
                      </div>
                      <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-blue-200">
                        Last updated: {supportContent.lastUpdated}
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <button 
                    onClick={() => toggleSupportSection('termsOfService')}
                    className="w-full text-left text-blue-600 hover:text-blue-800 font-medium py-1"
                  >
                    {t('termsOfService')}
                  </button>
                  {supportSections.termsOfService && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <div className="text-sm text-gray-700">
                        {formatContent(supportContent.termsOfServiceContent)}
                      </div>
                      <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-blue-200">
                        Last updated: {supportContent.lastUpdated}
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <button 
                    onClick={() => toggleSupportSection('cookiePolicy')}
                    className="w-full text-left text-blue-600 hover:text-blue-800 font-medium py-1"
                  >
                    {t('cookiePolicy')}
                  </button>
                  {supportSections.cookiePolicy && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <div className="text-sm text-gray-700">
                        {formatContent(supportContent.cookiePolicyContent)}
                      </div>
                      <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-blue-200">
                        Last updated: {supportContent.lastUpdated}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Administration Access — admin/manager only, and only inside the
            Profile section so it isn't repeated on every settings tab. */}
        {(user?.role === 'admin' || user?.role === 'manager') && activeTab === 'profile' && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-4 shadow-sm mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold mb-1">{t('administrationPanel')}</h3>
                <p className="text-blue-100 text-sm">{t('systemUserControlsSubtitle')}</p>
              </div>
              <button
                onClick={() => navigate('/administration')}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center"
              >
                <span className="mr-2">🛡️</span>
                {t('adminBtn')}
              </button>
            </div>
          </div>
        )}

        {/* AI Help Assistant Modal */}
        <AIHelpAssistant 
          isOpen={showAIHelp} 
          onClose={() => setShowAIHelp(false)} 
        />

        {/* Create Business workspace — name → POST /organizations →
            POST /organizations/{id}/checkout → redirect to Stripe. If
            billing is not yet configured the user lands on the welcome
            page so they can still explore the workspace. */}
        {showCreateOrgModal && (
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4"
            data-testid="create-org-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Name your workspace</h3>
              <p className="text-sm text-gray-600 mb-4">
                This is what your teammates will see. You can change it later.
              </p>
              <input
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Acme Inc."
                autoFocus
                maxLength={64}
                disabled={creatingOrg}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newOrgName.trim().length >= 2 && !creatingOrg) {
                    handleCreateOrganization();
                  }
                }}
                data-testid="create-org-name-input"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-4"
              />
              <p className="text-xs text-gray-500 mb-4">
                You'll be redirected to a secure Stripe checkout to start your
                14-day free trial. No charge during the trial.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleCreateOrganization}
                  disabled={creatingOrg || newOrgName.trim().length < 2}
                  data-testid="create-org-submit"
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 disabled:opacity-60 disabled:cursor-not-allowed text-white py-2.5 rounded-xl font-medium transition-all"
                >
                  {creatingOrg ? 'Creating…' : 'Continue to checkout'}
                </button>
                <button
                  onClick={() => setShowCreateOrgModal(false)}
                  disabled={creatingOrg}
                  data-testid="create-org-cancel"
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-2.5 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
          </div>{/* end right column */}
        </div>{/* end lg:grid */}
      </MobileLayout>
    );
  };

  // Categories Tab Content Component
  const CategoriesTabContent = ({ categories, createCategory, updateCategory, deleteCategory }) => {
    const { t } = useLanguage();
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '', color: '#3b82f6' });
    const [submitting, setSubmitting] = useState(false);

    const colors = [
      { name: 'Blue', value: '#3b82f6' },
      { name: 'Green', value: '#10b981' },
      { name: 'Yellow', value: '#f59e0b' },
      { name: 'Red', value: '#ef4444' },
      { name: 'Purple', value: '#8b5cf6' },
      { name: 'Pink', value: '#ec4899' },
      { name: 'Indigo', value: '#6366f1' },
      { name: 'Gray', value: '#6b7280' }
    ];

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.name.trim()) return;

      setSubmitting(true);
      try {
        if (editingCategory) {
          await updateCategory(editingCategory.id, formData);
        } else {
          await createCategory(formData);
        }
        
        // Reset form
        setFormData({ name: '', color: '#3b82f6' });
        setShowAddForm(false);
        setEditingCategory(null);
      } catch (error) {
        alert('Failed to save category. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    const handleEdit = (category) => {
      setEditingCategory(category);
      setFormData({ name: category.name, color: category.color || '#3b82f6' });
      setShowAddForm(true);
    };

    const handleDelete = async (category) => {
      if (window.confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
        try {
          await deleteCategory(category.id);
        } catch (error) {
          alert('Failed to delete category. Please try again.');
        }
      }
    };

    const handleCancel = () => {
      setShowAddForm(false);
      setEditingCategory(null);
      setFormData({ name: '', color: '#3b82f6' });
    };

    return (
      <div className="space-y-4">
        {/* Add Category Form */}
        {showAddForm && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter category name"
                  required
                  maxLength="50"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {colors.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      className={`w-10 h-10 rounded-full border-2 transition-transform ${
                        formData.color === color.value 
                          ? 'border-gray-900 scale-110' 
                          : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-800 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingCategory ? 'Update' : 'Add')} Category
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories List */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('myCategories')}</h3>
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all"
              >
                {t('addCategory')}
              </button>
            )}
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">🏷️</div>
              <p className="text-sm">{t('noCategoriesYet')}</p>
              <p className="text-xs text-gray-400 mt-1">
                {t('createCategoriesDesc')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map(category => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: category.color || '#3b82f6' }}
                    />
                    <span className="font-medium text-gray-900 truncate">
                      {category.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      title="Edit category"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(category)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      title="Delete category"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage Info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 {t('tips')}</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• {t('categoriesTip1')}</p>
            <p>• {t('categoriesTip2')}</p>
            <p>• {t('categoriesTip3')}</p>
            <p>• {t('categoriesTip4')}</p>
          </div>
        </div>
      </div>
    );
  };

  // Support Content Manager Component
  const SupportContentManager = () => {
    const { supportContent, updateSupportContent } = useSupportContent();
    const [editingField, setEditingField] = useState(null);
    const [tempContent, setTempContent] = useState('');

    const handleEditStart = (field) => {
      setEditingField(field);
      setTempContent(supportContent[field]);
    };

    const handleEditSave = (field) => {
      updateSupportContent(field, tempContent);
      setEditingField(null);
      console.log(`Updated ${field}:`, tempContent);
    };

    const handleEditCancel = () => {
      setEditingField(null);
      setTempContent('');
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Support Section Content Management</h3>
          
          <div className="space-y-4">
            {/* Support Email */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Support Email</h4>
                {editingField === 'supportEmail' ? (
                  <div className="flex space-x-2">
                    <button onClick={() => handleEditSave('supportEmail')} className="text-green-600 text-sm">✅ Save</button>
                    <button onClick={handleEditCancel} className="text-red-600 text-sm">❌ Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => handleEditStart('supportEmail')} className="text-blue-600 text-sm">✏️ Edit</button>
                )}
              </div>
              {editingField === 'supportEmail' ? (
                <input
                  type="email"
                  value={tempContent}
                  onChange={(e) => setTempContent(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              ) : (
                <p className="text-gray-600">{supportContent.supportEmail}</p>
              )}
            </div>

            {/* Feedback Email */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Feedback Email</h4>
                {editingField === 'feedbackEmail' ? (
                  <div className="flex space-x-2">
                    <button onClick={() => handleEditSave('feedbackEmail')} className="text-green-600 text-sm">✅ Save</button>
                    <button onClick={handleEditCancel} className="text-red-600 text-sm">❌ Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => handleEditStart('feedbackEmail')} className="text-blue-600 text-sm">✏️ Edit</button>
                )}
              </div>
              {editingField === 'feedbackEmail' ? (
                <input
                  type="email"
                  value={tempContent}
                  onChange={(e) => setTempContent(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              ) : (
                <p className="text-gray-600">{supportContent.feedbackEmail}</p>
              )}
            </div>

            {/* Help Center Content */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Help Center Content</h4>
                {editingField === 'helpCenterContent' ? (
                  <div className="flex space-x-2">
                    <button onClick={() => handleEditSave('helpCenterContent')} className="text-green-600 text-sm">✅ Save</button>
                    <button onClick={handleEditCancel} className="text-red-600 text-sm">❌ Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => handleEditStart('helpCenterContent')} className="text-blue-600 text-sm">✏️ Edit</button>
                )}
              </div>
              {editingField === 'helpCenterContent' ? (
                <div>
                  <textarea
                    value={tempContent}
                    onChange={(e) => setTempContent(e.target.value)}
                    rows={12}
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                    placeholder="Enter help center content. Use double line breaks for paragraphs."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Formatting tip: Use double line breaks to create paragraphs. Single line breaks create line breaks within paragraphs.
                  </p>
                </div>
              ) : (
                <div className="text-gray-600 text-sm">
                  {formatContent(supportContent.helpCenterContent)}
                </div>
              )}
            </div>

            {/* Privacy Policy Content */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Privacy Policy Content</h4>
                {editingField === 'privacyPolicyContent' ? (
                  <div className="flex space-x-2">
                    <button onClick={() => handleEditSave('privacyPolicyContent')} className="text-green-600 text-sm">✅ Save</button>
                    <button onClick={handleEditCancel} className="text-red-600 text-sm">❌ Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => handleEditStart('privacyPolicyContent')} className="text-blue-600 text-sm">✏️ Edit</button>
                )}
              </div>
              {editingField === 'privacyPolicyContent' ? (
                <div>
                  <textarea
                    value={tempContent}
                    onChange={(e) => setTempContent(e.target.value)}
                    rows={10}
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                    placeholder="Enter privacy policy content. Use double line breaks for paragraphs."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Formatting tip: Use double line breaks to create paragraphs.
                  </p>
                </div>
              ) : (
                <div className="text-gray-600 text-sm">
                  {formatContent(supportContent.privacyPolicyContent)}
                </div>
              )}
            </div>

            {/* Terms of Service Content */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Terms of Service Content</h4>
                {editingField === 'termsOfServiceContent' ? (
                  <div className="flex space-x-2">
                    <button onClick={() => handleEditSave('termsOfServiceContent')} className="text-green-600 text-sm">✅ Save</button>
                    <button onClick={handleEditCancel} className="text-red-600 text-sm">❌ Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => handleEditStart('termsOfServiceContent')} className="text-blue-600 text-sm">✏️ Edit</button>
                )}
              </div>
              {editingField === 'termsOfServiceContent' ? (
                <div>
                  <textarea
                    value={tempContent}
                    onChange={(e) => setTempContent(e.target.value)}
                    rows={10}
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                    placeholder="Enter terms of service content. Use double line breaks for paragraphs."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Formatting tip: Use double line breaks to create paragraphs.
                  </p>
                </div>
              ) : (
                <div className="text-gray-600 text-sm">
                  {formatContent(supportContent.termsOfServiceContent)}
                </div>
              )}
            </div>

            {/* Cookie Policy Content */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Cookie Policy Content</h4>
                {editingField === 'cookiePolicyContent' ? (
                  <div className="flex space-x-2">
                    <button onClick={() => handleEditSave('cookiePolicyContent')} className="text-green-600 text-sm">✅ Save</button>
                    <button onClick={handleEditCancel} className="text-red-600 text-sm">❌ Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => handleEditStart('cookiePolicyContent')} className="text-blue-600 text-sm">✏️ Edit</button>
                )}
              </div>
              {editingField === 'cookiePolicyContent' ? (
                <div>
                  <textarea
                    value={tempContent}
                    onChange={(e) => setTempContent(e.target.value)}
                    rows={8}
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                    placeholder="Enter cookie policy content. Use double line breaks for paragraphs."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Formatting tip: Use double line breaks to create paragraphs.
                  </p>
                </div>
              ) : (
                <div className="text-gray-600 text-sm">
                  {formatContent(supportContent.cookiePolicyContent)}
                </div>
              )}
            </div>

            {/* App Information */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">App Information</h4>
              
              <div className="grid grid-cols-1 gap-3">
                {/* App Version */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">App Version:</span>
                  {editingField === 'appVersion' ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={tempContent}
                        onChange={(e) => setTempContent(e.target.value)}
                        className="p-1 border border-gray-300 rounded text-sm w-32"
                      />
                      <button onClick={() => handleEditSave('appVersion')} className="text-green-600 text-sm">✅</button>
                      <button onClick={handleEditCancel} className="text-red-600 text-sm">❌</button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{supportContent.appVersion}</span>
                      <button onClick={() => handleEditStart('appVersion')} className="text-blue-600 text-sm">✏️</button>
                    </div>
                  )}
                </div>

                {/* App Status */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status:</span>
                  {editingField === 'appStatus' ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={tempContent}
                        onChange={(e) => setTempContent(e.target.value)}
                        className="p-1 border border-gray-300 rounded text-sm w-32"
                      />
                      <button onClick={() => handleEditSave('appStatus')} className="text-green-600 text-sm">✅</button>
                      <button onClick={handleEditCancel} className="text-red-600 text-sm">❌</button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600 font-medium">{supportContent.appStatus}</span>
                      <button onClick={() => handleEditStart('appStatus')} className="text-blue-600 text-sm">✏️</button>
                    </div>
                  )}
                </div>

                {/* Last Updated */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  {editingField === 'lastUpdated' ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={tempContent}
                        onChange={(e) => setTempContent(e.target.value)}
                        className="p-1 border border-gray-300 rounded text-sm w-32"
                      />
                      <button onClick={() => handleEditSave('lastUpdated')} className="text-green-600 text-sm">✅</button>
                      <button onClick={handleEditCancel} className="text-red-600 text-sm">❌</button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{supportContent.lastUpdated}</span>
                      <button onClick={() => handleEditStart('lastUpdated')} className="text-blue-600 text-sm">✏️</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  // AI Knowledge Base Manager Component
  const AIKnowledgeBaseManager = () => {
    const [knowledgeBase, setKnowledgeBase] = useState('');
    const [editingKnowledgeBase, setEditingKnowledgeBase] = useState(false);
    const [tempContent, setTempContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');

    // Load knowledge base content on component mount
    useEffect(() => {
      fetchKnowledgeBase();
    }, []);

    const fetchKnowledgeBase = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/knowledge-base`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setKnowledgeBase(response.data.content || '');
      } catch (error) {
        console.error('Error fetching knowledge base:', error);
        setSaveStatus('Error loading knowledge base');
      } finally {
        setLoading(false);
      }
    };

    const handleEditStart = () => {
      setEditingKnowledgeBase(true);
      setTempContent(knowledgeBase);
      setSaveStatus('');
    };

    const handleEditSave = async () => {
      try {
        setLoading(true);
        setSaveStatus('Saving...');
        const token = localStorage.getItem('token');
        
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/knowledge-base`, {
          content: tempContent
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        setKnowledgeBase(tempContent);
        setEditingKnowledgeBase(false);
        setSaveStatus('✅ Knowledge base updated successfully!');
        
        // Clear status after 3 seconds
        setTimeout(() => setSaveStatus(''), 3000);
      } catch (error) {
        console.error('Error updating knowledge base:', error);
        setSaveStatus('❌ Error updating knowledge base. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const handleEditCancel = () => {
      setEditingKnowledgeBase(false);
      setTempContent('');
      setSaveStatus('');
    };

    const formatContent = (content) => {
      if (!content) return <span className="text-gray-400 italic">No content available</span>;
      
      return content.split('\n\n').map((paragraph, index) => (
        <p key={index} className="mb-2 last:mb-0">
          {paragraph.split('\n').map((line, lineIndex) => (
            <span key={lineIndex}>
              {line}
              {lineIndex < paragraph.split('\n').length - 1 && <br />}
            </span>
          ))}
        </p>
      ));
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">🧠 AI Knowledge Base Management</h3>
            <div className="flex items-center space-x-2">
              {saveStatus && (
                <span className={`text-sm ${saveStatus.includes('✅') ? 'text-green-600' : saveStatus.includes('❌') ? 'text-red-600' : 'text-blue-600'}`}>
                  {saveStatus}
                </span>
              )}
              {loading && (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">ℹ️</span>
              <div>
                <h4 className="font-medium text-blue-900 mb-2">About AI Knowledge Base</h4>
                <p className="text-blue-800 text-sm mb-2">
                  This knowledge base provides information that the AI Help Assistant uses to answer user questions. 
                  The content you add here will be available to the AI when users interact with the Help Assistant feature.
                </p>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Include information about Edotask features and functionality</li>
                  <li>• Add common troubleshooting steps and solutions</li>
                  <li>• Provide guidance on how to use different parts of the app</li>
                  <li>• Use clear, concise language that users can easily understand</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Knowledge Base Content</h4>
              {editingKnowledgeBase ? (
                <div className="flex space-x-2">
                  <button 
                    onClick={handleEditSave} 
                    disabled={loading}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? '⏳ Saving...' : '✅ Save'}
                  </button>
                  <button 
                    onClick={handleEditCancel}
                    disabled={loading}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    ❌ Cancel
                  </button>
                </div>
              ) : (
                <button 
                  onClick={handleEditStart}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  ✏️ Edit
                </button>
              )}
            </div>
            
            {editingKnowledgeBase ? (
              <div>
                <textarea
                  value={tempContent}
                  onChange={(e) => setTempContent(e.target.value)}
                  rows={20}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm font-mono"
                  placeholder="Enter knowledge base content for the AI Help Assistant...

Example content:
# Edotask Help Guide

## Getting Started
Edotask is a task management application that helps you organize your work and personal tasks.

## Creating Tasks
To create a new task:
1. Click the + button at the bottom right
2. Fill in the task details
3. Set a due date and priority
4. Click Save

## Voice Task Creator
You can create tasks using voice commands:
1. Click the AI button above the + button
2. Speak your task description
3. The AI will ask follow-up questions for missing details

## Categories
Available task categories include:
- Work
- Personal  
- Health
- Education
- Shopping
- Family
- Finance
- Travel

Add more sections as needed..."
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    💡 Use # for headings, ## for subheadings, and bullet points for lists. The AI will use this content to help users.
                  </p>
                  <span className="text-xs text-gray-500">
                    {tempContent.length} characters
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-gray-600 text-sm max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Loading knowledge base...</span>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">
                    {formatContent(knowledgeBase)}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-yellow-50 rounded-lg p-4 mt-4">
            <div className="flex items-start space-x-3">
              <span className="text-xl">💡</span>
              <div>
                <h4 className="font-medium text-yellow-900 mb-2">Tips for Effective Knowledge Base Content</h4>
                <ul className="text-yellow-800 text-sm space-y-1">
                  <li>• Keep information organized with clear headings and sections</li>
                  <li>• Include step-by-step instructions for common tasks</li>
                  <li>• Update content regularly as new features are added</li>
                  <li>• Test the AI Help Assistant after making changes to ensure accuracy</li>
                  <li>• Use simple language that all users can understand</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // User Tasks Manager Component
  const UserTasksManager = ({ users, selectedUser: propSelectedUser, onBack }) => {
    const [selectedUser, setSelectedUser] = useState(propSelectedUser || null);
    const [userTasks, setUserTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // If a user is pre-selected via props, fetch their tasks
    useEffect(() => {
      if (propSelectedUser) {
        setSelectedUser(propSelectedUser);
        fetchUserTasks(propSelectedUser.id);
      }
    }, [propSelectedUser]);

    const fetchUserTasks = async (userId) => {
      setTasksLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Filter tasks for the specific user
        const allTasks = response.data || [];
        const userSpecificTasks = allTasks.filter(task => task.user_id === userId);
        setUserTasks(userSpecificTasks);
      } catch (error) {
        console.error('Error fetching user tasks:', error);
        setUserTasks([]);
        if (error.response?.status === 403) {
          alert('Admin access required to view user tasks');
        }
      } finally {
        setTasksLoading(false);
      }
    };

    const handleUserSelect = (user) => {
      setSelectedUser(user);
      fetchUserTasks(user.id);
    };

    const handleBack = () => {
      if (onBack) {
        onBack();
      } else {
        setSelectedUser(null);
        setUserTasks([]);
      }
    };

    // Filter users based on search query
    const filteredUsers = users.filter(user => {
      const query = searchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    });

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Tasks Management</h3>
          
          {!selectedUser ? (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">
                  Select a user to view their tasks: 
                  {searchQuery && (
                    <span className="text-sm text-gray-500 ml-2">
                      ({filteredUsers.length} of {users.length} users)
                    </span>
                  )}
                </h4>
                
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                         onClick={() => handleUserSelect(user)}>
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                          {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900">{user.name}</h5>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.tasks_count || 0} tasks
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p>No users found matching "{searchQuery}"</p>
                    <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Tasks for {selectedUser.name}</h4>
                <button onClick={handleBack} className="text-blue-600 text-sm hover:text-blue-700 flex items-center">
                  <span className="mr-1">←</span>
                  Back {onBack ? 'to Users' : ''}
                </button>
              </div>
              
              {tasksLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {userTasks.length > 0 ? userTasks.map((task) => (
                    <div key={task.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className={`font-medium ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                            {task.title}
                          </h5>
                          <div className="flex items-center space-x-3 mt-2 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              task.priority === 'High' ? 'bg-red-100 text-red-800' :
                              task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {task.priority}
                            </span>
                            {task.category && (
                              <span className="text-gray-500">{task.category}</span>
                            )}
                            {task.due_date && (
                              <span className="text-gray-500">Due: {new Date(task.due_date).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            task.completed ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {task.completed ? 'Completed' : 'Active'}
                          </span>
                          <button className="text-red-500 hover:text-red-700 text-xs">🗑️</button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-gray-500">
                      No tasks found for this user
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // User Contacts Manager Component
  const UserContactsManager = ({ users, selectedUser: propSelectedUser, onBack }) => {
    const [selectedUser, setSelectedUser] = useState(propSelectedUser || null);
    const [userContacts, setUserContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // If a user is pre-selected via props, fetch their contacts
    useEffect(() => {
      if (propSelectedUser) {
        setSelectedUser(propSelectedUser);
        fetchUserContacts(propSelectedUser.id);
      }
    }, [propSelectedUser]);

    const fetchUserContacts = async (userId) => {
      setContactsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/contacts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Filter contacts for the specific user
        const allContacts = response.data || [];
        const userSpecificContacts = allContacts.filter(contact => contact.user_id === userId);
        setUserContacts(userSpecificContacts);
      } catch (error) {
        console.error('Error fetching user contacts:', error);
        setUserContacts([]);
        if (error.response?.status === 403) {
          alert('Admin access required to view user contacts');
        }
      } finally {
        setContactsLoading(false);
      }
    };

    const handleUserSelect = (user) => {
      setSelectedUser(user);
      fetchUserContacts(user.id);
    };

    const handleBack = () => {
      if (onBack) {
        onBack();
      } else {
        setSelectedUser(null);
        setUserContacts([]);
      }
    };

    // Filter users based on search query
    const filteredUsers = users.filter(user => {
      const query = searchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    });

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Contacts Management</h3>
          
          {!selectedUser ? (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">
                  Select a user to view their contacts:
                  {searchQuery && (
                    <span className="text-sm text-gray-500 ml-2">
                      ({filteredUsers.length} of {users.length} users)
                    </span>
                  )}
                </h4>
                
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                         onClick={() => handleUserSelect(user)}>
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                          {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900">{user.name}</h5>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.contacts_count || 0} contacts
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p>No users found matching "{searchQuery}"</p>
                    <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Contacts for {selectedUser.name}</h4>
                <button onClick={handleBack} className="text-blue-600 text-sm hover:text-blue-700 flex items-center">
                  <span className="mr-1">←</span>
                  Back {onBack ? 'to Users' : ''}
                </button>
              </div>
              
              {contactsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {userContacts.length > 0 ? userContacts.map((contact) => (
                    <div key={contact.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">
                            {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900">{contact.name}</h5>
                            <p className="text-sm text-gray-600">{contact.email}</p>
                            {contact.phone && (
                              <p className="text-sm text-gray-500">{contact.phone}</p>
                            )}
                            {contact.company && (
                              <p className="text-sm text-blue-600">{contact.company}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-500 hover:text-blue-700 text-xs">✏️</button>
                          <button className="text-red-500 hover:text-red-700 text-xs">🗑️</button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-gray-500">
                      No contacts found for this user
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // User Payments Manager Component
  const UserPaymentsManager = ({ users, selectedUser: propSelectedUser, onBack }) => {
    const [selectedUser, setSelectedUser] = useState(propSelectedUser || null);
    const [userPayments, setUserPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // If a user is pre-selected via props, fetch their payments
    useEffect(() => {
      if (propSelectedUser) {
        setSelectedUser(propSelectedUser);
        fetchUserPayments(propSelectedUser.id);
      }
    }, [propSelectedUser]);

    const fetchUserPayments = async (userId) => {
      setPaymentsLoading(true);
      try {
        // Note: Admin payment history endpoint not yet implemented in backend
        // For now, we'll show the user's subscription information
        const user = users.find(u => u.id === userId);
        if (user) {
          // Create a basic payment info from user subscription data
          const subscriptionInfo = {
            user_subscription: user.subscription_status,
            subscription_plan: user.subscription_plan || 'free',
            created_at: user.created_at,
            // Note: Detailed payment transactions require backend admin endpoint
            note: 'Detailed payment transaction history requires backend implementation of /api/admin/payments/{user_id} endpoint'
          };
          setUserPayments([subscriptionInfo]);
        } else {
          setUserPayments([]);
        }
      } catch (error) {
        console.error('Error fetching user payments:', error);
        setUserPayments([]);
      } finally {
        setPaymentsLoading(false);
      }
    };

    const handleUserSelect = (user) => {
      setSelectedUser(user);
      fetchUserPayments(user.id);
    };

    const handleBack = () => {
      if (onBack) {
        onBack();
      } else {
        setSelectedUser(null);
        setUserPayments([]);
      }
    };

    // Filter users based on search query
    const filteredUsers = users.filter(user => {
      const query = searchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    });

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Payments Management</h3>
          
          {!selectedUser ? (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">
                  Select a user to view their payment history:
                  {searchQuery && (
                    <span className="text-sm text-gray-500 ml-2">
                      ({filteredUsers.length} of {users.length} users)
                    </span>
                  )}
                </h4>
                
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                         onClick={() => handleUserSelect(user)}>
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                          {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900">{user.name}</h5>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      <div className="text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.subscription_status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.subscription_status === 'active' ? 'Pro' : 'Free'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p>No users found matching "{searchQuery}"</p>
                    <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Payment History for {selectedUser.name}</h4>
                <button onClick={handleBack} className="text-blue-600 text-sm hover:text-blue-700 flex items-center">
                  <span className="mr-1">←</span>
                  Back {onBack ? 'to Users' : ''}
                </button>
              </div>
              
              {paymentsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {userPayments.length > 0 ? userPayments.map((payment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {payment.note ? (
                            // Show subscription info and note about missing backend endpoint
                            <div>
                              <div className="flex items-center space-x-3">
                                <h5 className="font-medium text-gray-900">Subscription: {payment.subscription_plan || 'Free'}</h5>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  payment.user_subscription === 'active' ? 'bg-green-100 text-green-800' :
                                  payment.user_subscription === 'inactive' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {payment.user_subscription}
                                </span>
                              </div>
                              <div className="mt-2 text-sm text-gray-500">
                                {payment.created_at && <span>Joined: {new Date(payment.created_at).toLocaleDateString()}</span>}
                              </div>
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                                {payment.note}
                              </div>
                            </div>
                          ) : (
                            // Legacy format for when real payment data is available
                            <div>
                              <div className="flex items-center space-x-3">
                                <h5 className="font-medium text-gray-900">${payment.amount}</h5>
                                <span className="text-gray-600">{payment.plan}</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {payment.status}
                                </span>
                              </div>
                              <div className="flex items-center space-x-3 mt-2 text-sm text-gray-500">
                                <span>Date: {new Date(payment.date).toLocaleDateString()}</span>
                                <span>Method: {payment.payment_method}</span>
                                <span>ID: {payment.transaction_id}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        {!payment.note && (
                          <div className="flex items-center space-x-2">
                            <button className="text-blue-500 hover:text-blue-700 text-xs">👁️ View</button>
                            <button className="text-green-500 hover:text-green-700 text-xs">📧 Resend Receipt</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-gray-500">
                      No payment information found for this user
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <SupportContentProvider>
      <WorkspaceProvider user={user}>
      {/* Trial Banner */}
      <TrialBanner />
      
      {/* Trial Expired Modal */}
      <TrialExpiredModal />
      
      <Routes>
        <Route path="/" element={
          <TasksPage 
            tasks={tasks}
            contacts={contacts}
            categories={categories}
            loading={loading}
            loadingMore={loadingMore}
            hasMoreTasks={hasMoreTasks}
            loadMoreTasks={loadMoreTasks}
            getContactName={getContactName}
            handleOpenAddTask={handleOpenAddTask}
            showAddTask={showAddTask}
            handleCloseAddTask={handleCloseAddTask}
            TASKS_PER_PAGE={TASKS_PER_PAGE}
            selectedTasks={selectedTasks}
            isSelectionMode={isSelectionMode}
            isBulkDeleting={isBulkDeleting}
            toggleSelectionMode={toggleSelectionMode}
            toggleTaskSelection={toggleTaskSelection}
            selectAllTasks={selectAllTasks}
            clearSelection={clearSelection}
            bulkDeleteTasks={bulkDeleteTasks}
            allTasksLoaded={allTasksLoaded}
            loadAllTasks={loadAllTasks}
          />
        } />
        <Route path="/tasks" element={
          <TasksPage 
            tasks={tasks}
            contacts={contacts}
            categories={categories}
            loading={loading}
            loadingMore={loadingMore}
            hasMoreTasks={hasMoreTasks}
            loadMoreTasks={loadMoreTasks}
            getContactName={getContactName}
            handleOpenAddTask={handleOpenAddTask}
            showAddTask={showAddTask}
            handleCloseAddTask={handleCloseAddTask}
            TASKS_PER_PAGE={TASKS_PER_PAGE}
            selectedTasks={selectedTasks}
            isSelectionMode={isSelectionMode}
            isBulkDeleting={isBulkDeleting}
            toggleSelectionMode={toggleSelectionMode}
            toggleTaskSelection={toggleTaskSelection}
            selectAllTasks={selectAllTasks}
            clearSelection={clearSelection}
            bulkDeleteTasks={bulkDeleteTasks}
            enterSelectionMode={enterSelectionMode}
            allTasksLoaded={allTasksLoaded}
            loadAllTasks={loadAllTasks}
          />
        } />
        <Route path="/task/:taskId" element={<TaskDetailsPage />} />
        <Route path="/contact/:contactId" element={<ContactDetailsPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/:section" element={<SettingsPage />} />
        <Route path="/organization/welcome" element={<OrganizationWelcomePage />} />
        <Route path="/organization/members" element={<OrganizationMembersPage />} />
        <Route path="/organization/settings" element={<OrganizationSettingsPage />} />
        <Route path="/organization/tasks" element={<OrganizationTeamTasksPage />} />
        <Route path="/organization/billing" element={<OrganizationBillingPage />} />
        <Route path="/administration" element={<AdministrationPage />} />
      </Routes>
      </WorkspaceProvider>
    </SupportContentProvider>
  );
};

export default Components;